import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  MiniMap,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Panel,
  MarkerType,
  NodeTypes,
  EdgeTypes,
  ConnectionMode,
  ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Save,
  Play,
  Pause,
  ArrowLeft,
  Settings,
  Zap,
  Mail,
  MessageSquare,
  Phone,
  Clock,
  GitBranch,
  UserPlus,
  Tag,
  FileText,
  Bell,
  Globe,
  Target,
  Users,
  Filter,
  Webhook,
  Loader2,
  Plus,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  CheckCircle,
  RefreshCw,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  X,
  Grip,
  Link2,
  MousePointer,
  LayoutGrid,
  Sparkles,
  Bot,
  Wand2,
  LayoutTemplate,
  Calendar,
  PhoneCall,
  Ticket,
  CalendarCheck,
  Video,
  Gift,
  Star,
  Split,
  FlaskConical,
  ExternalLink,
  Smartphone,
  ShoppingCart,
  Package,
  CreditCard,
  Store,
  BarChart3,
  Truck,
  PackageCheck,
  RotateCcw,
  Send,
  MessageCircle,
  AtSign,
  Radio,
  Search,
  Upload,
  History,
  Newspaper,
  StickyNote,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import apiClient from '../../services/api';
import Textarea from '../../components/ui/Textarea';
import LanguageSelector from '../../components/ui/LanguageSelector';
import Modal from '../../components/ui/Modal';

// Custom Node Components
import TriggerNode from '../../components/automation/nodes/TriggerNode';
import ActionNode from '../../components/automation/nodes/ActionNode';
import ConditionNode from '../../components/automation/nodes/ConditionNode';
import DelayNode from '../../components/automation/nodes/DelayNode';
import CRMActionNode from '../../components/automation/nodes/CRMActionNode';
import MarketplaceActionNode from '../../components/automation/nodes/MarketplaceActionNode';
import MarketplaceConnectionPanel from '../../components/automation/MarketplaceConnectionPanel';
import SocialPostPicker from '../../components/automation/SocialPostPicker';
import WorkflowRunLog from '../../components/automation/WorkflowRunLog';
import { MarketplaceConnection } from '../../services/marketplaceIntegrationService';
import ABTestNode from '../../components/automation/nodes/ABTestNode';
import CustomEdge from '../../components/automation/edges/CustomEdge';
import * as workflowService from '../../services/workflowService';
import * as abTestService from '../../services/abTestService';
import AiCostBadge from '../../components/ai/AiCostBadge';

// Types
export interface FlowNodeData {
  label: string;
  type: string;
  icon?: string;
  config: Record<string, any>;
  description?: string;
  isConfigured?: boolean;
}

export interface WorkflowData {
  id?: string;
  name: string;
  description: string;
  status: 'draft' | 'active' | 'paused';
  nodes: Node<FlowNodeData>[];
  edges: Edge[];
  crmIntegration?: {
    enabled: boolean;
    apiKey?: string;
    baseUrl?: string;
  };
  marketplaceIntegration?: {
    enabled: boolean;
    apiKey?: string;
    baseUrl?: string;
    storeId?: string;
    connectionId?: string;
    storeName?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

// Node Types Configuration
const nodeTypes: NodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  condition: ConditionNode,
  delay: DelayNode,
  crmAction: CRMActionNode,
  marketplaceAction: MarketplaceActionNode,
  abTest: ABTestNode,
};

const edgeTypes: EdgeTypes = {
  custom: CustomEdge,
};

// Nodes that have no required config fields are pre-configured on creation
// (usado tanto no drag-and-drop quanto no clique da paleta — senão o nó fica
// com o badge "Clique para configurar" eterno sem ter o que configurar)
const AUTO_CONFIGURED_TYPES = [
  'meta_lead_ad',
  'instagram_first_dm',
  'instagram_icebreaker',
  'instagram_story_reply',
  'instagram_comment',
  'facebook_comment',
  'threads_comment',
  'live_keyword_detected',
  'live_ended',
  'marketplace_order_created',
  'marketplace_cart_abandoned',
  'marketplace_store_registered',
  'marketplace_ticket_opened',
  'marketplace_payment_confirmed',
  'marketplace_order_processing',
  'marketplace_order_shipped',
  'marketplace_order_delivered',
  'marketplace_order_cancelled',
  'marketplace_order_refunded',
  'marketplace_payment_failed',
  'marketplace_payment_refunded',
];

// Available Nodes for Sidebar - with i18n keys
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getAvailableNodes = (t: any) => ({
  triggers: [
    { type: 'trigger', subtype: 'landing_page_submit', label: t('workflow.triggers.landingPage', 'Landing Page Lead'), icon: 'Globe', color: 'emerald' },
    { type: 'trigger', subtype: 'form_submit', label: t('workflow.triggers.formSubmit', 'Form Submitted'), icon: 'FileText', color: 'emerald' },
    { type: 'trigger', subtype: 'tag_added', label: t('workflow.triggers.tagAdded', 'Tag Added'), icon: 'Tag', color: 'emerald' },
    { type: 'trigger', subtype: 'lead_created', label: t('workflow.triggers.leadCreated', 'Lead Created in CRM'), icon: 'UserPlus', color: 'emerald' },
    { type: 'trigger', subtype: 'webhook', label: t('workflow.triggers.webhook', 'Webhook Received'), icon: 'Webhook', color: 'emerald' },
  ],
  actions: [
    { type: 'action', subtype: 'notification', label: t('workflow.actions.notification', 'Internal Notification'), icon: 'Bell', color: 'yellow' },
    { type: 'action', subtype: 'api_call', label: t('workflow.actions.apiCall', 'API Call'), icon: 'Globe', color: 'blue' },
    { type: 'action', subtype: 'redirect', label: t('workflow.actions.redirect', 'Redirect LP'), icon: 'Link2', color: 'indigo' },
  ],
  crmActions: [
    { type: 'crmAction', subtype: 'create_lead', label: t('workflow.crm.createLead', 'Create Lead'), icon: 'UserPlus', color: 'pink' },
    { type: 'crmAction', subtype: 'update_lead', label: t('workflow.crm.updateLead', 'Update Lead'), icon: 'Users', color: 'pink' },
    { type: 'crmAction', subtype: 'change_stage', label: t('workflow.crm.changeStage', 'Move in Pipeline'), icon: 'GitBranch', color: 'pink' },
    { type: 'crmAction', subtype: 'add_tag', label: t('workflow.crm.addTag', 'Add Tag'), icon: 'Tag', color: 'pink' },
    { type: 'crmAction', subtype: 'create_ticket', label: t('workflow.crm.createTicket', 'Create Ticket'), icon: 'Ticket', color: 'pink' },
    { type: 'crmAction', subtype: 'assign_owner', label: t('workflow.crm.assignOwner', 'Assign Owner'), icon: 'Target', color: 'pink' },
    { type: 'crmAction', subtype: 'crm_whatsapp', label: t('workflow.crm.whatsapp', 'WhatsApp'), icon: 'MessageSquare', color: 'green' },
    { type: 'crmAction', subtype: 'crm_email', label: t('workflow.crm.email', 'Email'), icon: 'Mail', color: 'blue' },
    { type: 'crmAction', subtype: 'crm_sms', label: t('workflow.crm.sms', 'SMS'), icon: 'Smartphone', color: 'teal' },
    { type: 'crmAction', subtype: 'crm_call', label: t('workflow.crm.call', 'Call/Schedule Call'), icon: 'PhoneCall', color: 'purple' },
  ],
  marketplaceActions: [
    // "Abrir chamado" saiu: o marketplace não tem chamados (fluxos antigos com ele mostram o aviso no painel do passo)
    { type: 'marketplaceAction', subtype: 'marketplace_notify_buyer', label: t('workflow.mkp.notifyBuyer', 'Avisar comprador (e-mail)'), icon: 'Mail', color: 'indigo' },
    { type: 'marketplaceAction', subtype: 'marketplace_update_order', label: t('workflow.mkp.updateOrder', 'Mudar pedido'), icon: 'Package', color: 'indigo' },
    { type: 'marketplaceAction', subtype: 'marketplace_add_note', label: t('workflow.mkp.addNote', 'Anotar no pedido'), icon: 'StickyNote', color: 'indigo' },
    { type: 'marketplaceAction', subtype: 'marketplace_add_tag', label: t('workflow.mkp.addTag', 'Etiquetar cliente'), icon: 'Tag', color: 'indigo' },
    { type: 'marketplaceAction', subtype: 'marketplace_publish_blog_post', label: t('workflow.mkp.publishBlog', 'Publicar no blog da loja'), icon: 'Newspaper', color: 'indigo' },
  ],
  socialTriggers: [
    { type: 'trigger', subtype: 'meta_lead_ad', label: t('workflow.social.metaLeadAd', 'Meta Lead Ad (Facebook/Instagram)'), icon: 'Target', color: 'blue' },
    { type: 'trigger', subtype: 'instagram_comment', label: t('workflow.social.instagramComment', 'Instagram Comment'), icon: 'Instagram', color: 'pink' },
    { type: 'trigger', subtype: 'facebook_comment', label: t('workflow.social.facebookComment', 'Facebook Comment'), icon: 'Facebook', color: 'blue' },
    { type: 'trigger', subtype: 'threads_comment', label: t('workflow.social.threadsComment', 'Threads Comment'), icon: 'AtSign', color: 'gray' },
    { type: 'trigger', subtype: 'instagram_first_dm', label: t('workflow.social.instagramFirstDm', 'First Instagram DM'), icon: 'Instagram', color: 'pink' },
    { type: 'trigger', subtype: 'instagram_icebreaker', label: t('workflow.social.instagramIcebreaker', 'Ice Breaker (Instagram)'), icon: 'Instagram', color: 'purple' },
    { type: 'trigger', subtype: 'instagram_story_reply', label: t('workflow.social.instagramStoryReply', 'Story Reply (Instagram)'), icon: 'Instagram', color: 'orange' },
    { type: 'trigger', subtype: 'live_keyword_detected', label: t('workflow.social.liveKeywordDetected', 'Keyword in Live'), icon: 'Radio', color: 'red' },
    { type: 'trigger', subtype: 'live_ended', label: t('workflow.social.liveEnded', 'Live Contact List (Post-Live)'), icon: 'Radio', color: 'orange' },
  ],
  socialActions: [
    { type: 'action', subtype: 'reply_comment', label: t('workflow.social.replyComment', 'Reply to Comment'), icon: 'MessageCircle', color: 'pink' },
    { type: 'action', subtype: 'private_reply', label: t('workflow.social.privateReply', 'Private Reply (DM)'), icon: 'Send', color: 'purple' },
    { type: 'action', subtype: 'reply_welcome_dm', label: t('workflow.social.replyWelcomeDm', 'Welcome DM Response'), icon: 'MessageSquare', color: 'pink' },
    { type: 'action', subtype: 'reply_icebreaker', label: t('workflow.social.replyIcebreaker', 'Ice Breaker Response'), icon: 'MessageSquare', color: 'purple' },
    { type: 'action', subtype: 'reply_story_dm', label: t('workflow.social.replyStoryDm', 'Story Reply Response'), icon: 'MessageCircle', color: 'orange' },
  ],
  marketplaceTriggers: [
    { type: 'trigger', subtype: 'marketplace_order_created', label: t('workflow.marketplace.orderCreated', 'New Purchase'), icon: 'ShoppingCart', color: 'indigo' },
    { type: 'trigger', subtype: 'marketplace_cart_abandoned', label: t('workflow.marketplace.cartAbandoned', 'Abandoned Cart'), icon: 'ShoppingCart', color: 'red' },
    // "Nova loja" e "Chamado aberto" saíram: o marketplace nunca manda esses avisos para uma loja
    { type: 'trigger', subtype: 'marketplace_payment_confirmed', label: t('workflow.marketplace.paymentConfirmed', 'Payment Confirmed'), icon: 'CreditCard', color: 'emerald' },
    { type: 'trigger', subtype: 'marketplace_order_processing', label: t('workflow.marketplace.orderProcessing', 'Order Processing'), icon: 'RefreshCw', color: 'yellow' },
    { type: 'trigger', subtype: 'marketplace_order_shipped', label: t('workflow.marketplace.orderShipped', 'Order Shipped'), icon: 'Truck', color: 'blue' },
    { type: 'trigger', subtype: 'marketplace_order_delivered', label: t('workflow.marketplace.orderDelivered', 'Order Delivered'), icon: 'PackageCheck', color: 'emerald' },
    { type: 'trigger', subtype: 'marketplace_order_cancelled', label: t('workflow.marketplace.orderCancelled', 'Order Cancelled'), icon: 'XCircle', color: 'red' },
    { type: 'trigger', subtype: 'marketplace_order_refunded', label: t('workflow.marketplace.orderRefunded', 'Order Refunded'), icon: 'RotateCcw', color: 'orange' },
    { type: 'trigger', subtype: 'marketplace_payment_failed', label: t('workflow.marketplace.paymentFailed', 'Payment Failed'), icon: 'AlertTriangle', color: 'red' },
    { type: 'trigger', subtype: 'marketplace_payment_refunded', label: t('workflow.marketplace.paymentRefunded', 'Payment Refunded'), icon: 'RotateCcw', color: 'orange' },
  ],
  logic: [
    { type: 'condition', subtype: 'if_else', label: t('workflow.logic.condition', 'If/Else Condition'), icon: 'GitBranch', color: 'orange' },
    { type: 'condition', subtype: 'filter', label: t('workflow.logic.filter', 'Filter Leads'), icon: 'Filter', color: 'orange' },
    { type: 'abTest', subtype: 'ab_test', label: t('workflow.logic.abTest', 'A/B Test'), icon: 'Split', color: 'purple' },
    { type: 'delay', subtype: 'wait', label: t('workflow.logic.wait', 'Wait Time'), icon: 'Clock', color: 'cyan' },
    { type: 'delay', subtype: 'wait_response', label: t('workflow.logic.waitResponse', 'Wait Response'), icon: 'MessageSquare', color: 'cyan' },
  ],
});

// Templates de workflows prontos
const WORKFLOW_TEMPLATES = [
  {
    id: 'lead-nurturing',
    name: 'Nutricao de Leads',
    nameEn: 'Lead Nurturing',
    description: 'Fluxo para nutrir leads com emails e whatsapp automaticos',
    descriptionEn: 'Flow to nurture leads with automatic emails and whatsapp',
    icon: 'Mail',
    category: 'marketing',
  },
  {
    id: 'welcome-sequence',
    name: 'Sequencia de Boas-vindas',
    nameEn: 'Welcome Sequence',
    description: 'Envie mensagens de boas-vindas para novos leads',
    descriptionEn: 'Send welcome messages to new leads',
    icon: 'UserPlus',
    category: 'onboarding',
  },
  {
    id: 'abandoned-cart',
    name: 'Carrinho Abandonado',
    nameEn: 'Abandoned Cart',
    description: 'Recupere vendas com lembretes automaticos',
    descriptionEn: 'Recover sales with automatic reminders',
    icon: 'Target',
    category: 'sales',
  },
  {
    id: 'appointment-reminder',
    name: 'Lembrete de Reuniao',
    nameEn: 'Appointment Reminder',
    description: 'Envie lembretes antes de reunioes agendadas',
    descriptionEn: 'Send reminders before scheduled meetings',
    icon: 'Calendar',
    category: 'operations',
  },
  {
    id: 'lead-qualification',
    name: 'Qualificacao de Leads',
    nameEn: 'Lead Qualification',
    description: 'Qualifique leads automaticamente com base em criterios',
    descriptionEn: 'Automatically qualify leads based on criteria',
    icon: 'Filter',
    category: 'sales',
  },
  {
    id: 'reengagement',
    name: 'Reengajamento',
    nameEn: 'Re-engagement',
    description: 'Reative leads inativos com campanhas automaticas',
    descriptionEn: 'Reactivate inactive leads with automatic campaigns',
    icon: 'Zap',
    category: 'marketing',
  },
  {
    id: 'post-sale',
    name: 'Pos-Venda',
    nameEn: 'Post-Sale',
    description: 'Acompanhe clientes apos a compra com mensagens automaticas',
    descriptionEn: 'Follow up with customers after purchase automatically',
    icon: 'CheckCircle',
    category: 'operations',
  },
  {
    id: 'webinar-followup',
    name: 'Follow-up Webinar',
    nameEn: 'Webinar Follow-up',
    description: 'Acompanhe participantes de webinars automaticamente',
    descriptionEn: 'Follow up with webinar attendees automatically',
    icon: 'Video',
    category: 'marketing',
  },
  {
    id: 'birthday-campaign',
    name: 'Campanha de Aniversario',
    nameEn: 'Birthday Campaign',
    description: 'Envie mensagens de aniversario para seus clientes',
    descriptionEn: 'Send birthday messages to your customers',
    icon: 'Gift',
    category: 'marketing',
  },
  {
    id: 'nps-survey',
    name: 'Pesquisa NPS',
    nameEn: 'NPS Survey',
    description: 'Colete feedback dos clientes com pesquisas automaticas',
    descriptionEn: 'Collect customer feedback with automatic surveys',
    icon: 'Star',
    category: 'operations',
  },
  {
    id: 'marketplace-abandoned-cart',
    name: 'Carrinho Abandonado (Marketplace)',
    nameEn: 'Abandoned Cart (Marketplace)',
    description: 'Recupere carrinhos abandonados com SMS, email e WhatsApp',
    descriptionEn: 'Recover abandoned carts with SMS, email and WhatsApp',
    icon: 'ShoppingCart',
    category: 'sales',
  },
  {
    id: 'marketplace-post-purchase',
    name: 'Pos-Compra (Marketplace)',
    nameEn: 'Post-Purchase (Marketplace)',
    description: 'Acompanhe o comprador apos a compra no marketplace',
    descriptionEn: 'Follow up with buyer after marketplace purchase',
    icon: 'Package',
    category: 'operations',
  },
  {
    id: 'marketplace-order-tracking',
    name: 'Rastreamento de Pedido',
    nameEn: 'Order Tracking',
    description: 'Notifique o cliente em cada etapa do pedido automaticamente',
    descriptionEn: 'Notify customers at each order stage automatically',
    icon: 'Truck',
    category: 'operations',
  },
  {
    id: 'marketplace-payment-failed',
    name: 'Recuperacao de Pagamento',
    nameEn: 'Payment Recovery',
    description: 'Tente recuperar pagamentos falhos com lembretes automaticos',
    descriptionEn: 'Try to recover failed payments with automatic reminders',
    icon: 'CreditCard',
    category: 'sales',
  },
  {
    id: 'marketplace-order-cancelled',
    name: 'Pedido Cancelado',
    nameEn: 'Order Cancelled',
    description: 'Notifique a equipe e o cliente quando um pedido for cancelado',
    descriptionEn: 'Notify team and customer when an order is cancelled',
    icon: 'XCircle',
    category: 'operations',
  },
  {
    id: 'marketplace-refund-flow',
    name: 'Fluxo de Reembolso',
    nameEn: 'Refund Flow',
    description: 'Automatize o processo de reembolso com notificacoes e tags',
    descriptionEn: 'Automate the refund process with notifications and tags',
    icon: 'RotateCcw',
    category: 'operations',
  },
  {
    id: 'social-comment-reply',
    name: 'Responder e Enviar DM (Redes Sociais)',
    nameEn: 'Social Media Reply & Private DM',
    description: 'Responda comentarios e envie DM privada automaticamente no Instagram, Facebook e Threads',
    descriptionEn: 'Automatically reply to comments and send private DMs on Instagram, Facebook and Threads',
    icon: 'MessageCircle',
    category: 'social',
  },
  {
    id: 'live-private-reply',
    name: 'Live: Resposta Privada com Link',
    nameEn: 'Live: Private Reply with Link',
    description: 'Quando alguem comenta uma palavra-chave na live, envia DM com link do produto',
    descriptionEn: 'When someone comments a keyword on live, sends DM with product link',
    icon: 'Radio',
    category: 'social',
  },
  {
    id: 'live-reply-followup',
    name: 'Live: Resposta + Follow-up',
    nameEn: 'Live: Reply + Follow-up',
    description: 'Envia DM com link e apos 1h envia follow-up se nao comprou',
    descriptionEn: 'Sends DM with link and after 1h sends follow-up if not purchased',
    icon: 'Radio',
    category: 'social',
  },
  {
    id: 'live-capture-remarketing',
    name: 'Live: Capturar para Remarketing',
    nameEn: 'Live: Capture for Remarketing',
    description: 'Captura contatos da live e adiciona em lista de remarketing',
    descriptionEn: 'Captures live contacts and adds to remarketing list',
    icon: 'Radio',
    category: 'social',
  },
  {
    id: 'live-coupon-reply',
    name: 'Live: Resposta com Cupom',
    nameEn: 'Live: Reply with Coupon',
    description: 'Envia DM com cupom exclusivo gerado automaticamente para cada pessoa da live',
    descriptionEn: 'Sends DM with exclusive auto-generated coupon for each live viewer',
    icon: 'Radio',
    category: 'social',
  },
  {
    id: 'live-post-broadcast',
    name: 'Live: Envio de Lista Pos-Live',
    nameEn: 'Live: Post-Live Broadcast',
    description: 'Ao final da live, envia mensagem para todos os contatos capturados com oferta especial',
    descriptionEn: 'After the live ends, sends message to all captured contacts with special offer',
    icon: 'Radio',
    category: 'social',
  },
];

// Initial empty workflow
const defaultWorkflow: WorkflowData = {
  name: '',
  description: '',
  status: 'draft',
  nodes: [],
  edges: [],
  crmIntegration: {
    enabled: false,
  },
  marketplaceIntegration: {
    enabled: false,
  },
};

const FlowAutomationPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  // Workflow state
  const [workflow, setWorkflow] = useState<WorkflowData>(defaultWorkflow);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<Node<FlowNodeData> | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showCRMConfig, setShowCRMConfig] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  // Celular: paletas viram gaveta/folha por cima do canvas (não empurram a tela)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    triggers: true,
    actions: false,
    crmActions: true,
    socialTriggers: true,
    socialActions: false,
    marketplaceTriggers: false,
    marketplaceActions: false,
    logic: false,
  });

  // CRM Integration state - só é considerado conectado se API key foi salva
  const CRM_BASE_URL = 'https://crm-api.devskin.com/api';
  const [crmConfig, setCrmConfig] = useState({
    enabled: false,
    apiKey: '',
    baseUrl: CRM_BASE_URL,
    projectId: '',
    wabaId: '',
    whatsappToken: '',
    isSaved: false,
  });
  const [isTestingCrm, setIsTestingCrm] = useState(false);
  const [crmProjects, setCrmProjects] = useState<Array<{ id: string; name: string }>>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // Marketplace Integration state
  const MARKETPLACE_BASE_URL = 'https://api-marketplace.devskin.com/api';
  // Loja do marketplace: conexão da CONTA (chave guardada no servidor); o fluxo só guarda qual loja usa.
  // apiKey = chave colada direto no fluxo em versões antigas (ainda funciona, até escolher uma loja).
  const [marketplaceConfig, setMarketplaceConfig] = useState({
    enabled: false,
    apiKey: '',
    baseUrl: MARKETPLACE_BASE_URL,
    storeId: '',
    connectionId: '',
    storeName: '',
    isSaved: false,
  });
  const [marketplaceConnections, setMarketplaceConnections] = useState<MarketplaceConnection[]>([]);
  const [showRunLog, setShowRunLog] = useState(false);

  // Integration modal tab
  const [integrationTab, setIntegrationTab] = useState<'crm' | 'marketplace'>('crm');

  // A/B Tests state
  const [abTests, setAbTests] = useState<any[]>([]);

  // Landing Pages state (loaded from API)
  const [availableLandingPages, setAvailableLandingPages] = useState<Array<{ id: string; name: string; slug: string; status: string; published_url?: string; custom_domain?: string }>>([]);

  // CRM Pipelines state (loaded dynamically from CRM API)
  const [crmPipelines, setCrmPipelines] = useState<Array<{ id: string; name: string; isDefault?: boolean; stages: Array<{ id: string; name: string; order: number }> }>>([]);

  // CRM Tags state (loaded dynamically from CRM API)
  const [crmTags, setCrmTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [isLoadingTags, setIsLoadingTags] = useState(false);
  const [tagMode, setTagMode] = useState<'existing' | 'new'>('existing');

  // Available nodes with translations
  const availableNodes = useMemo(() => getAvailableNodes(t), [t]);

  // Check if CRM is actually connected (has saved API key)
  const isCrmConnected = !!(crmConfig.enabled && crmConfig.apiKey && crmConfig.isSaved);
  const isMarketplaceEnabled = !!(marketplaceConfig.enabled && marketplaceConfig.isSaved);
  const isMarketplaceConnected = !!(marketplaceConfig.enabled && marketplaceConfig.isSaved && (marketplaceConfig.connectionId || marketplaceConfig.apiKey));
  const buildMarketplaceIntegration = (cfg: typeof marketplaceConfig) => (cfg.connectionId
    ? { enabled: cfg.enabled, connectionId: cfg.connectionId, storeId: cfg.storeId, storeName: cfg.storeName }
    : { enabled: cfg.enabled, apiKey: cfg.apiKey, baseUrl: cfg.baseUrl, storeId: cfg.storeId });
  const selectMarketplaceConnection = useCallback((conn: MarketplaceConnection | null) => {
    setMarketplaceConfig((prev) => (conn
      ? { ...prev, enabled: true, connectionId: conn.id, storeId: conn.store_id, storeName: conn.store_name || '', apiKey: '', isSaved: prev.connectionId === conn.id ? prev.isSaved : false }
      : { ...prev, connectionId: '', storeName: '', isSaved: false }));
  }, []);
  const isAnyIntegrationConnected = isCrmConnected || isMarketplaceEnabled;

  // Load workflow if editing
  useEffect(() => {
    if (id) {
      loadWorkflow(id);
    }
  }, [id]);

  // Load A/B tests for the ab_test node
  useEffect(() => {
    const loadAbTests = async () => {
      try {
        const response = await abTestService.getAllTests({ limit: 100 });
        if (response.success && response.data) {
          setAbTests(response.data);
        }
      } catch (error) {
        console.error('Error loading A/B tests:', error);
      }
    };
    loadAbTests();
  }, []);

  // Load landing pages for trigger dropdown
  useEffect(() => {
    const loadLandingPages = async () => {
      try {
        const response = await workflowService.getLandingPagesForWorkflow();
        if (response.success && response.data) {
          setAvailableLandingPages(response.data);
        }
      } catch (error) {
        console.error('Error loading landing pages:', error);
      }
    };
    loadLandingPages();
  }, []);

  // Load CRM pipelines when CRM is connected
  useEffect(() => {
    if (isCrmConnected && crmConfig.apiKey) {
      const loadPipelines = async () => {
        try {
          const response = await workflowService.getCrmPipelines(crmConfig.apiKey);
          if (response.success && response.data) {
            setCrmPipelines(response.data.map((p: any) => ({
              id: p.id,
              name: p.name,
              isDefault: p.isDefault,
              stages: (p.stages || []).map((s: any) => ({ id: s.id, name: s.name, order: s.order }))
            })));
          }
        } catch (error) {
          console.error('Error loading CRM pipelines:', error);
        }
      };
      loadPipelines();
    }
  }, [isCrmConnected, crmConfig.apiKey, crmConfig.baseUrl]);

  // Load CRM tags when CRM is connected
  useEffect(() => {
    if (isCrmConnected && crmConfig.apiKey) {
      const loadTags = async () => {
        setIsLoadingTags(true);
        try {
          const response = await workflowService.getCrmTags(crmConfig.apiKey);
          if (response.success && response.data) {
            setCrmTags(response.data.map((tag: any) => ({
              id: tag.id || tag._id,
              name: tag.name,
              color: tag.color
            })));
          }
        } catch (error) {
          console.error('Error loading CRM tags:', error);
        } finally {
          setIsLoadingTags(false);
        }
      };
      loadTags();
    }
  }, [isCrmConnected, crmConfig.apiKey]);

  // Carrega os projetos do CRM sempre que o modal de integração tem uma API Key —
  // antes só carregava após "Testar Conexão", e o usuário salvava sem projeto
  // (o lead caía silenciosamente no 1º projeto da empresa).
  useEffect(() => {
    if (!showCRMConfig || !crmConfig.enabled || !crmConfig.apiKey || crmConfig.apiKey.length < 16) return;
    if (crmProjects.length > 0) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setIsLoadingProjects(true);
      workflowService.getCrmProjects(crmConfig.apiKey)
        .then((res) => { if (!cancelled && res.success) setCrmProjects(res.data || []); })
        .catch(() => {})
        .finally(() => { if (!cancelled) setIsLoadingProjects(false); });
    }, 600);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [showCRMConfig, crmConfig.enabled, crmConfig.apiKey, crmProjects.length]);

  // Um projeto só no CRM → seleciona automaticamente
  useEffect(() => {
    if (crmProjects.length === 1 && !crmConfig.projectId) {
      setCrmConfig((prev) => ({ ...prev, projectId: crmProjects[0].id, isSaved: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crmProjects]);

  const loadWorkflow = async (workflowId: string) => {
    setIsLoading(true);
    setSelectedNode(null);
    setTagMode('existing');
    try {
      const response = await workflowService.getWorkflow(workflowId);
      if (response.success && response.data) {
        const data = response.data;
        // Map status, treating 'error' as 'paused'
        const mappedStatus: 'draft' | 'active' | 'paused' =
          data.status === 'error' ? 'paused' : data.status;

        setWorkflow({
          id: data.id,
          name: data.name,
          description: data.description || '',
          status: mappedStatus,
          nodes: data.nodes || [],
          edges: data.edges || [],
          crmIntegration: data.crm_integration || { enabled: false },
          marketplaceIntegration: data.marketplace_integration || { enabled: false },
          createdAt: data.created_at,
          updatedAt: data.updated_at,
        });
        // Normaliza nós salvos antes do fix do onDrop: tipos sem config obrigatória
        // não podem ficar com o badge "Clique para configurar" eterno
        setNodes(((data.nodes as Node<FlowNodeData>[]) || []).map((n) =>
          !n.data?.isConfigured && AUTO_CONFIGURED_TYPES.includes(n.data?.type || '')
            ? { ...n, data: { ...n.data, isConfigured: true } }
            : n
        ));
        setEdges(data.edges as Edge[] || []);
        const hasCrmApiKey = !!(data.crm_integration?.apiKey);
        setCrmConfig({
          enabled: data.crm_integration?.enabled || false,
          apiKey: data.crm_integration?.apiKey || '',
          baseUrl: CRM_BASE_URL,
          projectId: data.crm_integration?.projectId || '',
          wabaId: data.crm_integration?.wabaId || '',
          whatsappToken: data.crm_integration?.whatsappToken || '',
          isSaved: hasCrmApiKey,
        });
        // Restore Marketplace config
        const mi: any = data.marketplace_integration || {};
        setMarketplaceConfig({
          enabled: mi.enabled || false,
          apiKey: mi.apiKey || '',
          baseUrl: MARKETPLACE_BASE_URL,
          storeId: mi.storeId || '',
          connectionId: mi.connectionId || '',
          storeName: mi.storeName || '',
          isSaved: !!(mi.enabled),
        });
        // Load CRM projects if API key exists
        if (hasCrmApiKey) {
          try {
            const projectsResult = await workflowService.getCrmProjects(data.crm_integration!.apiKey!);
            if (projectsResult.success) {
              setCrmProjects(projectsResult.data);
            }
          } catch (e) {
            console.warn('Failed to load CRM projects:', e);
          }
        }
      }
    } catch (error) {
      toast.error(t('workflow.loadError', 'Error loading flow'));
      console.error('Load workflow error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveWorkflow = async () => {
    // Validate before saving
    if (!workflow.name?.trim()) {
      toast.error(t('workflow.validation.nameRequired', 'Enter a name for the flow'));
      return;
    }

    // Validate action nodes that require a message have a non-empty active message
    const messageActionTypes = ['reply_comment', 'send_social_dm', 'reply_welcome_dm', 'private_reply', 'reply_story_dm'];
    const actionNodes = nodes.filter(n => messageActionTypes.includes(n.data?.type || '') && n.data?.config?.replyMode !== 'ai');
    for (const rn of actionNodes) {
      const cfg = rn.data?.config || {};
      const msgs = Array.isArray(cfg.messages) ? cfg.messages : (cfg.message ? [cfg.message] : []);
      const idx = typeof cfg.selectedMessageIndex === 'number' ? cfg.selectedMessageIndex : 0;
      const activeMsg = msgs[idx] || '';
      if (!activeMsg.trim()) {
        toast.error(t('workflow.validation.emptyReplyMessage', 'The active reply message cannot be empty'));
        return;
      }
    }

    // Validate reply_icebreaker per-button responses
    const ibActionNodes = nodes.filter(n => n.data?.type === 'reply_icebreaker');
    for (const rn of ibActionNodes) {
      const responses = rn.data?.config?.responses || {};
      const hasAnyResponse = Object.values(responses).some((r: any) =>
        r.replyMode === 'ai' || !!(r.message?.trim())
      );
      if (!hasAnyResponse) {
        toast.error(t('workflow.validation.emptyIcebreakerResponse', 'Configure at least one Ice Breaker button response'));
        return;
      }
    }

    setIsSaving(true);
    try {
      // Find trigger node to determine trigger_type
      const triggerNode = nodes.find(n =>
        n.type === 'trigger' || n.data?.category === 'triggers'
      );
      const triggerType = triggerNode?.data?.type || undefined;

      // Map nodes to the expected format
      const mappedNodes: workflowService.WorkflowNode[] = nodes.map(n => ({
        id: n.id,
        type: n.type || 'action',
        position: n.position,
        data: {
          label: n.data?.label || '',
          type: n.data?.type || '',
          category: n.data?.category,
          icon: n.data?.icon,
          config: n.data?.config || {},
          isConfigured: n.data?.isConfigured
        }
      }));

      // Map edges to the expected format
      const mappedEdges: workflowService.WorkflowEdge[] = edges.map(e => ({
        id: e.id,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle || undefined,
        targetHandle: e.targetHandle || undefined,
        type: e.type
      }));

      const workflowPayload: Partial<workflowService.Workflow> = {
        name: workflow.name,
        description: workflow.description,
        status: workflow.status,
        nodes: mappedNodes,
        edges: mappedEdges,
        trigger_type: triggerType,
        trigger_config: triggerNode?.data?.config || {},
        crm_integration: { enabled: crmConfig.enabled, apiKey: crmConfig.apiKey, baseUrl: crmConfig.baseUrl, projectId: crmConfig.projectId, wabaId: crmConfig.wabaId, whatsappToken: crmConfig.whatsappToken },
        marketplace_integration: buildMarketplaceIntegration(marketplaceConfig),
      };

      let response: { success: boolean; data?: workflowService.Workflow };
      if (id) {
        // Update existing workflow
        response = await workflowService.updateWorkflow(id, workflowPayload);
      } else {
        // Create new workflow
        response = await workflowService.createWorkflow(workflowPayload);
        if (response.success && response.data?.id) {
          // Update URL with new workflow ID
          navigate(`/workflows/${response.data.id}`, { replace: true });
          setWorkflow(prev => ({ ...prev, id: response.data!.id }));
        }
      }

      if (response.success) {
        toast.success(t('workflow.saveSuccess', 'Flow saved successfully!'));
        return true;
      } else {
        const errMsg = (response as any)?.message || t('workflow.saveError', 'Error saving flow');
        toast.error(errMsg);
        console.error('Save workflow failed:', response);
        return false;
      }
    } catch (error: any) {
      const serverMsg = error?.response?.data?.message || error?.message || '';
      toast.error(serverMsg || t('workflow.saveError', 'Error saving flow'));
      console.error('Save workflow error:', error?.response?.data || error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Generate workflow with AI (real AI service)
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error(t('workflow.ai.emptyPrompt', 'Enter a flow description'));
      return;
    }

    if (aiPrompt.trim().length < 15) {
      toast.error(t('workflow.ai.promptTooShort', 'Describe the flow in more detail'));
      return;
    }

    setIsGeneratingAI(true);

    try {
      // Import polling service
      const { pollJobUntilComplete } = await import('../../services/aiJobService');

      // Call real AI service with async mode
      const response = await workflowService.generateWorkflowWithAI(aiPrompt, true, isMarketplaceEnabled);

      let workflowData = null;

      // If async mode, poll for result
      if (response.async && response.jobId) {
        toast.loading(t('workflow.ai.generating', 'Generating flow in background...'), { id: 'workflow-gen' });

        const result = await pollJobUntilComplete(
          response.jobId,
          undefined,
          2000,
          120000 // 2 minute timeout
        );

        toast.dismiss('workflow-gen');
        workflowData = result;
      } else if (response.success && response.data) {
        workflowData = response.data;
      }

      if (!workflowData) {
        throw new Error('Failed to generate workflow');
      }

      const { nodes: aiNodes, edges: aiEdges, summary } = workflowData;

      // Convert AI response to React Flow format
      const generatedNodes: Node<FlowNodeData>[] = aiNodes.map((node: any) => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          label: node.label,
          type: node.nodeType,
          icon: node.icon,
          config: node.config || {},
          isConfigured: Object.keys(node.config || {}).length > 0,
        },
      }));

      const generatedEdges: Edge[] = aiEdges.map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        type: 'custom',
        animated: true,
      }));

      setNodes(generatedNodes);
      setEdges(generatedEdges);
      setShowAIModal(false);
      setAiPrompt('');

      const message = summary
        ? `${t('workflow.ai.generated', 'Flow generated successfully!')} ${summary}`
        : t('workflow.ai.generated', 'Flow generated successfully!');
      toast.success(message);

      // Fit view after a short delay
      setTimeout(() => {
        reactFlowInstance?.fitView({ padding: 0.3 });
      }, 100);

    } catch (error: any) {
      console.error('AI generation error:', error);
      toast.dismiss('workflow-gen');
      toast.error(error.response?.data?.message || t('workflow.ai.error', 'Error generating flow. Try again.'));
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Load template
  const loadTemplate = (templateId: string) => {
    const generatedNodes: Node<FlowNodeData>[] = [];
    const generatedEdges: Edge[] = [];

    // Increased spacing: vertical 200px, horizontal 350px for branches
    const ySpacing = 200;
    const xCenter = 400;
    const xLeft = 150;
    const xRight = 650;

    if (templateId === 'lead-nurturing') {
      // Lead Nurturing template
      const triggerId = `trigger-${Date.now()}`;
      const createLeadId = `crmAction-${Date.now() + 1}`;
      const emailId = `crmAction-${Date.now() + 2}`;
      const delayId = `delay-${Date.now() + 3}`;
      const whatsappId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.leadFromLP', 'Lead from Landing Page'), type: 'landing_page_submit', icon: 'Globe', config: {}, isConfigured: false } },
        { id: createLeadId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.createLead', 'Create Lead'), type: 'create_lead', icon: 'UserPlus', config: {}, isConfigured: false } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.welcomeEmail', 'Welcome Email'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.wait2Days', 'Wait 2 days'), type: 'wait', icon: 'Clock', config: { amount: 2, unit: 'days' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.whatsappFollowup', 'WhatsApp Follow-up'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${createLeadId}`, source: triggerId, target: createLeadId, type: 'custom', animated: true },
        { id: `e-${createLeadId}-${emailId}`, source: createLeadId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${delayId}`, source: emailId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${whatsappId}`, source: delayId, target: whatsappId, type: 'custom', animated: true },
      );
    } else if (templateId === 'welcome-sequence') {
      const triggerId = `trigger-${Date.now()}`;
      const createLeadId = `crmAction-${Date.now() + 1}`;
      const whatsappId = `crmAction-${Date.now() + 2}`;
      const tagId = `crmAction-${Date.now() + 3}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.leadFromLP', 'Lead from Landing Page'), type: 'landing_page_submit', icon: 'Globe', config: {}, isConfigured: false } },
        { id: createLeadId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.createLead', 'Create Lead'), type: 'create_lead', icon: 'UserPlus', config: {}, isConfigured: false } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.welcomeMessage', 'Welcome Message'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
        { id: tagId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.addTagNew', 'Add Tag "New"'), type: 'add_tag', icon: 'Tag', config: { tagName: 'novo-lead' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${createLeadId}`, source: triggerId, target: createLeadId, type: 'custom', animated: true },
        { id: `e-${createLeadId}-${whatsappId}`, source: createLeadId, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${tagId}`, source: whatsappId, target: tagId, type: 'custom', animated: true },
      );
    } else if (templateId === 'abandoned-cart') {
      const triggerId = `trigger-${Date.now()}`;
      const delayId = `delay-${Date.now() + 1}`;
      const conditionId = `condition-${Date.now() + 2}`;
      const whatsappId = `crmAction-${Date.now() + 3}`;
      const emailId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.webhookCart', 'Cart Webhook'), type: 'webhook', icon: 'Webhook', config: {}, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.wait1Hour', 'Wait 1 hour'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'hours' }, isConfigured: true } },
        { id: conditionId, type: 'condition', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.purchased', 'Purchased?'), type: 'if_else', icon: 'GitBranch', config: { field: 'purchased', operator: 'equals', value: 'false' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xLeft, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.reminderWhatsapp', 'WhatsApp Reminder'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
        { id: emailId, type: 'crmAction', position: { x: xRight, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.emailWithCoupon', 'Email with Coupon'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${delayId}`, source: triggerId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${conditionId}`, source: delayId, target: conditionId, type: 'custom', animated: true },
        { id: `e-${conditionId}-${whatsappId}`, source: conditionId, target: whatsappId, sourceHandle: 'no', type: 'custom', animated: true },
        { id: `e-${conditionId}-${emailId}`, source: conditionId, target: emailId, sourceHandle: 'yes', type: 'custom', animated: true },
      );
    } else if (templateId === 'appointment-reminder') {
      const triggerId = `trigger-${Date.now()}`;
      const delayId = `delay-${Date.now() + 1}`;
      const whatsappId = `crmAction-${Date.now() + 2}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.meetingScheduled', 'Meeting Scheduled'), type: 'webhook', icon: 'Webhook', config: {}, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.wait1hBefore', 'Wait until 1h before'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'hours' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.reminderWhatsapp', 'WhatsApp Reminder'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${delayId}`, source: triggerId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${whatsappId}`, source: delayId, target: whatsappId, type: 'custom', animated: true },
      );
    } else if (templateId === 'lead-qualification') {
      const triggerId = `trigger-${Date.now()}`;
      const createLeadId = `crmAction-${Date.now() + 1}`;
      const conditionId = `condition-${Date.now() + 2}`;
      const tagHotId = `crmAction-${Date.now() + 3}`;
      const tagColdId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.leadFromLP', 'Lead from Landing Page'), type: 'landing_page_submit', icon: 'Globe', config: {}, isConfigured: false } },
        { id: createLeadId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.createLead', 'Create Lead'), type: 'create_lead', icon: 'UserPlus', config: {}, isConfigured: false } },
        { id: conditionId, type: 'condition', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.scoreAbove50', 'Score > 50?'), type: 'if_else', icon: 'GitBranch', config: { field: 'lead.score', operator: 'greater_than', value: '50' }, isConfigured: true } },
        { id: tagHotId, type: 'crmAction', position: { x: xLeft, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.tagHotLead', 'Tag "Hot Lead"'), type: 'add_tag', icon: 'Tag', config: { tagName: 'lead-quente' }, isConfigured: true } },
        { id: tagColdId, type: 'crmAction', position: { x: xRight, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.tagColdLead', 'Tag "Cold Lead"'), type: 'add_tag', icon: 'Tag', config: { tagName: 'lead-frio' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${createLeadId}`, source: triggerId, target: createLeadId, type: 'custom', animated: true },
        { id: `e-${createLeadId}-${conditionId}`, source: createLeadId, target: conditionId, type: 'custom', animated: true },
        { id: `e-${conditionId}-${tagHotId}`, source: conditionId, target: tagHotId, sourceHandle: 'yes', type: 'custom', animated: true },
        { id: `e-${conditionId}-${tagColdId}`, source: conditionId, target: tagColdId, sourceHandle: 'no', type: 'custom', animated: true },
      );
    } else if (templateId === 'reengagement') {
      const triggerId = `trigger-${Date.now()}`;
      const delayId = `delay-${Date.now() + 1}`;
      const emailId = `crmAction-${Date.now() + 2}`;
      const delay2Id = `delay-${Date.now() + 3}`;
      const whatsappId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.tagInactiveAdded', 'Tag "Inactive" Added'), type: 'tag_added', icon: 'Tag', config: {}, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.wait1Day', 'Wait 1 day'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'days' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.reengagementEmail', 'Re-engagement Email'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
        { id: delay2Id, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.wait3Days', 'Wait 3 days'), type: 'wait', icon: 'Clock', config: { amount: 3, unit: 'days' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.whatsappSpecialOffer', 'WhatsApp Special Offer'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${delayId}`, source: triggerId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${delay2Id}`, source: emailId, target: delay2Id, type: 'custom', animated: true },
        { id: `e-${delay2Id}-${whatsappId}`, source: delay2Id, target: whatsappId, type: 'custom', animated: true },
      );
    } else if (templateId === 'post-sale') {
      const triggerId = `trigger-${Date.now()}`;
      const whatsappId = `crmAction-${Date.now() + 1}`;
      const delayId = `delay-${Date.now() + 2}`;
      const emailId = `crmAction-${Date.now() + 3}`;
      const stageId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.webhookPurchase', 'Purchase Webhook'), type: 'webhook', icon: 'Webhook', config: {}, isConfigured: false } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.whatsappThanks', 'WhatsApp Thank You'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.wait7Days', 'Wait 7 days'), type: 'wait', icon: 'Clock', config: { amount: 7, unit: 'days' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.emailRequestReview', 'Email Requesting Review'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
        { id: stageId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.moveToCustomers', 'Move to "Customers"'), type: 'change_stage', icon: 'GitBranch', config: { targetStageId: 'clientes' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${whatsappId}`, source: triggerId, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${delayId}`, source: whatsappId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${stageId}`, source: emailId, target: stageId, type: 'custom', animated: true },
      );
    } else if (templateId === 'webinar-followup') {
      const triggerId = `trigger-${Date.now()}`;
      const tagId = `crmAction-${Date.now() + 1}`;
      const delayId = `delay-${Date.now() + 2}`;
      const emailId = `crmAction-${Date.now() + 3}`;
      const whatsappId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.webhookWebinar', 'Webinar Attendance Webhook'), type: 'webhook', icon: 'Webhook', config: {}, isConfigured: false } },
        { id: tagId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.tagWebinar', 'Tag "Webinar"'), type: 'add_tag', icon: 'Tag', config: { tagName: 'participou-webinar' }, isConfigured: true } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.wait1Hour', 'Wait 1 hour'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'hours' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.emailWithRecording', 'Email with Recording'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.whatsappExclusiveOffer', 'WhatsApp Exclusive Offer'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${tagId}`, source: triggerId, target: tagId, type: 'custom', animated: true },
        { id: `e-${tagId}-${delayId}`, source: tagId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${whatsappId}`, source: emailId, target: whatsappId, type: 'custom', animated: true },
      );
    } else if (templateId === 'birthday-campaign') {
      const triggerId = `trigger-${Date.now()}`;
      const whatsappId = `crmAction-${Date.now() + 1}`;
      const emailId = `crmAction-${Date.now() + 2}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.webhookBirthday', 'Birthday Webhook'), type: 'webhook', icon: 'Webhook', config: {}, isConfigured: false } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.whatsappHappy', 'WhatsApp Happy Birthday'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.emailSpecialCoupon', 'Email with Special Coupon'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${whatsappId}`, source: triggerId, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${emailId}`, source: whatsappId, target: emailId, type: 'custom', animated: true },
      );
    } else if (templateId === 'nps-survey') {
      const triggerId = `trigger-${Date.now()}`;
      const delayId = `delay-${Date.now() + 1}`;
      const whatsappId = `crmAction-${Date.now() + 2}`;
      const ticketId = `crmAction-${Date.now() + 3}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.webhookServiceDone', 'Service Completed Webhook'), type: 'webhook', icon: 'Webhook', config: {}, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.wait2Days', 'Wait 2 days'), type: 'wait', icon: 'Clock', config: { amount: 2, unit: 'days' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.whatsappNpsSurvey', 'WhatsApp NPS Survey'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
        { id: ticketId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.createFollowupTicket', 'Create Follow-up Ticket'), type: 'create_ticket', icon: 'Ticket', config: { ticketTitle: 'Follow-up NPS', ticketPriority: 'medium' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${delayId}`, source: triggerId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${whatsappId}`, source: delayId, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${ticketId}`, source: whatsappId, target: ticketId, type: 'custom', animated: true },
      );
    } else if (templateId === 'marketplace-abandoned-cart') {
      const triggerId = `trigger-${Date.now()}`;
      const delayId = `delay-${Date.now() + 1}`;
      const smsId = `crmAction-${Date.now() + 2}`;
      const delay2Id = `delay-${Date.now() + 3}`;
      const emailId = `crmAction-${Date.now() + 4}`;
      const delay3Id = `delay-${Date.now() + 5}`;
      const whatsappId = `crmAction-${Date.now() + 6}`;
      const tagId = `marketplaceAction-${Date.now() + 7}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.cartAbandoned', 'Cart Abandoned'), type: 'marketplace_cart_abandoned', icon: 'ShoppingCart', config: {}, isConfigured: true } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.wait30Min', 'Wait 30 min'), type: 'wait', icon: 'Clock', config: { amount: 30, unit: 'minutes' }, isConfigured: true } },
        { id: smsId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.smsReminder', 'SMS Reminder'), type: 'crm_sms', icon: 'Smartphone', config: { sendNow: true, message: t('workflow.tpl.smsReminderMsg', 'Hi {{lead.name}}, you left items in your cart! Complete your purchase: {{order.link}}') }, isConfigured: true } },
        { id: delay2Id, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.wait2Hours', 'Wait 2 hours'), type: 'wait', icon: 'Clock', config: { amount: 2, unit: 'hours' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.emailWithDiscount', 'Email with Discount'), type: 'crm_email', icon: 'Mail', config: { sendNow: true, subject: t('workflow.tpl.emailDiscountSubject', '{{lead.name}}, your cart awaits! 10% OFF') }, isConfigured: false } },
        { id: delay3Id, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 5 }, data: { label: t('workflow.tpl.wait1Day', 'Wait 1 day'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'days' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 6 }, data: { label: t('workflow.tpl.whatsappLastChance', 'WhatsApp Last Chance'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true, message: t('workflow.tpl.whatsappLastChanceMsg', '{{lead.name}}, last chance! Your cart expires soon.') }, isConfigured: true } },
        { id: tagId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 7 }, data: { label: t('workflow.tpl.tagAbandonment', 'Tag "Abandonment"'), type: 'marketplace_add_tag', icon: 'Tag', config: { tagName: 'carrinho-abandonado' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${delayId}`, source: triggerId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${smsId}`, source: delayId, target: smsId, type: 'custom', animated: true },
        { id: `e-${smsId}-${delay2Id}`, source: smsId, target: delay2Id, type: 'custom', animated: true },
        { id: `e-${delay2Id}-${emailId}`, source: delay2Id, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${delay3Id}`, source: emailId, target: delay3Id, type: 'custom', animated: true },
        { id: `e-${delay3Id}-${whatsappId}`, source: delay3Id, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${tagId}`, source: whatsappId, target: tagId, type: 'custom', animated: true },
      );
    } else if (templateId === 'marketplace-post-purchase') {
      const triggerId = `trigger-${Date.now()}`;
      const smsId = `crmAction-${Date.now() + 1}`;
      const updateId = `marketplaceAction-${Date.now() + 2}`;
      const delayId = `delay-${Date.now() + 3}`;
      const emailId = `crmAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.paymentConfirmed', 'Payment Confirmed'), type: 'marketplace_payment_confirmed', icon: 'CreditCard', config: {}, isConfigured: true } },
        { id: smsId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.smsConfirmation', 'SMS Confirmation'), type: 'crm_sms', icon: 'Smartphone', config: { sendNow: true, message: t('workflow.tpl.smsConfirmationMsg', '{{lead.name}}, purchase confirmed! Order {{order.number}}') }, isConfigured: true } },
        { id: updateId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.updateOrder', 'Update Order'), type: 'marketplace_update_order', icon: 'Package', config: { orderStatus: 'processing' }, isConfigured: true } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.wait7Days', 'Wait 7 days'), type: 'wait', icon: 'Clock', config: { amount: 7, unit: 'days' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.emailRequestReview', 'Email Requesting Review'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${smsId}`, source: triggerId, target: smsId, type: 'custom', animated: true },
        { id: `e-${smsId}-${updateId}`, source: smsId, target: updateId, type: 'custom', animated: true },
        { id: `e-${updateId}-${delayId}`, source: updateId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
      );
    } else if (templateId === 'marketplace-new-store-onboarding') {
      const triggerId = `trigger-${Date.now()}`;
      const emailId = `crmAction-${Date.now() + 1}`;
      const delayId = `delay-${Date.now() + 2}`;
      const whatsappId = `crmAction-${Date.now() + 3}`;
      const delay2Id = `delay-${Date.now() + 4}`;
      const smsId = `crmAction-${Date.now() + 5}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.newStoreRegistered', 'New Store Registered'), type: 'marketplace_store_registered', icon: 'Store', config: {}, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.welcomeEmailShort', 'Welcome Email'), type: 'crm_email', icon: 'Mail', config: { sendNow: true, subject: t('workflow.tpl.welcomeSubject', 'Welcome to the Marketplace!') }, isConfigured: false } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.wait1Day', 'Wait 1 day'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'days' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.whatsappTips', 'WhatsApp - Tips'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true, message: t('workflow.tpl.whatsappTipsMsg', 'Hi {{lead.name}}! Here are 3 tips to sell more on the marketplace...') }, isConfigured: true } },
        { id: delay2Id, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.wait3Days', 'Wait 3 days'), type: 'wait', icon: 'Clock', config: { amount: 3, unit: 'days' }, isConfigured: true } },
        { id: smsId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 5 }, data: { label: t('workflow.tpl.smsSetupCheck', 'SMS - All set up?'), type: 'crm_sms', icon: 'Smartphone', config: { sendNow: true, message: t('workflow.tpl.smsSetupCheckMsg', '{{lead.name}}, have you set up your store? Need help?') }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${emailId}`, source: triggerId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${delayId}`, source: emailId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${whatsappId}`, source: delayId, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${delay2Id}`, source: whatsappId, target: delay2Id, type: 'custom', animated: true },
        { id: `e-${delay2Id}-${smsId}`, source: delay2Id, target: smsId, type: 'custom', animated: true },
      );

    // === New Marketplace Templates ===

    } else if (templateId === 'marketplace-order-tracking') {
      // Order shipped → notify buyer → wait → order delivered → email review
      const triggerId = `trigger-${Date.now()}`;
      const notifyId = `marketplaceAction-${Date.now() + 1}`;
      const whatsappId = `crmAction-${Date.now() + 2}`;
      const delayId = `delay-${Date.now() + 3}`;
      const triggerId2 = `trigger-${Date.now() + 4}`;
      const emailId = `crmAction-${Date.now() + 5}`;
      const tagId = `marketplaceAction-${Date.now() + 6}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.orderShipped', 'Order Shipped'), type: 'marketplace_order_shipped', icon: 'Truck', config: {}, isConfigured: true } },
        { id: notifyId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.notifyShipped', 'Notify Shipment'), type: 'marketplace_notify_buyer', icon: 'Mail', config: { channel: 'email', subject: t('workflow.tpl.shippedSubject', 'Your order {{order.number}} has been shipped!'), message: t('workflow.tpl.shippedMsg', '{{customer.name}}, your order is on its way!') }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.whatsappTracking', 'WhatsApp Tracking'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true, message: t('workflow.tpl.whatsappTrackingMsg', '{{customer.name}}, your order was shipped! Track here: {{order.tracking_url}}') }, isConfigured: true } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.wait5Days', 'Wait 5 days'), type: 'wait', icon: 'Clock', config: { amount: 5, unit: 'days' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.emailDelivered', 'Email - Did it arrive?'), type: 'crm_email', icon: 'Mail', config: { sendNow: true, subject: t('workflow.tpl.deliveredSubject', '{{customer.name}}, did your order arrive?') }, isConfigured: false } },
        { id: tagId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 5 }, data: { label: t('workflow.tpl.tagDelivered', 'Tag "Delivered"'), type: 'marketplace_add_tag', icon: 'Tag', config: { tagName: 'pedido-entregue' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${notifyId}`, source: triggerId, target: notifyId, type: 'custom', animated: true },
        { id: `e-${notifyId}-${whatsappId}`, source: notifyId, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${delayId}`, source: whatsappId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${tagId}`, source: emailId, target: tagId, type: 'custom', animated: true },
      );

    } else if (templateId === 'marketplace-payment-failed') {
      // Payment failed → SMS → wait 1h → email → wait 1 day → whatsapp last chance → tag
      const triggerId = `trigger-${Date.now()}`;
      const smsId = `crmAction-${Date.now() + 1}`;
      const delayId = `delay-${Date.now() + 2}`;
      const emailId = `crmAction-${Date.now() + 3}`;
      const delay2Id = `delay-${Date.now() + 4}`;
      const whatsappId = `crmAction-${Date.now() + 5}`;
      const tagId = `marketplaceAction-${Date.now() + 6}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.paymentFailed', 'Payment Failed'), type: 'marketplace_payment_failed', icon: 'AlertTriangle', config: {}, isConfigured: true } },
        { id: smsId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.smsPaymentRetry', 'SMS - Try Again'), type: 'crm_sms', icon: 'Smartphone', config: { sendNow: true, message: t('workflow.tpl.smsPaymentRetryMsg', '{{customer.name}}, there was a problem with the payment for order {{order.number}}. Try again: {{order.payment_url}}') }, isConfigured: true } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.wait1Hour', 'Wait 1 hour'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'hours' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.emailPaymentHelp', 'Email - Payment Help'), type: 'crm_email', icon: 'Mail', config: { sendNow: true, subject: t('workflow.tpl.emailPaymentHelpSubject', 'We need your help to complete the payment') }, isConfigured: false } },
        { id: delay2Id, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.wait1Day', 'Wait 1 day'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'days' }, isConfigured: true } },
        { id: whatsappId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 5 }, data: { label: t('workflow.tpl.whatsappPaymentLast', 'WhatsApp - Last Attempt'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true, message: t('workflow.tpl.whatsappPaymentLastMsg', '{{customer.name}}, your payment is still pending. Can I help?') }, isConfigured: true } },
        { id: tagId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 6 }, data: { label: t('workflow.tpl.tagPaymentIssue', 'Tag "Payment Pending"'), type: 'marketplace_add_tag', icon: 'Tag', config: { tagName: 'pagamento-pendente' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${smsId}`, source: triggerId, target: smsId, type: 'custom', animated: true },
        { id: `e-${smsId}-${delayId}`, source: smsId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${delay2Id}`, source: emailId, target: delay2Id, type: 'custom', animated: true },
        { id: `e-${delay2Id}-${whatsappId}`, source: delay2Id, target: whatsappId, type: 'custom', animated: true },
        { id: `e-${whatsappId}-${tagId}`, source: whatsappId, target: tagId, type: 'custom', animated: true },
      );

    } else if (templateId === 'marketplace-order-cancelled') {
      // Order cancelled → notify buyer → notification internal → create ticket
      const triggerId = `trigger-${Date.now()}`;
      const notifyId = `marketplaceAction-${Date.now() + 1}`;
      const notifIntId = `action-${Date.now() + 2}`;
      const ticketId = `marketplaceAction-${Date.now() + 3}`;
      const tagId = `marketplaceAction-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.orderCancelled', 'Order Cancelled'), type: 'marketplace_order_cancelled', icon: 'XCircle', config: {}, isConfigured: true } },
        { id: notifyId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.notifyCancellation', 'Notify Cancellation'), type: 'marketplace_notify_buyer', icon: 'Mail', config: { channel: 'email', subject: t('workflow.tpl.cancelSubject', 'Order {{order.number}} cancelled'), message: t('workflow.tpl.cancelMsg', '{{customer.name}}, your order has been cancelled. If you need help, contact us.') }, isConfigured: true } },
        { id: notifIntId, type: 'action', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.internalNotif', 'Team Alert'), type: 'notification', icon: 'Bell', config: { message: t('workflow.tpl.cancelAlertMsg', 'Order {{order.number}} cancelled - Customer: {{customer.name}}') }, isConfigured: true } },
        { id: ticketId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.noteCancel', 'Anotar no pedido'), type: 'marketplace_add_note', icon: 'StickyNote', config: { note: t('workflow.tpl.cancelNote', 'Cliente avisado do cancelamento pela automação') }, isConfigured: true } },
        { id: tagId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.tagCancelled', 'Tag "Cancelled"'), type: 'marketplace_add_tag', icon: 'Tag', config: { tagName: 'pedido-cancelado' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${notifyId}`, source: triggerId, target: notifyId, type: 'custom', animated: true },
        { id: `e-${notifyId}-${notifIntId}`, source: notifyId, target: notifIntId, type: 'custom', animated: true },
        { id: `e-${notifIntId}-${ticketId}`, source: notifIntId, target: ticketId, type: 'custom', animated: true },
        { id: `e-${ticketId}-${tagId}`, source: ticketId, target: tagId, type: 'custom', animated: true },
      );

    } else if (templateId === 'marketplace-refund-flow') {
      // Order refunded → notify buyer → update order → tag → notification internal
      const triggerId = `trigger-${Date.now()}`;
      const notifyId = `marketplaceAction-${Date.now() + 1}`;
      const emailId = `crmAction-${Date.now() + 2}`;
      const tagId = `marketplaceAction-${Date.now() + 3}`;
      const notifId = `action-${Date.now() + 4}`;

      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.tpl.orderRefunded', 'Order Refunded'), type: 'marketplace_order_refunded', icon: 'RotateCcw', config: {}, isConfigured: true } },
        { id: notifyId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.notifyRefund', 'Notify Refund'), type: 'marketplace_notify_buyer', icon: 'Mail', config: { channel: 'email', subject: t('workflow.tpl.refundSubject', 'Refund processed - Order {{order.number}}'), message: t('workflow.tpl.refundMsg', '{{customer.name}}, your refund has been processed successfully.') }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.emailFeedback', 'Email - Feedback'), type: 'crm_email', icon: 'Mail', config: { sendNow: true, subject: t('workflow.tpl.feedbackSubject', '{{customer.name}}, how can we improve?') }, isConfigured: false } },
        { id: tagId, type: 'marketplaceAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.tagRefunded', 'Tag "Refunded"'), type: 'marketplace_add_tag', icon: 'Tag', config: { tagName: 'reembolsado' }, isConfigured: true } },
        { id: notifId, type: 'action', position: { x: xCenter, y: 50 + ySpacing * 4 }, data: { label: t('workflow.tpl.internalNotif', 'Team Alert'), type: 'notification', icon: 'Bell', config: { message: t('workflow.tpl.refundAlertMsg', 'Refund processed - Order {{order.number}} - {{customer.name}}') }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${notifyId}`, source: triggerId, target: notifyId, type: 'custom', animated: true },
        { id: `e-${notifyId}-${emailId}`, source: notifyId, target: emailId, type: 'custom', animated: true },
        { id: `e-${emailId}-${tagId}`, source: emailId, target: tagId, type: 'custom', animated: true },
        { id: `e-${tagId}-${notifId}`, source: tagId, target: notifId, type: 'custom', animated: true },
      );
    } else if (templateId === 'social-comment-reply') {
      // Instagram + Facebook + Threads → Reply comment (AI) → Private Reply (DM)
      const igTriggerId = `trigger-${Date.now()}`;
      const fbTriggerId = `trigger-${Date.now() + 1}`;
      const threadsTriggerId = `trigger-${Date.now() + 2}`;
      const replyId = `action-${Date.now() + 3}`;
      const privateReplyId = `action-${Date.now() + 4}`;
      const triggerGap = 280;

      generatedNodes.push(
        { id: igTriggerId, type: 'trigger', position: { x: xCenter - triggerGap, y: 50 }, data: { label: t('workflow.social.instagramComment', 'Instagram Comment'), type: 'instagram_comment', icon: 'Instagram', config: { keyword: '' }, isConfigured: true } },
        { id: fbTriggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.social.facebookComment', 'Facebook Comment'), type: 'facebook_comment', icon: 'Facebook', config: { keyword: '' }, isConfigured: true } },
        { id: threadsTriggerId, type: 'trigger', position: { x: xCenter + triggerGap, y: 50 }, data: { label: t('workflow.social.threadsComment', 'Threads Comment'), type: 'threads_comment', icon: 'AtSign', config: { keyword: '' }, isConfigured: true } },
        { id: replyId, type: 'action', position: { x: xCenter - 180, y: 50 + ySpacing }, data: { label: t('workflow.social.replyComment', 'Reply to Comment'), type: 'reply_comment', icon: 'MessageCircle', config: { replyMode: 'ai', aiTone: 'friendly', aiInstructions: '', replyScope: 'each', message: '' }, isConfigured: true } },
        { id: privateReplyId, type: 'action', position: { x: xCenter + 180, y: 50 + ySpacing }, data: { label: t('workflow.social.privateReply', 'Private Reply (DM)'), type: 'private_reply', icon: 'Send', config: { message: '' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${igTriggerId}-${replyId}`, source: igTriggerId, target: replyId, type: 'custom', animated: true },
        { id: `e-${fbTriggerId}-${replyId}`, source: fbTriggerId, target: replyId, type: 'custom', animated: true },
        { id: `e-${threadsTriggerId}-${replyId}`, source: threadsTriggerId, target: replyId, type: 'custom', animated: true },
        { id: `e-${igTriggerId}-${privateReplyId}`, source: igTriggerId, target: privateReplyId, type: 'custom', animated: true },
        { id: `e-${fbTriggerId}-${privateReplyId}`, source: fbTriggerId, target: privateReplyId, type: 'custom', animated: true },
        { id: `e-${threadsTriggerId}-${privateReplyId}`, source: threadsTriggerId, target: privateReplyId, type: 'custom', animated: true },
      );
    } else if (templateId === 'live-private-reply') {
      // Live keyword → Private Reply (DM) with product
      const triggerId = `trigger-${Date.now()}`;
      const privateReplyId = `action-${Date.now() + 1}`;
      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.social.liveKeywordDetected', 'Live Keyword'), type: 'live_keyword_detected', icon: 'Radio', config: {}, isConfigured: true } },
        { id: privateReplyId, type: 'action', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.social.privateReply', 'Private Reply (DM)'), type: 'private_reply', icon: 'Send', config: { message: t('workflow.tpl.liveReplyMsg', 'Hi {{commenter.username}}! I noticed you\'re interested in {{product.name}}! Here\'s the link: {{product.url}} 🛒\nUse coupon {{coupon}} for a discount!'), productUrl: '{{product.url}}', couponCode: '{{coupon}}' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${privateReplyId}`, source: triggerId, target: privateReplyId, type: 'custom', animated: true },
      );
    } else if (templateId === 'live-reply-followup') {
      // Live keyword → Private Reply → Delay 1h → Follow-up email
      const triggerId = `trigger-${Date.now()}`;
      const privateReplyId = `action-${Date.now() + 1}`;
      const delayId = `delay-${Date.now() + 2}`;
      const emailId = `crmAction-${Date.now() + 3}`;
      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.social.liveKeywordDetected', 'Live Keyword'), type: 'live_keyword_detected', icon: 'Radio', config: {}, isConfigured: true } },
        { id: privateReplyId, type: 'action', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.social.privateReply', 'Private Reply (DM)'), type: 'private_reply', icon: 'Send', config: { message: t('workflow.tpl.liveReplySimple', 'Hi {{commenter.username}}! Here\'s the link: {{product.url}} 🛒'), productUrl: '{{product.url}}', couponCode: '{{coupon}}' }, isConfigured: true } },
        { id: delayId, type: 'delay', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.wait1Hour', 'Wait 1 hour'), type: 'wait', icon: 'Clock', config: { amount: 1, unit: 'hours' }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 3 }, data: { label: t('workflow.tpl.followupEmail', 'Email Follow-up'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${privateReplyId}`, source: triggerId, target: privateReplyId, type: 'custom', animated: true },
        { id: `e-${privateReplyId}-${delayId}`, source: privateReplyId, target: delayId, type: 'custom', animated: true },
        { id: `e-${delayId}-${emailId}`, source: delayId, target: emailId, type: 'custom', animated: true },
      );
    } else if (templateId === 'live-capture-remarketing') {
      // Live keyword → Create Lead → Add Tag
      const triggerId = `trigger-${Date.now()}`;
      const createLeadId = `crmAction-${Date.now() + 1}`;
      const tagId = `crmAction-${Date.now() + 2}`;
      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.social.liveKeywordDetected', 'Live Keyword'), type: 'live_keyword_detected', icon: 'Radio', config: {}, isConfigured: true } },
        { id: createLeadId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.tpl.createLead', 'Create Lead'), type: 'create_lead', icon: 'UserPlus', config: {}, isConfigured: false } },
        { id: tagId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.addTag', 'Add Tag'), type: 'add_tag', icon: 'Tag', config: { tag: 'live-lead' }, isConfigured: true } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${createLeadId}`, source: triggerId, target: createLeadId, type: 'custom', animated: true },
        { id: `e-${createLeadId}-${tagId}`, source: createLeadId, target: tagId, type: 'custom', animated: true },
      );
    } else if (templateId === 'live-coupon-reply') {
      // Live keyword → Private Reply with coupon
      const triggerId = `trigger-${Date.now()}`;
      const privateReplyId = `action-${Date.now() + 1}`;
      const createLeadId = `crmAction-${Date.now() + 2}`;
      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.social.liveKeywordDetected', 'Live Keyword'), type: 'live_keyword_detected', icon: 'Radio', config: {}, isConfigured: true } },
        { id: privateReplyId, type: 'action', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.social.privateReply', 'Private Reply (DM)'), type: 'private_reply', icon: 'Send', config: { message: t('workflow.tpl.liveCouponMsg', 'Hi {{commenter.username}}! 🎉 Here\'s your exclusive coupon: {{coupon}}\nUse it at: {{product.url}}'), productUrl: '{{product.url}}', couponCode: '{{coupon}}' }, isConfigured: true } },
        { id: createLeadId, type: 'crmAction', position: { x: xCenter, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.createLead', 'Create Lead'), type: 'create_lead', icon: 'UserPlus', config: {}, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${privateReplyId}`, source: triggerId, target: privateReplyId, type: 'custom', animated: true },
        { id: `e-${privateReplyId}-${createLeadId}`, source: privateReplyId, target: createLeadId, type: 'custom', animated: true },
      );
    } else if (templateId === 'live-post-broadcast') {
      // Live ended → Private Reply (DM) + Email + WhatsApp
      const triggerId = `trigger-${Date.now()}`;
      const dmId = `action-${Date.now() + 1}`;
      const emailId = `crmAction-${Date.now() + 2}`;
      const whatsappId = `crmAction-${Date.now() + 3}`;
      generatedNodes.push(
        { id: triggerId, type: 'trigger', position: { x: xCenter, y: 50 }, data: { label: t('workflow.social.liveEnded', 'Live Contact List (Post-Live)'), type: 'live_ended', icon: 'Radio', config: {}, isConfigured: true } },
        { id: dmId, type: 'action', position: { x: xCenter, y: 50 + ySpacing }, data: { label: t('workflow.social.privateReply', 'Private Reply (DM)'), type: 'private_reply', icon: 'Send', config: { message: t('workflow.tpl.postLiveDmMsg', 'Hi {{contact.name}}! Thanks for joining our live {{liveSession.title}}! 🎉\n\nHere are the product links we showed. Enjoy!') }, isConfigured: true } },
        { id: emailId, type: 'crmAction', position: { x: xLeft + 50, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.broadcastEmail', 'Post-Live Email'), type: 'crm_email', icon: 'Mail', config: { sendNow: true }, isConfigured: false } },
        { id: whatsappId, type: 'crmAction', position: { x: xRight - 50, y: 50 + ySpacing * 2 }, data: { label: t('workflow.tpl.broadcastWhatsapp', 'Post-Live WhatsApp'), type: 'crm_whatsapp', icon: 'MessageSquare', config: { sendNow: true }, isConfigured: false } },
      );
      generatedEdges.push(
        { id: `e-${triggerId}-${dmId}`, source: triggerId, target: dmId, type: 'custom', animated: true },
        { id: `e-${dmId}-${emailId}`, source: dmId, target: emailId, type: 'custom', animated: true },
        { id: `e-${dmId}-${whatsappId}`, source: dmId, target: whatsappId, type: 'custom', animated: true },
      );
    }

    setNodes(generatedNodes);
    setEdges(generatedEdges);
    setShowTemplatesModal(false);
    toast.success(t('workflow.template.loaded', 'Template loaded! Customize the details.'));

    // Fit view with more padding for zoom out effect
    setTimeout(() => {
      reactFlowInstance?.fitView({ padding: 0.4, maxZoom: 0.8 });
    }, 100);
  };

  // Save CRM config
  const saveCrmConfig = async () => {
    if (crmConfig.enabled && !crmConfig.apiKey) {
      toast.error(t('workflow.crm.apiKeyRequired', 'Enter the CRM API Key'));
      return;
    }

    // Projeto é OBRIGATÓRIO: sem ele o CRM joga o lead no 1º projeto da empresa
    // (fallback silencioso) e o usuário não encontra o lead depois.
    if (crmConfig.enabled && crmConfig.apiKey && !crmConfig.projectId) {
      toast.error(t('workflow.crm.projectRequired', 'Selecione o Projeto do CRM — sem ele os leads caem no projeto errado'));
      return;
    }

    // Test connection if enabled
    if (crmConfig.enabled && crmConfig.apiKey) {
      try {
        const result = await workflowService.testCrmConnection(crmConfig.apiKey);
        if (!result.success) {
          toast.error(result.message || t('workflow.crm.connectionFailed', 'CRM connection failed'));
          return;
        }
      } catch (error) {
        toast.error(t('workflow.crm.connectionError', 'Error testing connection'));
        return;
      }
    }

    const updatedCrm = { ...crmConfig, isSaved: true };
    setShowCRMConfig(false);

    // Persist immediately if workflow already exists
    if (id) {
      try {
        await workflowService.updateWorkflow(id, {
          crm_integration: { enabled: updatedCrm.enabled, apiKey: updatedCrm.apiKey, baseUrl: updatedCrm.baseUrl, projectId: updatedCrm.projectId, wabaId: updatedCrm.wabaId, whatsappToken: updatedCrm.whatsappToken },
        });
        setCrmConfig(updatedCrm);
        toast.success(t('workflow.crm.saved', 'CRM configuration saved!'));
      } catch (error) {
        console.error('Error saving CRM config:', error);
        toast.error(t('workflow.crm.saveError', 'Error saving CRM configuration'));
      }
    } else {
      setCrmConfig(updatedCrm);
      toast.success(t('workflow.crm.saved', 'CRM configuration saved!'));
    }
  };

  const saveMarketplaceConfig = async () => {
    if (marketplaceConfig.enabled && !marketplaceConfig.connectionId && !marketplaceConfig.apiKey) {
      toast.error(t('workflow.mkp.connectFirst', 'Conecte a loja primeiro (cole a chave da loja).'));
      return;
    }
    const updatedConfig = { ...marketplaceConfig, isSaved: true };
    setShowCRMConfig(false);

    // Persist immediately if workflow already exists
    if (id) {
      try {
        await workflowService.updateWorkflow(id, {
          marketplace_integration: buildMarketplaceIntegration(updatedConfig),
        });
        setMarketplaceConfig(updatedConfig);
        toast.success(t('workflow.marketplace.saved', 'Marketplace configuration saved!'));
      } catch (error) {
        console.error('Error saving marketplace config:', error);
        toast.error(t('workflow.marketplace.saveError', 'Error saving marketplace configuration'));
      }
    } else {
      setMarketplaceConfig(updatedConfig);
      toast.success(t('workflow.marketplace.saved', 'Marketplace configuration saved!'));
    }
  };

  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        type: 'custom',
        animated: true,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#6366f1',
        },
        style: {
          stroke: '#6366f1',
          strokeWidth: 2,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();

      if (!reactFlowWrapper.current || !reactFlowInstance) return;

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const nodeData = JSON.parse(event.dataTransfer.getData('application/reactflow'));

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNode: Node<FlowNodeData> = {
        id: `${nodeData.type}-${Date.now()}`,
        type: nodeData.type,
        position,
        data: {
          label: nodeData.label,
          type: nodeData.subtype,
          icon: nodeData.icon,
          config: {},
          isConfigured: AUTO_CONFIGURED_TYPES.includes(nodeData.subtype),
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const onNodeClick = useCallback((event: React.MouseEvent, node: Node<FlowNodeData>) => {
    setSelectedNode(node);
    setTagMode('existing');
  }, []);

  const updateNodeData = useCallback((nodeId: string, data: Partial<FlowNodeData>) => {
    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === nodeId) {
          const updated = {
            ...node,
            data: {
              ...node.data,
              ...data,
            },
          };
          setSelectedNode((prev) => prev?.id === nodeId ? updated as Node<FlowNodeData> : prev);
          return updated;
        }
        return node;
      })
    );
  }, [setNodes]);

  const deleteNode = useCallback((nodeId: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== nodeId));
    setEdges((eds) => eds.filter((edge) => edge.source !== nodeId && edge.target !== nodeId));
    setSelectedNode(null);
  }, [setNodes, setEdges]);

  const duplicateNode = useCallback((node: Node<FlowNodeData>) => {
    const newNode: Node<FlowNodeData> = {
      ...node,
      id: `${node.type}-${Date.now()}`,
      position: {
        x: node.position.x + 50,
        y: node.position.y + 50,
      },
    };
    setNodes((nds) => nds.concat(newNode));
  }, [setNodes]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const onDragStart = (event: React.DragEvent<HTMLDivElement>, nodeData: any) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(nodeData));
    event.dataTransfer.effectAllowed = 'move';
  };

  const addNodeByClick = useCallback((nodeData: any) => {
    if (!reactFlowInstance) return;
    // Place below the last node, or at center if no nodes exist
    let posX = 250;
    let posY = 100;
    if (nodes.length > 0) {
      const lastNode = nodes[nodes.length - 1];
      posX = lastNode.position.x;
      posY = lastNode.position.y + 150;
    }
    const isAutoConfigured = AUTO_CONFIGURED_TYPES.includes(nodeData.subtype);

    const newNode: Node<FlowNodeData> = {
      id: `${nodeData.type}-${Date.now()}`,
      type: nodeData.type,
      position: { x: posX, y: posY },
      data: {
        label: nodeData.label,
        type: nodeData.subtype,
        icon: nodeData.icon,
        config: {},
        isConfigured: isAutoConfigured,
      },
    };
    setNodes((nds) => nds.concat(newNode));
    // Pan to the new node
    setTimeout(() => {
      reactFlowInstance.setCenter(posX + 100, posY + 40, { zoom: 0.8, duration: 400 });
    }, 50);
    toast.success(`${nodeData.label} ${t('workflow.nodeAdded', 'added')}`);
  }, [reactFlowInstance, nodes.length, setNodes]);

  const getIconComponent = (iconName: string) => {
    const icons: Record<string, React.ReactNode> = {
      Globe: <Globe className="w-4 h-4" />,
      FileText: <FileText className="w-4 h-4" />,
      Eye: <Eye className="w-4 h-4" />,
      Tag: <Tag className="w-4 h-4" />,
      UserPlus: <UserPlus className="w-4 h-4" />,
      Webhook: <Webhook className="w-4 h-4" />,
      Clock: <Clock className="w-4 h-4" />,
      Mail: <Mail className="w-4 h-4" />,
      MessageSquare: <MessageSquare className="w-4 h-4" />,
      Phone: <Phone className="w-4 h-4" />,
      PhoneCall: <PhoneCall className="w-4 h-4" />,
      Bell: <Bell className="w-4 h-4" />,
      Link2: <Link2 className="w-4 h-4" />,
      Users: <Users className="w-4 h-4" />,
      GitBranch: <GitBranch className="w-4 h-4" />,
      Target: <Target className="w-4 h-4" />,
      Filter: <Filter className="w-4 h-4" />,
      Bot: <Bot className="w-4 h-4" />,
      Ticket: <Ticket className="w-4 h-4" />,
      StickyNote: <StickyNote className="w-4 h-4" />,
      Newspaper: <Newspaper className="w-4 h-4" />,
      Calendar: <Calendar className="w-4 h-4" />,
      CalendarCheck: <CalendarCheck className="w-4 h-4" />,
      Video: <Video className="w-4 h-4" />,
      Gift: <Gift className="w-4 h-4" />,
      Star: <Star className="w-4 h-4" />,
      Zap: <Zap className="w-4 h-4" />,
      CheckCircle: <CheckCircle className="w-4 h-4" />,
      Smartphone: <Smartphone className="w-4 h-4" />,
      ShoppingCart: <ShoppingCart className="w-4 h-4" />,
      Package: <Package className="w-4 h-4" />,
      CreditCard: <CreditCard className="w-4 h-4" />,
      Store: <Store className="w-4 h-4" />,
      BarChart3: <BarChart3 className="w-4 h-4" />,
      Truck: <Truck className="w-4 h-4" />,
      RefreshCw: <RefreshCw className="w-4 h-4" />,
      PackageCheck: <PackageCheck className="w-4 h-4" />,
      XCircle: <XCircle className="w-4 h-4" />,
      RotateCcw: <RotateCcw className="w-4 h-4" />,
      AlertTriangle: <AlertTriangle className="w-4 h-4" />,
      Send: <Send className="w-4 h-4" />,
      MessageCircle: <MessageCircle className="w-4 h-4" />,
      Radio: <Radio className="w-4 h-4" />,
      Instagram: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
      Facebook: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    };
    return icons[iconName] || <Zap className="w-4 h-4" />;
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, string> = {
      emerald: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
      blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
      green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
      purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
      gray: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700',
      indigo: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
      pink: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 border-pink-200 dark:border-pink-800',
      orange: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800',
      cyan: 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 border-cyan-200 dark:border-cyan-800',
      teal: 'bg-teal-100 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 border-teal-200 dark:border-teal-800',
      red: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    };
    return colors[color] || colors.gray;
  };

  const renderNodeCategory = (
    title: string,
    categoryKey: string,
    nodes: typeof availableNodes.triggers,
    icon: React.ReactNode
  ) => (
    <div className="mb-1">
      <button
        onClick={() => toggleCategory(categoryKey)}
        className={`w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${
          expandedCategories[categoryKey]
            ? 'bg-gray-100 dark:bg-gray-700/50 text-gray-900 dark:text-white'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`}
      >
        <div className="flex items-center gap-2.5">
          {icon}
          {title}
          <span className="ml-1 px-1.5 py-0.5 text-[10px] font-medium bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-md">
            {nodes.length}
          </span>
        </div>
        <ChevronRight
          className={`w-4 h-4 transition-transform duration-200 ${
            expandedCategories[categoryKey] ? 'rotate-90' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          expandedCategories[categoryKey] ? 'max-h-[1000px] opacity-100 mt-2' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="space-y-1.5 px-1">
          {nodes.map((node, index) => (
            <div
              key={index}
              draggable
              onDragStart={(e) => onDragStart(e, node)}
              onClick={() => { addNodeByClick(node); if (isMobile) setSidebarCollapsed(true); }}
              className={`group flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer md:cursor-grab md:active:cursor-grabbing transition-all duration-200 hover:shadow-md hover:scale-[1.02] active:scale-[0.98] ${getColorClasses(node.color)}`}
            >
              <Plus className="w-3 h-3 opacity-60 md:hidden" />
              <Grip className="w-3 h-3 opacity-30 group-hover:opacity-60 transition-opacity hidden md:block" />
              <div className="flex-shrink-0">{getIconComponent(node.icon)}</div>
              <span className="text-xs font-medium truncate flex-1">{node.label}</span>
              <Plus className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60 transition-opacity hidden md:block" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    // h-dvh = altura visível real no celular (h-screen fica de reserva p/ navegador antigo);
    // pan-x/pan-y: pinça fora do canvas não dá zoom na página inteira
    <div className="h-screen h-dvh w-full max-w-[100vw] overflow-hidden flex flex-col bg-gray-100 dark:bg-gray-900 [touch-action:pan-x_pan-y]">
      {/* Header — uma linha só; no celular só ícones */}
      <div className="min-h-[56px] md:h-16 py-1 md:py-0 bg-gradient-to-r from-white via-white to-gray-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-900 border-b border-gray-200 dark:border-gray-700 flex flex-nowrap items-center justify-between px-2 sm:px-3 md:px-4 flex-shrink-0 shadow-sm gap-1.5 sm:gap-2 min-w-0">
        <div className="flex items-center gap-1 sm:gap-2 md:gap-4 min-w-0 flex-1">
          <button
            onClick={() => navigate('/workflows')}
            aria-label={t('common.back', 'Voltar')}
            className="p-2 md:p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all duration-200 group flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200 transition-colors" />
          </button>
          <div className="sm:border-l border-gray-200 dark:border-gray-700 pl-1 sm:pl-3 md:pl-4 min-w-0 flex-1">
            <input
              type="text"
              value={workflow.name}
              onChange={(e) => setWorkflow({ ...workflow, name: e.target.value })}
              className="text-base md:text-lg font-bold text-gray-900 dark:text-white bg-transparent border-none focus:outline-none focus:ring-0 w-full min-w-0 max-w-full sm:max-w-[200px] md:max-w-[280px] truncate"
              placeholder={t('workflow.namePlaceholder', 'Flow name')}
            />
            <div className="flex items-center gap-2 md:gap-3 text-sm text-gray-500 dark:text-gray-400 min-w-0">
              <span className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap ${
                workflow.status === 'active'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : workflow.status === 'paused'
                    ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${
                  workflow.status === 'active' ? 'bg-green-500 animate-pulse' :
                  workflow.status === 'paused' ? 'bg-yellow-500' : 'bg-gray-400'
                }`} />
                {workflow.status === 'active' ? t('workflow.status.active', 'Active') :
                 workflow.status === 'paused' ? t('workflow.status.paused', 'Paused') :
                 t('workflow.status.draft', 'Draft')}
              </span>
              <span className="hidden sm:flex items-center gap-1">
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <LayoutGrid className="w-3.5 h-3.5" />
                {nodes.length} {t('workflow.nodes', 'nodes')}
              </span>
              <span className="hidden md:flex items-center gap-1">
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <GitBranch className="w-3.5 h-3.5" />
                {edges.length} {t('workflow.edges', 'connections')}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2.5 flex-shrink-0">
          {/* AI Generate Button */}
          <button
            onClick={() => setShowAIModal(true)}
            aria-label={t('workflow.ai.generate', 'Generate with AI')}
            title={t('workflow.ai.generate', 'Generate with AI')}
            className="flex items-center gap-2 p-2 sm:px-2.5 md:px-4 md:py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white hover:from-violet-600 hover:via-purple-600 hover:to-fuchsia-600 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/40 hover:scale-[1.02] active:scale-[0.98]"
          >
            <Sparkles className="w-4 h-4" />
            <span className="hidden lg:inline">{t('workflow.ai.generate', 'Generate with AI')}</span>
          </button>

          {/* Templates Button */}
          <button
            onClick={() => setShowTemplatesModal(true)}
            className="hidden sm:flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 shadow-sm"
          >
            <LayoutTemplate className="w-4 h-4" />
            <span className="hidden lg:inline">{t('workflow.templatesButton', 'Templates')}</span>
          </button>

          {/* Histórico de execuções (com os erros de cada passo) */}
          {id && (
            <button
              onClick={() => setShowRunLog(true)}
              aria-label={t('workflow.runLog.open', 'Execuções')}
              title={t('workflow.runLog.open', 'Execuções')}
              className="flex items-center gap-2 p-2 sm:px-2.5 md:px-3 lg:px-4 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 shadow-sm"
              data-testid="open-run-log"
            >
              <History className="w-4 h-4" />
              <span className="hidden lg:inline">{t('workflow.runLog.open', 'Execuções')}</span>
            </button>
          )}

          {/* Integrations Status */}
          <button
            onClick={() => setShowCRMConfig(true)}
            aria-label={t('workflow.integrations', 'Integrations')}
            title={t('workflow.integrations', 'Integrations')}
            className={`flex items-center gap-2 p-2 sm:px-2.5 md:px-3 lg:px-4 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 border shadow-sm ${
              isAnyIntegrationConnected
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800 hover:border-green-300'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-gray-300'
            }`}
          >
            {isAnyIntegrationConnected ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <Link2 className="w-4 h-4" />
            )}
            <span className="hidden lg:inline">{t('workflow.integrations', 'Integrations')}</span>
          </button>

          <div className="hidden md:block">
            <LanguageSelector />
          </div>

          <div className="hidden md:block w-px h-8 bg-gray-200 dark:bg-gray-700 mx-1" />

          <Button variant="secondary" onClick={() => setShowSettings(true)} leftIcon={<Settings className="w-4 h-4" />} className="!rounded-xl !p-2 sm:!px-4 sm:!py-2" aria-label={t('common.settings', 'Settings')} title={t('common.settings', 'Settings')}>
            <span className="hidden lg:inline">{t('common.settings', 'Settings')}</span>
          </Button>

          {workflow.status === 'active' ? (
            <button
              onClick={async () => {
                if (id) {
                  try {
                    await workflowService.updateWorkflowStatus(id, 'paused');
                    setWorkflow({ ...workflow, status: 'paused' });
                    toast.success(t('workflow.paused', 'Flow paused'));
                  } catch (error) {
                    toast.error(t('workflow.pauseError', 'Error pausing flow'));
                  }
                } else {
                  setWorkflow({ ...workflow, status: 'paused' });
                }
              }}
              aria-label={t('workflow.pause', 'Pause')}
              className="flex items-center gap-2 p-2 sm:px-2.5 md:px-4 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 shadow-sm"
            >
              <Pause className="w-4 h-4" />
              <span className="hidden sm:inline">{t('workflow.pause', 'Pause')}</span>
            </button>
          ) : (
            <button
              onClick={async () => {
                if (nodes.length < 2) {
                  toast.error(t('workflow.minNodesError', 'Add at least 2 nodes to activate'));
                  return;
                }
                const hasTrigger = nodes.some(n => n.type === 'trigger');
                if (!hasTrigger) {
                  toast.error(t('workflow.triggerRequiredError', 'Add a trigger to activate'));
                  return;
                }
                // Fluxo com ações de CRM exige a integração CRM conectada — sem isso os
                // nós falham em silêncio e o lead nunca chega ao CRM.
                const crmNodeTypes = ['create_lead', 'crm_create_lead', 'update_lead', 'change_stage', 'add_tag', 'create_ticket', 'assign_owner', 'crm_whatsapp', 'send_whatsapp', 'crm_email', 'send_email', 'crm_sms', 'send_sms', 'crm_call', 'create_task', 'schedule_call'];
                const hasCrmNode = nodes.some(n => crmNodeTypes.includes(n.data?.type || ''));
                if (hasCrmNode && !isCrmConnected) {
                  toast.error(t('workflow.crmRequiredToActivate', 'Este fluxo usa ações de CRM. Configure e teste a integração CRM (botão Integrações) antes de ativar.'));
                  return;
                }
                // Passos/gatilhos do marketplace sem loja conectada: nunca disparariam nem agiriam
                const hasMarketplaceNode = nodes.some(n => String(n.data?.type || '').startsWith('marketplace_'));
                if (hasMarketplaceNode && !isMarketplaceConnected) {
                  toast.error(t('workflow.mkp.requiredToActivate', 'Este fluxo usa a loja do marketplace. Conecte a loja (botão Integrações › Marketplace) antes de ativar.'));
                  setIntegrationTab('marketplace');
                  setShowCRMConfig(true);
                  return;
                }
                // Fluxos salvos antes da obrigatoriedade do projeto podem estar sem ele
                if (hasCrmNode && isCrmConnected && !crmConfig.projectId) {
                  toast.error(t('workflow.crm.projectRequired', 'Selecione o Projeto do CRM — sem ele os leads caem no projeto errado'));
                  setShowCRMConfig(true);
                  return;
                }
                if (id) {
                  try {
                    // Salva o canvas antes de ativar — senão o servidor ativa a versão
                    // antiga do grafo (sem os nós recém-arrastados) e o gatilho não dispara.
                    const saved = await saveWorkflow();
                    if (!saved) return;
                    await workflowService.updateWorkflowStatus(id, 'active');
                    setWorkflow({ ...workflow, status: 'active' });
                    toast.success(t('workflow.activated', 'Flow activated'));
                  } catch (error) {
                    toast.error(t('workflow.activateError', 'Error activating flow'));
                  }
                } else {
                  toast.error(t('workflow.saveFirstError', 'Save the flow before activating'));
                }
              }}
              aria-label={t('workflow.activate', 'Activate')}
              className="flex items-center gap-2 p-2 sm:px-2.5 md:px-4 md:py-2.5 rounded-xl text-sm font-medium transition-all duration-200 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/30 shadow-sm"
            >
              <Play className="w-4 h-4" />
              <span className="hidden sm:inline">{t('workflow.activate', 'Activate')}</span>
            </button>
          )}

          <button
            onClick={saveWorkflow}
            disabled={isSaving}
            aria-label={t('common.save', 'Save')}
            className="flex items-center gap-2 p-2 sm:px-3 md:px-5 sm:py-2 md:py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/30 hover:shadow-primary-500/40 hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">{t('common.save', 'Save')}</span>
          </button>
        </div>
      </div>

      {/* Automação com passo de IA: quanto custa em média cada execução (automação gasta a cada gatilho) */}
      {nodes.some((n: any) => n.data?.config?.replyMode === 'ai' || n.data?.config?.mode === 'ai' || String(n.data?.type || '').startsWith('ai')) && (
        <div className="px-4 py-1.5 border-b border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/80">
          <AiCostBadge feature="workflow" action="run" unitLabel={t('aiCredits.perRun', 'por execução')} />
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative min-h-0 min-w-0">
        {/* Left Sidebar - Node Palette (celular: gaveta por cima do canvas; recolhida some e vira botão flutuante) */}
        {isMobile && !sidebarCollapsed && (
          <div className="absolute inset-0 z-20 bg-black/30" onClick={() => setSidebarCollapsed(true)} />
        )}
        <div
          className={`bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transition-all duration-300 overflow-hidden shadow-lg ${
            isMobile
              ? (sidebarCollapsed ? 'hidden' : 'absolute inset-y-0 left-0 z-30 w-[85vw] max-w-sm')
              : (sidebarCollapsed ? 'w-14' : 'w-64 md:w-80')
          }`}
        >
          {sidebarCollapsed ? (
            <div className="p-3 flex flex-col items-center gap-3">
              <button
                onClick={() => setSidebarCollapsed(false)}
                className="p-2.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors group"
                title={t('workflow.expandSidebar', 'Expand panel')}
              >
                <ChevronRight className="w-5 h-5 text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200" />
              </button>
              <div className="w-8 h-px bg-gray-200 dark:bg-gray-700" />
              <button
                onClick={() => { setSidebarCollapsed(false); setExpandedCategories(prev => ({ ...prev, triggers: true })); }}
                className="p-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-xl transition-colors group"
                title={t('workflow.sidebar.triggers', 'Triggers')}
              >
                <Zap className="w-5 h-5 text-emerald-500" />
              </button>
              <button
                onClick={() => { setSidebarCollapsed(false); setExpandedCategories(prev => ({ ...prev, crmActions: true })); }}
                className="p-2.5 hover:bg-pink-50 dark:hover:bg-pink-900/20 rounded-xl transition-colors group"
                title={t('workflow.sidebar.crmActions', 'CRM Actions')}
              >
                <Users className="w-5 h-5 text-pink-500" />
              </button>
              <button
                onClick={() => { setSidebarCollapsed(false); setExpandedCategories(prev => ({ ...prev, marketplaceTriggers: true, marketplaceActions: true })); }}
                className="p-2.5 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-xl transition-colors group"
                title={t('workflow.sidebar.marketplace', 'Marketplace')}
              >
                <ShoppingCart className="w-5 h-5 text-indigo-500" />
              </button>
              <button
                onClick={() => { setSidebarCollapsed(false); setExpandedCategories(prev => ({ ...prev, logic: true })); }}
                className="p-2.5 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-xl transition-colors group"
                title={t('workflow.sidebar.logic', 'Logic')}
              >
                <GitBranch className="w-5 h-5 text-orange-500" />
              </button>
            </div>
          ) : (
            <div className="h-full flex flex-col">
              <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
                    <LayoutGrid className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">{t('workflow.components', 'Components')}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('workflow.dragToBuild', 'Drag to build')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSidebarCollapsed(true)}
                  aria-label={t('common.close', 'Fechar')}
                  className="p-2 md:p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 md:w-4 md:h-4 text-gray-400" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {renderNodeCategory(t('workflow.sidebar.triggers', 'Triggers'), 'triggers', availableNodes.triggers, <Zap className="w-4 h-4 text-emerald-500" />)}
                {renderNodeCategory(t('workflow.sidebar.actions', 'Actions'), 'actions', availableNodes.actions, <Play className="w-4 h-4 text-blue-500" />)}
                {renderNodeCategory(t('workflow.sidebar.crmActions', 'CRM Actions'), 'crmActions', availableNodes.crmActions, <Users className="w-4 h-4 text-pink-500" />)}
                {renderNodeCategory(t('workflow.sidebar.socialTriggers', 'Social Triggers'), 'socialTriggers', availableNodes.socialTriggers, <AtSign className="w-4 h-4 text-pink-500" />)}
                {renderNodeCategory(t('workflow.sidebar.socialActions', 'Social Actions'), 'socialActions', availableNodes.socialActions, <Send className="w-4 h-4 text-purple-500" />)}
                {renderNodeCategory(t('workflow.sidebar.marketplaceTriggers', 'Marketplace Triggers'), 'marketplaceTriggers', availableNodes.marketplaceTriggers, <ShoppingCart className="w-4 h-4 text-indigo-500" />)}
                {renderNodeCategory(t('workflow.sidebar.marketplaceActions', 'Marketplace Actions'), 'marketplaceActions', availableNodes.marketplaceActions, <Store className="w-4 h-4 text-indigo-500" />)}
                {renderNodeCategory(t('workflow.sidebar.logic', 'Logic'), 'logic', availableNodes.logic, <GitBranch className="w-4 h-4 text-orange-500" />)}
              </div>

              {/* Templates Quick Access */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800 space-y-2">
                <button
                  onClick={() => setShowTemplatesModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-500 via-primary-600 to-purple-600 text-white rounded-xl font-semibold text-sm hover:from-primary-600 hover:via-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 hover:scale-[1.02] active:scale-[0.98]"
                >
                  <LayoutTemplate className="w-4 h-4" />
                  {t('workflow.readyTemplates', 'Ready Templates')}
                </button>
                <button
                  onClick={() => setShowAIModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-100 to-purple-100 dark:from-violet-900/30 dark:to-purple-900/30 text-purple-700 dark:text-purple-300 rounded-xl font-medium text-sm hover:from-violet-200 hover:to-purple-200 dark:hover:from-violet-900/40 dark:hover:to-purple-900/40 transition-all duration-200 border border-purple-200 dark:border-purple-800"
                >
                  <Sparkles className="w-4 h-4" />
                  {t('workflow.ai.generateShort', 'Create with AI')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Canvas */}
        <div className="flex-1 relative min-w-0 [touch-action:none]" ref={reactFlowWrapper}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onInit={setReactFlowInstance}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionMode={ConnectionMode.Loose}
            defaultEdgeOptions={{
              type: 'custom',
              animated: true,
            }}
            defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
            minZoom={0.2}
            maxZoom={1.5}
            fitView
            fitViewOptions={{ padding: isMobile ? 0.15 : 0.3, maxZoom: 0.8 }}
            zoomOnPinch
            panOnDrag
            zoomOnDoubleClick={!isMobile}
            preventScrolling
            snapToGrid
            snapGrid={[15, 15]}
            className="bg-gray-50 dark:bg-gray-900"
          >
            <Background color="#94a3b8" gap={20} size={1} />
            <Controls showInteractive={!isMobile} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" />
            {!isMobile && (
            <MiniMap
              nodeColor={(node) => {
                switch (node.type) {
                  case 'trigger':
                    return '#10b981';
                  case 'action':
                    return '#3b82f6';
                  case 'crmAction':
                    return '#ec4899';
                  case 'condition':
                    return '#f97316';
                  case 'delay':
                    return '#06b6d4';
                  case 'abTest':
                    return '#a855f7';
                  case 'marketplaceAction':
                    return '#6366f1';
                  default:
                    return '#6b7280';
                }
              }}
              className="bg-white dark:bg-gray-800"
            />
            )}

            {/* Empty State */}
            {nodes.length === 0 && (
              <Panel position="top-center" className="mt-4 sm:mt-16">
                <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl shadow-gray-300/50 dark:shadow-black/30 p-5 sm:p-8 text-center w-[calc(100vw-2rem)] max-w-lg border border-gray-100 dark:border-gray-700">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary-100 via-purple-100 to-pink-100 dark:from-primary-900/30 dark:via-purple-900/30 dark:to-pink-900/30 rounded-3xl flex items-center justify-center mx-auto shadow-lg shadow-primary-500/20">
                      <MousePointer className="w-10 h-10 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-emerald-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg animate-bounce">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                    {t('workflow.emptyState.title', 'Create your automation flow')}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
                    {t('workflow.emptyState.description', 'Drag a trigger from the sidebar to start or use AI to generate automatically.')}
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-6">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">{t('workflow.trigger', 'Trigger')}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                      <span className="w-2.5 h-2.5 rounded-full bg-pink-500" />
                      <span className="text-xs font-medium text-pink-700 dark:text-pink-400">{t('workflow.action', 'Action')}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <span className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                      <span className="text-xs font-medium text-orange-700 dark:text-orange-400">{t('workflow.condition', 'Condition')}</span>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
                    <button
                      onClick={() => setShowTemplatesModal(true)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                    >
                      <LayoutTemplate className="w-4 h-4" />
                      {t('workflow.useTemplate', 'Use template')}
                    </button>
                    <button
                      onClick={() => setShowAIModal(true)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-xl font-medium text-sm hover:from-violet-600 hover:to-purple-600 transition-all shadow-lg shadow-purple-500/25"
                    >
                      <Sparkles className="w-4 h-4" />
                      {t('workflow.ai.generate', 'Generate with AI')}
                    </button>
                  </div>
                </div>
              </Panel>
            )}
          </ReactFlow>

          {/* Celular: botão flutuante para abrir os componentes (no lugar da barra lateral) */}
          {isMobile && sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed(false)}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white text-sm font-semibold shadow-lg shadow-primary-500/30 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              {t('workflow.addStep', 'Adicionar etapa')}
            </button>
          )}
        </div>

        {/* Right Sidebar - Node Properties (celular: folha de baixo com fundo escuro) */}
        {selectedNode && isMobile && (
          <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setSelectedNode(null)} />
        )}
        {selectedNode && (
          <div className={isMobile
            ? 'fixed inset-x-0 bottom-0 z-50 h-[80dvh] max-h-[85vh] bg-white dark:bg-gray-800 rounded-t-2xl overflow-y-auto shadow-2xl'
            : 'w-96 flex-shrink-0 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto shadow-xl'
          }>
            <NodePropertiesPanel
              node={selectedNode}
              allNodes={nodes}
              onUpdate={(data) => updateNodeData(selectedNode.id, data)}
              onDelete={() => deleteNode(selectedNode.id)}
              onDuplicate={() => duplicateNode(selectedNode)}
              onClose={() => setSelectedNode(null)}
              crmEnabled={crmConfig.enabled}
              crmApiKey={crmConfig.apiKey}
              wabaId={crmConfig.wabaId}
              whatsappToken={crmConfig.whatsappToken}
              isCrmConnected={isCrmConnected}
              isMarketplaceConnected={isMarketplaceConnected}
              marketplaceStoreName={marketplaceConfig.storeName || (marketplaceConnections.find((c) => c.id === marketplaceConfig.connectionId)?.store_name || '')}
              onOpenMarketplace={() => { setIntegrationTab('marketplace'); setShowCRMConfig(true); }}
              crmPipelines={crmPipelines}
              landingPages={availableLandingPages}
              abTests={abTests}
              crmTags={crmTags}
              isLoadingTags={isLoadingTags}
              tagMode={tagMode}
              setTagMode={setTagMode}
            />
          </div>
        )}
      </div>

      {/* Integrations Modal */}
      <Modal
        isOpen={showCRMConfig}
        onClose={() => { setShowCRMConfig(false); setIntegrationTab('crm'); }}
        title=""
        size="lg"
      >
        <div className="space-y-3 sm:space-y-6">
          {/* Header */}
          <div className="text-center pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-20 h-20 bg-gradient-to-br from-pink-500 via-rose-500 to-red-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-pink-500/30">
              <Link2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('workflow.integrations.title', 'Integrations')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('workflow.integrations.subtitle', 'Connect your services to automate actions in the workflow')}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
            <button
              onClick={() => setIntegrationTab('crm')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                integrationTab === 'crm'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Users className="w-4 h-4" />
              CRM
              {isCrmConnected && <span className="w-2 h-2 rounded-full bg-green-500" />}
            </button>
            <button
              onClick={() => setIntegrationTab('marketplace')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                integrationTab === 'marketplace'
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Store className="w-4 h-4" />
              Marketplace
              {isMarketplaceConnected && <span className="w-2 h-2 rounded-full bg-green-500" />}
            </button>
          </div>

          {/* CRM Tab */}
          {integrationTab === 'crm' && (
            <>
              {/* Toggle */}
              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{t('workflow.crm.enableIntegration', 'Enable Integration')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('workflow.crm.enableDescription', 'Enables communication with CRM')}</p>
                </div>
                <button
                  onClick={() => setCrmConfig({ ...crmConfig, enabled: !crmConfig.enabled, isSaved: false })}
                  className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                    crmConfig.enabled
                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 shadow-lg shadow-pink-500/30'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transform transition-all duration-300 ${
                      crmConfig.enabled ? 'left-7' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>

              {crmConfig.enabled && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('workflow.crm.apiUrl', 'CRM API URL')}
                    </label>
                    <Input
                      value={CRM_BASE_URL}
                      disabled
                      className="!rounded-xl !bg-gray-100 dark:!bg-gray-700 !cursor-not-allowed !opacity-75"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('workflow.crm.documentation', 'Documentation')}: <a href="https://crm.devskin.com/api-docs.html" target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline font-medium">{t('workflow.crm.viewDocs', 'View API Docs')}</a>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('workflow.crm.apiKey', 'API Key')}
                    </label>
                    <Input
                      type="password"
                      value={crmConfig.apiKey}
                      onChange={(e) => {
                        setCrmConfig({ ...crmConfig, apiKey: e.target.value, projectId: '', isSaved: false });
                        setCrmProjects([]); // chave mudou → lista de projetos é de outra empresa
                      }}
                      placeholder={t('workflow.crm.apiKeyPlaceholder', 'Your CRM API key')}
                      className="!rounded-xl"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {t('workflow.crm.findApiKey', 'Find your API Key in Settings > Integrations in CRM')}
                    </p>
                  </div>

                  {/* Project Selector — obrigatório: sem projeto o CRM manda o lead pro 1º projeto da empresa */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('workflow.crm.project', 'CRM Project')} <span className="text-red-500">*</span>
                    </label>
                    {crmProjects.length > 0 ? (
                      <select
                        value={crmConfig.projectId}
                        onChange={(e) => setCrmConfig({ ...crmConfig, projectId: e.target.value, isSaved: false })}
                        className={`w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-pink-500 focus:border-pink-500 ${crmConfig.projectId ? 'border-gray-300 dark:border-gray-600' : 'border-red-400 dark:border-red-600'}`}
                      >
                        <option value="">{t('workflow.crm.selectProject', 'Select a project')}</option>
                        {crmProjects.map((project) => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 text-sm">
                        {isLoadingProjects ? (
                          <><RefreshCw className="w-4 h-4 animate-spin" /> {t('workflow.crm.loadingProjects', 'Loading projects...')}</>
                        ) : crmConfig.apiKey ? (
                          <>{t('workflow.crm.projectsLoadFailed', 'Não foi possível carregar os projetos — confira a API Key ou clique em Testar Conexão')}</>
                        ) : (
                          <>{t('workflow.crm.enterKeyToLoadProjects', 'Informe a API Key para carregar os projetos')}</>
                        )}
                      </div>
                    )}
                    <p className={`text-xs mt-1 ${crmConfig.projectId ? 'text-gray-500' : 'text-red-500 font-medium'}`}>
                      {crmConfig.projectId
                        ? t('workflow.crm.projectHint', 'Select which CRM project leads will be created in')
                        : t('workflow.crm.projectRequiredHint', 'Obrigatório — sem o projeto, os leads caem no primeiro projeto da empresa')}
                    </p>
                  </div>

                  {/* WhatsApp Business API Config */}
                  <div className="p-4 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800">
                    <div className="flex items-center gap-2 mb-3">
                      <MessageSquare className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700 dark:text-green-400">WhatsApp Business API</span>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          WABA ID (WhatsApp Business Account)
                        </label>
                        <Input
                          value={crmConfig.wabaId}
                          onChange={(e) => setCrmConfig({ ...crmConfig, wabaId: e.target.value, isSaved: false })}
                          placeholder="Ex: 123456789012345"
                          className="!rounded-lg !text-sm"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Encontre no Meta Business Suite &gt; Configurações do WhatsApp &gt; ID da conta
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          Token de Acesso (WhatsApp)
                        </label>
                        <Input
                          type="password"
                          value={crmConfig.whatsappToken}
                          onChange={(e) => setCrmConfig({ ...crmConfig, whatsappToken: e.target.value, isSaved: false })}
                          placeholder="EAAxxxxxxx..."
                          className="!rounded-lg !text-sm !font-mono"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Token permanente do System User com permissão whatsapp_business_messaging
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-gradient-to-r from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 rounded-2xl border border-emerald-200 dark:border-emerald-800">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 mb-3">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-semibold">{t('workflow.crm.availableActions', 'Available actions with CRM:')}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { icon: UserPlus, text: t('workflow.crm.action1', 'Create and update leads') },
                        { icon: MessageSquare, text: t('workflow.crm.action2', 'Send WhatsApp') },
                        { icon: Mail, text: t('workflow.crm.action3', 'Send Email') },
                        { icon: PhoneCall, text: t('workflow.crm.action4', 'Call/Schedule call') },
                        { icon: GitBranch, text: t('workflow.crm.action5', 'Move in pipeline') },
                        { icon: Tag, text: t('workflow.crm.action6', 'Add tags') },
                      ].map((action, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                          <action.icon className="w-4 h-4 flex-shrink-0" />
                          <span>{action.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* CRM Actions */}
              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                <button
                  onClick={() => setShowCRMConfig(false)}
                  className="px-5 py-2.5 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm order-3 sm:order-1"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                {crmConfig.enabled && (
                  <button
                    onClick={async () => {
                      if (!crmConfig.apiKey) return;
                      setIsTestingCrm(true);
                      try {
                        const result = await workflowService.testCrmConnection(crmConfig.apiKey);
                        if (result.success) {
                          toast.success(t('workflow.crm.connectionSuccess', 'CRM connection successful!'));
                          setIsLoadingProjects(true);
                          try {
                            const projectsResult = await workflowService.getCrmProjects(crmConfig.apiKey);
                            if (projectsResult.success) {
                              setCrmProjects(projectsResult.data);
                            }
                          } catch (e) {
                            console.warn('Failed to load projects:', e);
                          } finally {
                            setIsLoadingProjects(false);
                          }
                        } else {
                          toast.error(result.message || t('workflow.crm.connectionFailed', 'Connection failed'));
                        }
                      } catch (error) {
                        toast.error(t('workflow.crm.connectionError', 'Error testing connection'));
                      } finally {
                        setIsTestingCrm(false);
                      }
                    }}
                    disabled={!crmConfig.apiKey || isTestingCrm}
                    className={`flex items-center justify-center gap-2 px-5 py-2.5 font-medium rounded-xl transition-colors text-sm order-2 ${
                      crmConfig.apiKey && !isTestingCrm
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                        : 'bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isTestingCrm ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        {t('workflow.crm.testing', 'Testing...')}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {t('workflow.crm.testConnection', 'Test Connection')}
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={saveCrmConfig}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg shadow-pink-500/25 text-sm order-1 sm:order-3"
                >
                  <Save className="w-4 h-4" />
                  {t('common.saveConfig', 'Save Configuration')}
                </button>
              </div>
            </>
          )}

          {/* Marketplace Tab — loja da CONTA: cola a chave, a loja é descoberta e os avisos são cadastrados sozinhos */}
          {integrationTab === 'marketplace' && (
            <>
              <div className="flex items-center justify-between p-5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800/50 dark:to-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">{t('workflow.mkp.enableIntegration', 'Usar a loja neste fluxo')}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('workflow.mkp.enableDescription', 'O fluxo começa sozinho quando a loja avisa (compra, carrinho, envio...) e pode agir na loja.')}</p>
                </div>
                <button
                  onClick={() => setMarketplaceConfig({ ...marketplaceConfig, enabled: !marketplaceConfig.enabled, isSaved: false })}
                  className={`relative w-14 h-7 flex-shrink-0 rounded-full transition-all duration-300 ${
                    marketplaceConfig.enabled
                      ? 'bg-gradient-to-r from-pink-500 to-rose-500 shadow-lg shadow-pink-500/30'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                  aria-label={t('workflow.mkp.enableIntegration', 'Usar a loja neste fluxo')}
                  data-testid="mkp-toggle"
                >
                  <div
                    className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow-md transform transition-all duration-300 ${
                      marketplaceConfig.enabled ? 'left-7' : 'left-0.5'
                    }`}
                  />
                </button>
              </div>

              {marketplaceConfig.enabled && (
                <>
                  <MarketplaceConnectionPanel
                    selectedId={marketplaceConfig.connectionId || null}
                    onSelect={selectMarketplaceConnection}
                    onChange={setMarketplaceConnections}
                  />

                  {!marketplaceConfig.connectionId && marketplaceConfig.apiKey && (
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-800 dark:text-yellow-300">
                      {t('workflow.mkp.legacyKey', 'Este fluxo tem uma chave antiga colada direto nele. Conecte a loja acima para os avisos chegarem sozinhos.')}
                    </div>
                  )}

                  {/* O que chega da loja */}
                  <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('workflow.mkp.variablesTitle', 'Dados que você pode usar nas mensagens:')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono leading-relaxed">
                      {'{{customer.name}} {{customer.first_name}} {{customer.email}} {{customer.phone}} {{order.number}} {{order.total}} {{order.status}} {{order.tracking_code}} {{store.name}} {{store.url}} {{cart.url}}'}
                    </p>
                  </div>
                </>
              )}

              <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 pt-2">
                <button
                  onClick={() => setShowCRMConfig(false)}
                  className="px-5 py-2.5 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm order-2 sm:order-1"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  onClick={saveMarketplaceConfig}
                  className="flex items-center justify-center gap-2 px-6 py-2.5 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-rose-600 transition-all shadow-lg shadow-pink-500/25 text-sm order-1 sm:order-2"
                  data-testid="mkp-save"
                >
                  <Save className="w-4 h-4" />
                  {t('common.saveConfig', 'Save Configuration')}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Histórico de execuções */}
      {id && showRunLog && (
        <WorkflowRunLog workflowId={id} nodes={nodes} isOpen={showRunLog} onClose={() => setShowRunLog(false)} />
      )}

      {/* Workflow Settings Modal */}
      <Modal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        title=""
      >
        <div className="space-y-3 sm:space-y-6">
          {/* Header */}
          <div className="text-center pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-600 to-gray-800 dark:from-gray-500 dark:to-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
              {t('workflow.settings.title', 'Flow Settings')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('workflow.settings.subtitle', 'Set the name and description of the flow')}
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('workflow.settings.name', 'Flow Name')}
            </label>
            <Input
              value={workflow.name}
              onChange={(e) => setWorkflow({ ...workflow, name: e.target.value })}
              placeholder={t('workflow.settings.namePlaceholder', 'Flow name')}
              className="!rounded-xl"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              {t('workflow.settings.description', 'Description')}
            </label>
            <Textarea
              value={workflow.description}
              onChange={(e) => setWorkflow({ ...workflow, description: e.target.value })}
              placeholder={t('workflow.settings.descriptionPlaceholder', 'Describe the purpose of this flow...')}
              rows={4}
              className="!rounded-xl"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowSettings(false)}
              className="px-5 py-2.5 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              onClick={() => {
                toast.success(t('workflow.settings.saved', 'Settings saved!'));
                setShowSettings(false);
              }}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-gray-700 to-gray-900 dark:from-gray-600 dark:to-gray-800 text-white font-semibold rounded-xl hover:from-gray-800 hover:to-gray-950 transition-all shadow-lg"
            >
              <Save className="w-4 h-4" />
              {t('common.save', 'Save')}
            </button>
          </div>
        </div>
      </Modal>

      {/* AI Generation Modal */}
      <Modal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        title=""
        size="lg"
      >
        <div className="space-y-3 sm:space-y-6">
          {/* Header */}
          <div className="text-center pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="w-20 h-20 bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-purple-500/30">
              <Wand2 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t('workflow.ai.title', 'Generate Flow with AI')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              {t('workflow.ai.description', 'Describe what you want to automate and AI will create the flow automatically')}
            </p>
          </div>

          {/* Input */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              {t('workflow.ai.promptLabel', 'What do you want to automate?')}
            </label>
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder={t('workflow.ai.promptPlaceholder', 'E.g.: When a lead arrives from landing page, create in CRM, send welcome WhatsApp, wait 2 days and send follow-up email...')}
              rows={5}
              className="resize-none !rounded-xl !text-base"
            />
          </div>

          {/* Tips */}
          <div className="p-4 bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 rounded-xl border border-violet-200 dark:border-violet-800">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-lg flex-shrink-0">
                <Sparkles className="w-5 h-5 text-violet-600 dark:text-violet-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-violet-900 dark:text-violet-200 mb-1">
                  {t('workflow.ai.tips', 'Tips for better results')}
                </p>
                <p className="text-sm text-violet-700 dark:text-violet-300">
                  {t('workflow.ai.tipsText', 'Mention actions like "whatsapp", "email", "call", "meeting", "wait", "if/condition" so AI understands better.')}
                </p>
              </div>
            </div>
          </div>

          {/* Marketplace context */}
          {isMarketplaceEnabled && (
            <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex-shrink-0">
                  <ShoppingCart className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-indigo-900 dark:text-indigo-200 mb-1">
                    {t('workflow.ai.marketplaceEnabled', 'Marketplace connected')}
                  </p>
                  <p className="text-sm text-indigo-700 dark:text-indigo-300">
                    {t('workflow.ai.marketplaceTips', 'You can mention "order", "purchase", "abandoned cart", "payment", "refund", "store" for AI to use marketplace triggers and actions.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setShowAIModal(false)}
              className="px-5 py-2.5 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <AiCostBadge feature="ai-generation" action="workflow-generate" className="mr-auto" />
            <button
              onClick={generateWithAI}
              disabled={isGeneratingAI || !aiPrompt.trim()}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-violet-500 via-purple-500 to-fuchsia-500 text-white font-semibold rounded-xl hover:from-violet-600 hover:via-purple-600 hover:to-fuchsia-600 transition-all shadow-lg shadow-purple-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingAI ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Sparkles className="w-5 h-5" />
              )}
              {t('workflow.ai.generateButton', 'Generate Flow')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-none sm:rounded-3xl shadow-2xl w-full max-w-5xl h-full sm:h-auto sm:max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-primary-50 via-purple-50 to-pink-50 dark:from-gray-800 dark:via-gray-800 dark:to-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-5">
                  <div className="w-10 h-10 sm:w-16 sm:h-16 bg-gradient-to-br from-primary-500 via-primary-600 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xl shadow-primary-500/30 flex-shrink-0">
                    <LayoutTemplate className="w-5 h-5 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                      {t('workflow.templates.title', 'Automation Templates')}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1 hidden sm:block">
                      {t('workflow.templates.description', 'Choose a ready template to start quickly')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTemplatesModal(false)}
                  className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />
                </button>
              </div>

              {/* Category Pills */}
              <div className="flex items-center gap-2 mt-3 sm:mt-5 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide">
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white dark:bg-gray-700 rounded-xl text-xs sm:text-sm font-medium text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                  {t('common.all', 'All')} ({WORKFLOW_TEMPLATES.length})
                </span>
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-xs sm:text-sm font-medium text-blue-700 dark:text-blue-400 whitespace-nowrap">
                  {t('workflow.category.marketing', 'Marketing')} ({WORKFLOW_TEMPLATES.filter(t => t.category === 'marketing').length})
                </span>
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-100 dark:bg-orange-900/30 rounded-xl text-xs sm:text-sm font-medium text-orange-700 dark:text-orange-400 whitespace-nowrap">
                  {t('workflow.category.sales', 'Sales')} ({WORKFLOW_TEMPLATES.filter(t => t.category === 'sales').length})
                </span>
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl text-xs sm:text-sm font-medium text-purple-700 dark:text-purple-400 whitespace-nowrap">
                  {t('workflow.category.operations', 'Operations')} ({WORKFLOW_TEMPLATES.filter(t => t.category === 'operations').length})
                </span>
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl text-xs sm:text-sm font-medium text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                  {t('workflow.category.onboarding', 'Onboarding')} ({WORKFLOW_TEMPLATES.filter(t => t.category === 'onboarding').length})
                </span>
                <span className="px-3 sm:px-4 py-1.5 sm:py-2 bg-pink-100 dark:bg-pink-900/30 rounded-xl text-xs sm:text-sm font-medium text-pink-700 dark:text-pink-400 whitespace-nowrap">
                  {t('workflow.category.social', 'Social')} ({WORKFLOW_TEMPLATES.filter(t => t.category === 'social').length})
                </span>
              </div>

              {/* Marketplace badge */}
              <div className="mt-2 flex items-center gap-2">
                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg text-xs font-medium text-indigo-700 dark:text-indigo-400 flex items-center gap-1.5">
                  <ShoppingCart className="w-3 h-3" />
                  {t('workflow.category.marketplace', 'Marketplace')} ({WORKFLOW_TEMPLATES.filter(t => t.id.startsWith('marketplace-')).length})
                </span>
              </div>
            </div>

            {/* Templates Grid */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 auto-rows-fr">
                {WORKFLOW_TEMPLATES.map((template) => {
                  const IconComponent = {
                    Mail, UserPlus, Target, Calendar, Filter, Zap, CheckCircle, Video, Gift, Star, ShoppingCart, Package, Store, Truck, CreditCard, XCircle, RotateCcw, MessageCircle, Radio
                  }[template.icon] || Zap;

                  return (
                    <button
                      key={template.id}
                      onClick={() => loadTemplate(template.id)}
                      className="p-5 text-left bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-700 rounded-2xl hover:border-primary-400 dark:hover:border-primary-600 hover:shadow-xl hover:shadow-primary-500/10 hover:bg-white dark:hover:bg-gray-800 transition-all duration-300 group flex flex-col"
                    >
                      <div className="flex items-center gap-4 mb-3">
                        <div className={`p-3 rounded-xl shadow-md transition-all duration-300 group-hover:scale-105 group-hover:shadow-lg flex-shrink-0 ${
                          template.category === 'marketing'
                            ? 'bg-gradient-to-br from-blue-500 to-indigo-500 shadow-blue-500/20'
                            : template.category === 'onboarding'
                              ? 'bg-gradient-to-br from-emerald-500 to-green-500 shadow-emerald-500/20'
                              : template.category === 'sales'
                                ? 'bg-gradient-to-br from-orange-500 to-red-500 shadow-orange-500/20'
                                : 'bg-gradient-to-br from-purple-500 to-pink-500 shadow-purple-500/20'
                        }`}>
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors text-sm leading-tight">
                          {t(`workflow.templates.${template.id}.name`, template.name)}
                        </h4>
                      </div>

                      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed flex-1 min-h-[40px]">
                        {t(`workflow.templates.${template.id}.description`, template.description)}
                      </p>

                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded-md ${
                          template.category === 'marketing'
                            ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                            : template.category === 'onboarding'
                              ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                              : template.category === 'sales'
                                ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                                : 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
                        }`}>
                          {t(`workflow.category.${template.category}`, template.category)}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-primary-500 dark:text-primary-400 font-medium group-hover:gap-2 transition-all">
                          {t('workflow.useTemplate', 'Use template')}
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 sm:px-8 py-3 sm:py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0 flex-shrink-0">
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 hidden sm:block">
                <Sparkles className="w-4 h-4 inline mr-1" />
                {t('workflow.templates.hint', 'Or use AI to create a custom flow')}
              </p>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => {
                    setShowTemplatesModal(false);
                    setShowAIModal(true);
                  }}
                  className="flex-1 sm:flex-none px-4 py-2.5 sm:py-2 text-purple-600 dark:text-purple-400 font-medium rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Wand2 className="w-4 h-4" />
                  {t('workflow.ai.generateShort', 'Create with AI')}
                </button>
                <button
                  onClick={() => setShowTemplatesModal(false)}
                  className="flex-1 sm:flex-none px-5 py-2.5 sm:py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm text-center"
                >
                  {t('common.close', 'Close')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Node Properties Panel Component
interface NodePropertiesPanelProps {
  node: Node<FlowNodeData>;
  allNodes: Node<FlowNodeData>[];
  onUpdate: (data: Partial<FlowNodeData>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onClose: () => void;
  crmEnabled: boolean;
  crmApiKey?: string;
  wabaId?: string;
  whatsappToken?: string;
  isCrmConnected: boolean;
  isMarketplaceConnected: boolean;
  marketplaceStoreName?: string;
  onOpenMarketplace?: () => void;
  crmPipelines: Array<{ id: string; name: string; isDefault?: boolean; stages: Array<{ id: string; name: string; order: number }> }>;
  landingPages: Array<{ id: string; name: string; slug: string; status: string; published_url?: string; custom_domain?: string }>;
  abTests: any[];
  crmTags: Array<{ id: string; name: string; color?: string }>;
  isLoadingTags: boolean;
  tagMode: 'existing' | 'new';
  setTagMode: (mode: 'existing' | 'new') => void;
}

const NodePropertiesPanel: React.FC<NodePropertiesPanelProps> = ({
  node,
  allNodes,
  onUpdate,
  onDelete,
  onDuplicate,
  onClose,
  crmEnabled,
  crmApiKey,
  wabaId,
  whatsappToken,
  isCrmConnected,
  isMarketplaceConnected,
  marketplaceStoreName,
  onOpenMarketplace,
  crmPipelines,
  landingPages,
  abTests,
  crmTags,
  isLoadingTags,
  tagMode,
  setTagMode,
}) => {
  const { t } = useTranslation();
  const [config, setConfig] = useState(node.data.config || {});
  const [whatsappTemplates, setWhatsappTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [crmCustomFields, setCrmCustomFields] = useState<Array<{ key: string; name: string; type: string }>>([]);
  // Pré-visualização do e-mail: mostra como o texto (inclusive o que a IA
  // escreveu) vai chegar na caixa de entrada, sem precisar disparar o fluxo.
  const [showEmailPreview, setShowEmailPreview] = useState(false);

  useEffect(() => {
    setConfig(node.data.config || {});
  }, [node.id, node.data.config]);

  // Fetch WhatsApp templates when crm_whatsapp node is selected
  const isWhatsAppNode = node.data.type === 'crm_whatsapp' || node.data.type === 'send_whatsapp';
  const hasCrmCredentials = crmEnabled && !!crmApiKey;

  // Meta Lead Ads: status das contas Facebook + páginas inscritas no leadgen
  const isMetaLeadAdNode = node.data.type === 'meta_lead_ad';
  const [leadAdsAccounts, setLeadAdsAccounts] = useState<Array<{ id: string; username: string; leadAdsEnabled: boolean; pages: Array<{ id: string; name: string }> }>>([]);
  const [loadingLeadAds, setLoadingLeadAds] = useState(false);
  useEffect(() => {
    if (!isMetaLeadAdNode) return;
    setLoadingLeadAds(true);
    apiClient.get('/social/meta-leadads/status')
      .then((res) => setLeadAdsAccounts(res.data?.accounts || []))
      .catch(() => setLeadAdsAccounts([]))
      .finally(() => setLoadingLeadAds(false));
  }, [isMetaLeadAdNode]);

  const loadWhatsAppTemplates = useCallback(() => {
    if (isWhatsAppNode && hasCrmCredentials) {
      setLoadingTemplates(true);
      workflowService.getWhatsAppTemplates(crmApiKey, wabaId, whatsappToken)
        .then(res => {
          if (res.success && res.data) {
            setWhatsappTemplates(res.data);
          }
        })
        .catch(() => {
          setWhatsappTemplates([]);
        })
        .finally(() => setLoadingTemplates(false));
    }
  }, [isWhatsAppNode, hasCrmCredentials, crmApiKey, wabaId, whatsappToken]);

  useEffect(() => {
    loadWhatsAppTemplates();
  }, [loadWhatsAppTemplates]);

  // Fetch CRM custom fields for template variable mapping
  const [loadingCustomFields, setLoadingCustomFields] = useState(false);
  useEffect(() => {
    if (isWhatsAppNode && hasCrmCredentials) {
      setLoadingCustomFields(true);
      workflowService.getCrmCustomFields(crmApiKey!)
        .then(res => {
          if (res.success && res.data) {
            setCrmCustomFields(res.data.filter((f: any) => f.type !== 'FILE'));
          }
        })
        .catch(() => setCrmCustomFields([]))
        .finally(() => setLoadingCustomFields(false));
    } else {
      setCrmCustomFields([]);
    }
  }, [isWhatsAppNode, hasCrmCredentials, crmApiKey]);

  // Available fields for template variable mapping
  const templateFieldOptions = useMemo(() => {
    const baseFields = [
      { value: '{{lead.name}}', label: t('workflow.config.fieldName', 'Nome completo') },
      { value: '{{lead.firstName}}', label: t('workflow.config.fieldFirstName', 'Primeiro nome') },
      { value: '{{lead.email}}', label: t('workflow.config.fieldEmail', 'Email') },
      { value: '{{lead.phone}}', label: t('workflow.config.fieldPhone', 'Telefone') },
      { value: '{{lead.company}}', label: t('workflow.config.fieldCompany', 'Empresa') },
      { value: '{{lead.position}}', label: t('workflow.config.fieldPosition', 'Cargo') },
    ];
    const customOptions = crmCustomFields.map(f => ({
      value: `{{lead.customFields.${f.key}}}`,
      label: `📋 ${f.name}`,
    }));
    return { baseFields, customOptions };
  }, [crmCustomFields, t]);

  const handleConfigChange = (key: string, value: any) => {
    const newConfig = { ...config, [key]: value };
    // Ao escolher a tag na tela, descarta os campos legados que ficam no nó quando o
    // fluxo é duplicado (config.tags / config.tag) — eles venciam a escolha do usuário
    // e o lead entrava com a tag do fluxo de origem.
    if (key === 'tagName') {
      delete newConfig.tags;
      delete newConfig.tag;
    }
    setConfig(newConfig);
    onUpdate({ config: newConfig, isConfigured: true });
  };

  const renderConfigFields = () => {
    const { type } = node.data;

    switch (type) {
      case 'meta_lead_ad':
        return (
          <>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                {t('workflow.config.metaLeadAdInfo', 'Dispara quando alguém envia um formulário de Lead Ads do Facebook/Instagram. Os dados do lead ficam disponíveis nas variáveis abaixo.')}
              </p>
              <p className="text-xs text-blue-500 dark:text-blue-300 mt-2 font-mono">
                {'{{lead.name}} · {{lead.email}} · {{lead.phone}} · {{lead.company}}'}
              </p>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.metaLeadAdPages', 'Páginas conectadas (Lead Ads)')}
              </label>
              {loadingLeadAds ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('common.loading', 'Carregando...')}
                </div>
              ) : leadAdsAccounts.length === 0 ? (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                  <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4" />
                    {t('workflow.config.metaLeadAdNoAccount', 'Nenhuma conta Facebook conectada. Conecte em Redes Sociais para receber os leads.')}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {leadAdsAccounts.map((acc) => (
                    <div key={acc.id} className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{acc.username}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${acc.leadAdsEnabled ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'}`}>
                          {acc.leadAdsEnabled ? t('workflow.config.metaLeadAdActive', 'Lead Ads ativo') : t('workflow.config.metaLeadAdInactive', 'Inativo')}
                        </span>
                      </div>
                      {acc.pages.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {acc.pages.map((p) => p.name).join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mt-3">
              <p className="text-sm text-pink-600 dark:text-pink-400">
                {t('workflow.config.metaLeadAdCrmHint', 'Para enviar o lead ao CRM, conecte este gatilho a um nó "Criar Lead" e ative a integração CRM do fluxo (botão CRM no topo).')}
              </p>
            </div>
            {!isCrmConnected && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'landing_page_submit':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.landingPage.select', 'Select Landing Page')}
              </label>
              <Select
                value={config.landingPageId || ''}
                onChange={(e) => handleConfigChange('landingPageId', e.target.value)}
                options={[
                  { value: '', label: t('workflow.landingPage.placeholder', 'Select a LP...') },
                  ...landingPages.map((lp) => ({
                    value: lp.id,
                    label: `${lp.name}${lp.status === 'published' ? '' : ` (${lp.status})`}`,
                  })),
                ]}
              />
              {landingPages.length === 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                  {t('workflow.landingPage.noPages', 'No landing pages found. Create one first.')}
                </p>
              )}
              {config.landingPageId && (() => {
                const selectedLp = landingPages.find(lp => lp.id === config.landingPageId);
                if (!selectedLp) return null;
                const lpUrl = selectedLp.custom_domain || selectedLp.published_url;
                if (!lpUrl) return (
                  <p className="text-xs text-gray-400 mt-1.5">
                    {t('workflow.landingPage.notDeployed', 'This LP has not been published yet.')}
                  </p>
                );
                return (
                  <a
                    href={lpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    {selectedLp.name}
                  </a>
                );
              })()}
            </div>
            <div className="flex items-center gap-2 mt-3">
              <input
                type="checkbox"
                id="captureUtm"
                checked={config.captureUtm || false}
                onChange={(e) => handleConfigChange('captureUtm', e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="captureUtm" className="text-sm text-gray-600 dark:text-gray-400">
                {t('workflow.landingPage.captureUtm', 'Capture UTM parameters')}
              </label>
            </div>
          </>
        );

      case 'crm_whatsapp':
      case 'send_whatsapp':
        return (
          <>
            {/* WhatsApp Mode Selector */}
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.config.whatsappMode', 'Send mode')}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleConfigChange('whatsappMode', 'template')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.whatsappMode !== 'freetext'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.whatsappTemplate', 'Template')}
                  </button>
                  <button
                    onClick={() => handleConfigChange('whatsappMode', 'freetext')}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.whatsappMode === 'freetext'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.whatsappFreetext', 'Free text')}
                  </button>
                </div>
              </div>
            </div>

            {/* Template Mode (default - WhatsApp Business) */}
            {config.whatsappMode !== 'freetext' && (
              <>
                {/* Template Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.selectTemplate', 'Select Template')}
                  </label>
                  {loadingTemplates ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {t('workflow.config.loadingTemplates', 'Loading templates...')}
                    </div>
                  ) : whatsappTemplates.length > 0 ? (
                    <>
                      <Select
                        value={config.templateName || ''}
                        onChange={(e) => {
                          const selected = whatsappTemplates.find((tpl: any) => tpl.name === e.target.value);
                          const newConfig: Record<string, any> = {
                            ...config,
                            templateName: e.target.value,
                            templateLanguage: selected?.language || 'pt_BR',
                            templateCategory: selected?.category || '',
                            templateComponents: selected?.components || [],
                            templateParameters: { header: [], body: [], buttons: [] },
                          };
                          // Detect if template has image header
                          newConfig.templateHasImageHeader = false;
                          if (selected?.components) {
                            for (const comp of selected.components) {
                              if (comp.type === 'HEADER' && comp.format === 'IMAGE') {
                                newConfig.templateHasImageHeader = true;
                                newConfig.templateParameters.header = []; // No text params for image headers
                              } else if (comp.type === 'HEADER' && comp.format === 'VIDEO') {
                                newConfig.templateHasImageHeader = false; // Video not supported yet
                                newConfig.templateParameters.header = [];
                              } else if (comp.type === 'HEADER' && comp.text) {
                                const headerParams = (comp.text.match(/\{\{(\d+)\}\}/g) || []);
                                newConfig.templateParameters.header = headerParams.map(() => '');
                              }
                              if (comp.type === 'BODY' && comp.text) {
                                const bodyParams = (comp.text.match(/\{\{(\d+)\}\}/g) || []);
                                newConfig.templateParameters.body = bodyParams.map(() => '');
                              }
                              if (comp.type === 'BUTTONS' && comp.buttons) {
                                const btnParams = comp.buttons.filter((b: any) => b.type === 'URL' && b.url?.includes('{{'));
                                newConfig.templateParameters.buttons = btnParams.map(() => '');
                              }
                            }
                          }
                          setConfig(newConfig);
                          onUpdate({ config: newConfig, isConfigured: !!e.target.value });
                        }}
                        options={[
                          { value: '', label: t('workflow.config.chooseTemplate', '-- Select a template --') },
                          ...whatsappTemplates.map((tpl: any) => ({
                            value: tpl.name,
                            label: `${tpl.name} (${tpl.language || 'pt_BR'}) - ${tpl.category || 'MARKETING'}`
                          }))
                        ]}
                      />
                      <button
                        onClick={loadWhatsAppTemplates}
                        className="mt-2 text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" />
                        {t('workflow.config.reloadTemplates', 'Reload templates')}
                      </button>
                    </>
                  ) : (
                    <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                        {crmEnabled
                          ? t('workflow.config.noTemplatesFound', 'No templates found. Create approved templates in WhatsApp Business Manager.')
                          : t('workflow.config.connectCrmForTemplates', 'Connect CRM to load your WhatsApp Business templates.')
                        }
                      </p>
                      {crmEnabled && (
                        <button
                          onClick={loadWhatsAppTemplates}
                          className="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" />
                          {t('workflow.config.retryLoadTemplates', 'Try again')}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Template Preview */}
                {config.templateName && config.templateComponents && (
                  <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase mb-2">
                      {t('workflow.config.templatePreview', 'Template Preview')}
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border border-gray-100 dark:border-gray-700">
                      {config.templateComponents.map((comp: any, idx: number) => {
                        const replaceVarsInPreview = (text: string, section: 'header' | 'body') => {
                          if (!text) return text;
                          const allFields = [...templateFieldOptions.baseFields, ...templateFieldOptions.customOptions];
                          return text.replace(/\{\{(\d+)\}\}/g, (_match: string, num: string) => {
                            const paramIdx = parseInt(num) - 1;
                            const paramValue = config.templateParameters?.[section]?.[paramIdx];
                            if (paramValue) {
                              const fieldLabel = allFields.find((f) => f.value === paramValue)?.label;
                              return `[${fieldLabel || paramValue}]`;
                            }
                            return `{{${num}}}`;
                          });
                        };
                        if (comp.type === 'HEADER' && comp.text) {
                          return <p key={idx} className="font-semibold text-sm text-gray-900 dark:text-white mb-1">{replaceVarsInPreview(comp.text, 'header')}</p>;
                        }
                        if (comp.type === 'HEADER' && comp.format === 'IMAGE') {
                          return config.headerImageUrl ? (
                            <img key={idx} src={config.headerImageUrl} alt="Header" className="rounded h-24 w-full object-cover mb-2" />
                          ) : (
                            <div key={idx} className="bg-gray-200 dark:bg-gray-700 rounded h-20 flex items-center justify-center text-xs text-gray-500 mb-2">[Imagem não definida]</div>
                          );
                        }
                        if (comp.type === 'BODY' && comp.text) {
                          return <p key={idx} className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{replaceVarsInPreview(comp.text, 'body')}</p>;
                        }
                        if (comp.type === 'FOOTER' && comp.text) {
                          return <p key={idx} className="text-xs text-gray-400 mt-2">{comp.text}</p>;
                        }
                        if (comp.type === 'BUTTONS' && comp.buttons) {
                          return (
                            <div key={idx} className="mt-2 space-y-1">
                              {comp.buttons.map((btn: any, bIdx: number) => (
                                <div key={bIdx} className="text-center py-1.5 text-xs font-medium text-blue-500 border-t border-gray-100 dark:border-gray-700">
                                  {btn.text}
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                )}

                {/* Template Parameters */}
                {config.templateName && config.templateParameters && (
                  <>
                    {/* Image Header Upload */}
                    {config.templateHasImageHeader && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Imagem do Cabeçalho
                        </label>
                        {config.headerImageUrl ? (
                          <div className="relative">
                            <img src={config.headerImageUrl} alt="Header" className="rounded-lg h-32 w-full object-cover border border-gray-200 dark:border-gray-700" />
                            <button
                              type="button"
                              onClick={() => handleConfigChange('headerImageUrl', '')}
                              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              id="whatsapp-header-image"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                if (file.size > 5 * 1024 * 1024) {
                                  toast.error('A imagem deve ter no máximo 5MB');
                                  return;
                                }
                                try {
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  const res = await apiClient.post('/media/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                                  if (res.data.success && res.data.data?.url) {
                                    handleConfigChange('headerImageUrl', res.data.data.url);
                                    toast.success('Imagem enviada com sucesso!');
                                  }
                                } catch (err) {
                                  toast.error('Erro ao enviar imagem');
                                }
                              }}
                            />
                            <label
                              htmlFor="whatsapp-header-image"
                              className="flex flex-col items-center justify-center h-28 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-primary-500 transition-colors"
                            >
                              <Upload className="w-6 h-6 text-gray-400 mb-1" />
                              <span className="text-xs text-gray-500">Clique para enviar imagem (max 5MB)</span>
                              <span className="text-xs text-gray-400 mt-0.5">JPG, PNG ou WEBP</span>
                            </label>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Esta imagem será enviada no cabeçalho do template do WhatsApp.
                        </p>
                      </div>
                    )}

                    {/* Text Header Parameters */}
                    {!config.templateHasImageHeader && config.templateParameters.header?.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('workflow.config.headerParams', 'Header Parameters')}
                        </label>
                        {config.templateParameters.header.map((_: any, idx: number) => {
                          const currentValue = config.templateParameters.header[idx] || '';
                          const allOptions = [...templateFieldOptions.baseFields, ...templateFieldOptions.customOptions];
                          const isUnknownSavedValue = currentValue && !allOptions.find(f => f.value === currentValue);
                          return (
                            <div key={`header-${idx}`} className="mb-2 flex items-center gap-2">
                              <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded whitespace-nowrap">{`{{${idx + 1}}}`}</span>
                              <span className="text-gray-400">→</span>
                              <select
                                value={currentValue}
                                onChange={(e) => {
                                  const newParams = { ...config.templateParameters };
                                  newParams.header = [...newParams.header];
                                  newParams.header[idx] = e.target.value;
                                  handleConfigChange('templateParameters', newParams);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              >
                                <option value="">{t('workflow.config.selectField', 'Selecionar campo...')}</option>
                                {templateFieldOptions.baseFields.map((f) => (
                                  <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                                {templateFieldOptions.customOptions.length > 0 && (
                                  <optgroup label={t('workflow.config.customFields', 'Campos personalizados')}>
                                    {templateFieldOptions.customOptions.map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {isUnknownSavedValue && (
                                  <option value={currentValue}>{currentValue}</option>
                                )}
                              </select>
                              {loadingCustomFields && <Loader2 className="w-3 h-3 animate-spin text-gray-400 flex-shrink-0" />}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {config.templateParameters.body?.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('workflow.config.bodyParams', 'Body Parameters')}
                        </label>
                        {config.templateParameters.body.map((_: any, idx: number) => {
                          const currentValue = config.templateParameters.body[idx] || '';
                          const allOptions = [...templateFieldOptions.baseFields, ...templateFieldOptions.customOptions];
                          const isUnknownSavedValue = currentValue && !allOptions.find(f => f.value === currentValue);
                          return (
                            <div key={`body-${idx}`} className="mb-2 flex items-center gap-2">
                              <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded whitespace-nowrap">{`{{${idx + 1}}}`}</span>
                              <span className="text-gray-400">→</span>
                              <select
                                value={currentValue}
                                onChange={(e) => {
                                  const newParams = { ...config.templateParameters };
                                  newParams.body = [...newParams.body];
                                  newParams.body[idx] = e.target.value;
                                  handleConfigChange('templateParameters', newParams);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                              >
                                <option value="">{t('workflow.config.selectField', 'Selecionar campo...')}</option>
                                {templateFieldOptions.baseFields.map((f) => (
                                  <option key={f.value} value={f.value}>{f.label}</option>
                                ))}
                                {templateFieldOptions.customOptions.length > 0 && (
                                  <optgroup label={t('workflow.config.customFields', 'Campos personalizados')}>
                                    {templateFieldOptions.customOptions.map((f) => (
                                      <option key={f.value} value={f.value}>{f.label}</option>
                                    ))}
                                  </optgroup>
                                )}
                                {isUnknownSavedValue && (
                                  <option value={currentValue}>{currentValue}</option>
                                )}
                              </select>
                              {loadingCustomFields && <Loader2 className="w-3 h-3 animate-spin text-gray-400 flex-shrink-0" />}
                            </div>
                          );
                        })}
                        <p className="text-xs text-gray-500 mt-1">
                          {t('workflow.config.variablesMappingHint', 'As variáveis serão substituídas pelos dados do lead no momento do envio.')}
                        </p>
                      </div>
                    )}

                    {config.templateParameters.buttons?.length > 0 && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t('workflow.config.buttonParams', 'Button Parameters')}
                        </label>
                        {config.templateParameters.buttons.map((_: any, idx: number) => (
                          <div key={`btn-${idx}`} className="mb-2">
                            <Input
                              value={config.templateParameters.buttons[idx] || ''}
                              onChange={(e) => {
                                const newParams = { ...config.templateParameters };
                                newParams.buttons = [...newParams.buttons];
                                newParams.buttons[idx] = e.target.value;
                                handleConfigChange('templateParameters', newParams);
                              }}
                              placeholder={`{{${idx + 1}}} - ex: codigo-do-cupom`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Template Language Override */}
                {config.templateName && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('workflow.config.templateLanguage', 'Template Language')}
                    </label>
                    <Select
                      value={config.templateLanguage || 'pt_BR'}
                      onChange={(e) => handleConfigChange('templateLanguage', e.target.value)}
                      options={[
                        { value: 'pt_BR', label: 'Portugues (Brasil)' },
                        { value: 'en_US', label: 'English (US)' },
                        { value: 'es', label: 'Espanol' },
                      ]}
                    />
                  </div>
                )}

                {/* Filter by status - only approved templates */}
                {whatsappTemplates.length > 0 && (
                  <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded-lg mb-4">
                    <p className="text-xs text-green-700 dark:text-green-400">
                      {t('workflow.config.approvedTemplatesCount', `${whatsappTemplates.length} approved template(s) found`)}
                    </p>
                  </div>
                )}

                {/* Info about WhatsApp Business templates */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    <strong>{t('workflow.config.whatsappBusinessInfo', 'WhatsApp Business API')}:</strong>{' '}
                    {t('workflow.config.whatsappBusinessDesc', 'Templates are messages pre-approved by Meta. They can be sent at any time, without depending on the 24h window.')}
                  </p>
                </div>
              </>
            )}

            {/* Free Text Mode (24h window) */}
            {config.whatsappMode === 'freetext' && (
              <>
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mb-4">
                  <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                    <span>{t('workflow.config.freetextWarning', 'Free text messages can only be sent within the 24h window after the contact\'s last message.')}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.message', 'Message')}
                  </label>
                  <Textarea
                    value={config.message || ''}
                    onChange={(e) => handleConfigChange('message', e.target.value)}
                    placeholder={t('workflow.config.messagePlaceholder', 'Hello {{lead.name}}, how are you?')}
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t('workflow.config.variables', 'Use {{lead.name}}, {{lead.email}}, {{lead.phone}} to personalize')}
                  </p>
                </div>
              </>
            )}

            {/* Schedule option (both modes) */}
            <div className="p-3 bg-gray-50 dark:bg-gray-900/30 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.config.scheduleDelivery', 'Schedule delivery')}
                </span>
                <button
                  onClick={() => handleConfigChange('sendNow', config.sendNow === false ? true : false)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    config.sendNow === false
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {config.sendNow === false ? t('workflow.config.scheduled', 'Scheduled') : t('workflow.config.immediate', 'Immediate')}
                </button>
              </div>
            </div>

            {config.sendNow === false && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.scheduleIn', 'Schedule in')}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={config.scheduleAmount || 1}
                    onChange={(e) => handleConfigChange('scheduleAmount', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.unit', 'Unit')}
                  </label>
                  <Select
                    value={config.scheduleUnit || 'minutes'}
                    onChange={(e) => handleConfigChange('scheduleUnit', e.target.value)}
                    options={[
                      { value: 'minutes', label: t('workflow.config.minutes', 'Minutes') },
                      { value: 'hours', label: t('workflow.config.hours', 'Hours') },
                      { value: 'days', label: t('workflow.config.days', 'Days') },
                    ]}
                  />
                </div>
              </div>
            )}

            {!crmEnabled && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'crm_email':
      case 'send_email':
        return (
          <>
            {/* Send Now or Schedule */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.config.sendMode', 'Send mode')}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleConfigChange('sendNow', true)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.sendNow !== false
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.sendNow', 'Send now')}
                  </button>
                  <button
                    onClick={() => handleConfigChange('sendNow', false)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.sendNow === false
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.schedule', 'Schedule')}
                  </button>
                </div>
              </div>
            </div>

            {config.sendNow === false && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.scheduleIn', 'Schedule in')}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={config.scheduleAmount || 1}
                    onChange={(e) => handleConfigChange('scheduleAmount', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.unit', 'Unit')}
                  </label>
                  <Select
                    value={config.scheduleUnit || 'minutes'}
                    onChange={(e) => handleConfigChange('scheduleUnit', e.target.value)}
                    options={[
                      { value: 'minutes', label: t('workflow.config.minutes', 'Minutes') },
                      { value: 'hours', label: t('workflow.config.hours', 'Hours') },
                      { value: 'days', label: t('workflow.config.days', 'Days') },
                    ]}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.subject', 'Subject')}
              </label>
              <Input
                value={config.subject || ''}
                onChange={(e) => handleConfigChange('subject', e.target.value)}
                placeholder={t('workflow.config.subjectPlaceholder', 'Welcome, {{lead.name}}!')}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.config.body', 'Email Body')}
                </label>
                <button
                  type="button"
                  data-testid="toggle-email-preview"
                  onClick={() => setShowEmailPreview((v) => !v)}
                  className="flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Eye className="w-3.5 h-3.5" />
                  {showEmailPreview
                    ? t('workflow.config.hidePreview', 'Ocultar pré-visualização')
                    : t('workflow.config.showPreview', 'Ver como fica o e-mail')}
                </button>
              </div>
              <Textarea
                value={config.body || ''}
                onChange={(e) => handleConfigChange('body', e.target.value)}
                placeholder={t('workflow.config.bodyPlaceholder', 'Email content...')}
                rows={6}
              />
              {showEmailPreview && (
                <div
                  data-testid="email-preview"
                  className="mt-3 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <div className="px-3 py-2 bg-gray-100 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t('workflow.config.previewSubject', 'Assunto')}
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                      {config.subject || t('workflow.config.noSubject', '(sem assunto)')}
                    </p>
                  </div>
                  <div className="p-4 bg-white dark:bg-gray-950 max-h-72 overflow-y-auto">
                    {config.body ? (
                      <div
                        className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words"
                        // Só o próprio dono vê/edita este conteúdo, mas ainda assim
                        // removemos script/handlers antes de renderizar o HTML.
                        dangerouslySetInnerHTML={{
                          __html: String(config.body)
                            .replace(/<script[\s\S]*?<\/script>/gi, '')
                            .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
                            .replace(/javascript:/gi, ''),
                        }}
                      />
                    ) : (
                      <p className="text-sm text-gray-400">
                        {t('workflow.config.noBody', 'Este e-mail ainda não tem texto.')}
                      </p>
                    )}
                  </div>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/40 border-t border-gray-200 dark:border-gray-700">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {t('workflow.config.previewHint', 'As marcações {{lead.name}} são trocadas pelos dados do lead no envio.')}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {!crmEnabled && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'crm_sms':
      case 'send_sms':
        return (
          <>
            {/* Send Now or Schedule */}
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-800 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.config.sendMode', 'Send mode')}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleConfigChange('sendNow', true)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.sendNow !== false
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.sendNow', 'Send now')}
                  </button>
                  <button
                    onClick={() => handleConfigChange('sendNow', false)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.sendNow === false
                        ? 'bg-teal-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.schedule', 'Schedule')}
                  </button>
                </div>
              </div>
            </div>

            {config.sendNow === false && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.scheduleIn', 'Schedule in')}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={config.scheduleAmount || 1}
                    onChange={(e) => handleConfigChange('scheduleAmount', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.unit', 'Unit')}
                  </label>
                  <Select
                    value={config.scheduleUnit || 'minutes'}
                    onChange={(e) => handleConfigChange('scheduleUnit', e.target.value)}
                    options={[
                      { value: 'minutes', label: t('workflow.config.minutes', 'Minutes') },
                      { value: 'hours', label: t('workflow.config.hours', 'Hours') },
                      { value: 'days', label: t('workflow.config.days', 'Days') },
                    ]}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.smsMessage', 'SMS Message')}
              </label>
              <Textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder={t('workflow.config.smsPlaceholder', 'Hello {{lead.name}}, your purchase has been confirmed!')}
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('workflow.config.smsLimit', 'Max 160 characters per SMS. Use {{lead.name}}, {{lead.phone}} to personalize')}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {config.message?.length || 0}/160
              </p>
            </div>

            {!crmEnabled && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'crm_call':
        return (
          <>
            {/* Call Now or Schedule */}
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 mb-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.config.callMode', 'Call mode')}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleConfigChange('scheduleCall', false)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.scheduleCall !== true
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.callNow', 'Call now')}
                  </button>
                  <button
                    onClick={() => handleConfigChange('scheduleCall', true)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      config.scheduleCall === true
                        ? 'bg-purple-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {t('workflow.config.scheduleCall', 'Schedule call')}
                  </button>
                </div>
              </div>
            </div>

            {config.scheduleCall === true && (
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.scheduleIn', 'Schedule in')}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    value={config.scheduleAmount || 1}
                    onChange={(e) => handleConfigChange('scheduleAmount', parseInt(e.target.value))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.config.unit', 'Unit')}
                  </label>
                  <Select
                    value={config.scheduleUnit || 'hours'}
                    onChange={(e) => handleConfigChange('scheduleUnit', e.target.value)}
                    options={[
                      { value: 'minutes', label: t('workflow.config.minutes', 'Minutes') },
                      { value: 'hours', label: t('workflow.config.hours', 'Hours') },
                      { value: 'days', label: t('workflow.config.days', 'Days') },
                    ]}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.callNotes', 'Call notes')}
              </label>
              <Textarea
                value={config.callNotes || ''}
                onChange={(e) => handleConfigChange('callNotes', e.target.value)}
                placeholder={t('workflow.config.callNotesPlaceholder', 'Important information for the call...')}
                rows={3}
              />
            </div>

            {!crmEnabled && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'crm_bot_meeting':
        return (
          <>
            <div className="p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800 mb-4">
              <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400">
                <Bot className="w-5 h-5" />
                <span className="text-sm font-medium">{t('workflow.config.botMeetingInfo', 'The CRM bot will interact with the lead via WhatsApp to schedule the meeting')}</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.meetingType', 'Meeting type')}
              </label>
              <Select
                value={config.meetingType || 'online'}
                onChange={(e) => handleConfigChange('meetingType', e.target.value)}
                options={[
                  { value: 'online', label: t('workflow.config.online', 'Online (Google Meet/Zoom)') },
                  { value: 'presential', label: t('workflow.config.presential', 'In-person') },
                  { value: 'phone', label: t('workflow.config.phoneCall', 'Phone call') },
                ]}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.meetingDuration', 'Default duration')}
              </label>
              <Select
                value={config.meetingDuration || '30'}
                onChange={(e) => handleConfigChange('meetingDuration', e.target.value)}
                options={[
                  { value: '15', label: '15 ' + t('workflow.config.minutes', 'minutes') },
                  { value: '30', label: '30 ' + t('workflow.config.minutes', 'minutes') },
                  { value: '45', label: '45 ' + t('workflow.config.minutes', 'minutes') },
                  { value: '60', label: '1 ' + t('workflow.config.hour', 'hour') },
                ]}
              />
            </div>

            {!crmEnabled && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'create_ticket':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.ticketTitle', 'Ticket Title')}
              </label>
              <Input
                value={config.ticketTitle || ''}
                onChange={(e) => handleConfigChange('ticketTitle', e.target.value)}
                placeholder={t('workflow.config.ticketTitlePlaceholder', 'New lead: {{lead.name}}')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.ticketDescription', 'Description')}
              </label>
              <Textarea
                value={config.ticketDescription || ''}
                onChange={(e) => handleConfigChange('ticketDescription', e.target.value)}
                placeholder={t('workflow.config.ticketDescriptionPlaceholder', 'Ticket details...')}
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.ticketPriority', 'Priority')}
              </label>
              <Select
                value={config.ticketPriority || 'medium'}
                onChange={(e) => handleConfigChange('ticketPriority', e.target.value)}
                options={[
                  { value: 'low', label: t('workflow.config.low', 'Low') },
                  { value: 'medium', label: t('workflow.config.medium', 'Medium') },
                  { value: 'high', label: t('workflow.config.high', 'High') },
                  { value: 'urgent', label: t('workflow.config.urgent', 'Urgent') },
                ]}
              />
            </div>

            {!crmEnabled && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'create_lead':
        const selectedPipelineForCreate = crmPipelines.find(p => p.id === config.pipelineId);
        return (
          <>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <p className="text-sm text-pink-600 dark:text-pink-400">
                {t('workflow.config.createLeadInfo', 'Lead data will be automatically sent to the CRM')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.pipeline', 'Pipeline')}
              </label>
              <Select
                value={config.pipelineId || ''}
                onChange={(e) => {
                  handleConfigChange('pipelineId', e.target.value);
                  handleConfigChange('stageId', '');
                }}
                options={[
                  { value: '', label: t('workflow.config.defaultPipeline', 'Default pipeline') },
                  ...crmPipelines.map(p => ({ value: p.id, label: p.name }))
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.initialStage', 'Initial Stage')}
              </label>
              <Select
                value={config.stageId || ''}
                onChange={(e) => handleConfigChange('stageId', e.target.value)}
                options={[
                  { value: '', label: t('workflow.config.firstStage', 'First stage') },
                  ...(selectedPipelineForCreate?.stages || crmPipelines[0]?.stages || [])
                    .sort((a, b) => a.order - b.order)
                    .map(s => ({ value: s.id, label: s.name }))
                ]}
              />
            </div>
            {!isCrmConnected && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'change_stage':
        const allStagesForMove = crmPipelines.flatMap(p =>
          (p.stages || []).map(s => ({ ...s, pipelineName: p.name }))
        ).sort((a, b) => a.order - b.order);
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.moveToStage', 'Move to Stage')}
              </label>
              <Select
                value={config.targetStageId || config.stageId || ''}
                onChange={(e) => handleConfigChange('targetStageId', e.target.value)}
                options={[
                  { value: '', label: t('common.select', 'Select') },
                  ...allStagesForMove.map(s => ({
                    value: s.id,
                    label: `${s.name} (${s.pipelineName})`
                  }))
                ]}
              />
            </div>
            {!isCrmConnected && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'if_else':
      case 'filter':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.fieldToEvaluate', 'Field to Evaluate')}
              </label>
              <Select
                value={config.field || ''}
                onChange={(e) => handleConfigChange('field', e.target.value)}
                options={(() => {
                  const base = [
                    { value: '', label: t('workflow.config.selectPlaceholder', 'Select...') },
                    { value: 'lead.email', label: t('workflow.config.leadEmail', 'Lead Email') },
                    { value: 'lead.phone', label: t('workflow.config.leadPhone', 'Lead Phone') },
                    { value: 'lead.source', label: t('workflow.config.source', 'Source') },
                    { value: 'lead.score', label: t('workflow.config.leadScore', 'Lead Score') },
                    { value: 'utm.source', label: t('workflow.config.utmSource', 'UTM Source') },
                    { value: 'response', label: t('workflow.config.whatsappResponse', 'WhatsApp Response') },
                  ];
                  // Se o campo já está configurado como um campo do formulário (form.xxx)
                  // — ex.: gerado pela IA — mostramos ele na lista para não sumir/zerar.
                  const cur = config.field;
                  if (cur && !base.some(o => o.value === cur)) {
                    const label = cur.startsWith('form.')
                      ? `${t('workflow.config.formField', 'Form field')}: ${cur.slice(5)}`
                      : cur;
                    base.push({ value: cur, label });
                  }
                  return base;
                })()}
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('workflow.config.formFieldHint', 'To evaluate a Landing Page form answer, use form.field_name (e.g., form.profissao).')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.operator', 'Operator')}
              </label>
              <Select
                value={config.operator || ''}
                onChange={(e) => handleConfigChange('operator', e.target.value)}
                options={[
                  { value: 'equals', label: t('workflow.config.equals', 'Equals') },
                  { value: 'not_equals', label: t('workflow.config.notEquals', 'Not equals') },
                  { value: 'contains', label: t('workflow.config.contains', 'Contains') },
                  { value: 'not_contains', label: t('workflow.config.notContains', 'Does not contain') },
                  { value: 'greater_than', label: t('workflow.config.greaterThan', 'Greater than') },
                  { value: 'less_than', label: t('workflow.config.lessThan', 'Less than') },
                  { value: 'is_empty', label: t('workflow.config.isEmpty', 'Is empty') },
                  { value: 'is_not_empty', label: t('workflow.config.isNotEmpty', 'Is not empty') },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.value', 'Value')}
              </label>
              <Input
                value={config.value || ''}
                onChange={(e) => handleConfigChange('value', e.target.value)}
                placeholder={t('workflow.config.valuePlaceholder', 'Value to compare')}
              />
            </div>
          </>
        );

      case 'wait':
        return (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.quantity', 'Quantity')}
              </label>
              <Input
                type="number"
                min="1"
                value={config.amount || 1}
                onChange={(e) => handleConfigChange('amount', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.unit', 'Unit')}
              </label>
              <Select
                value={config.unit || 'minutes'}
                onChange={(e) => handleConfigChange('unit', e.target.value)}
                options={[
                  { value: 'minutes', label: t('workflow.config.minutes', 'Minutes') },
                  { value: 'hours', label: t('workflow.config.hours', 'Hours') },
                  { value: 'days', label: t('workflow.config.days', 'Days') },
                ]}
              />
            </div>
          </div>
        );

      case 'wait_response':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.timeout', 'Timeout (hours)')}
              </label>
              <Input
                type="number"
                min="1"
                value={config.timeoutHours || 24}
                onChange={(e) => handleConfigChange('timeoutHours', parseInt(e.target.value))}
              />
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-600 dark:text-blue-400">
                {t('workflow.config.waitResponseInfo', 'The flow waits for the lead\'s response. If there is no response within the defined time, it follows the "Timeout" path.')}
              </p>
            </div>
          </>
        );

      case 'ab_test':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.abTest.selectTest', 'Select A/B Test')}
              </label>
              <Select
                value={config.abTestId || ''}
                onChange={(e) => {
                  const testId = e.target.value;
                  handleConfigChange('abTestId', testId);
                  // Find the test to get its traffic split
                  const selectedTest = abTests.find((test: any) => test.id === testId);
                  if (selectedTest) {
                    const controlVariant = selectedTest.variants?.find((v: any) => v.is_control);
                    handleConfigChange('trafficSplit', controlVariant?.traffic_percentage || 50);
                    handleConfigChange('testName', selectedTest.name);
                  }
                }}
                options={[
                  { value: '', label: t('workflow.abTest.selectTestPlaceholder', 'Select a test...') },
                  ...abTests.map((test: any) => ({
                    value: test.id,
                    label: `${test.name} (${test.status === 'running' ? t('workflow.abTest.statusActive', 'Active') : test.status === 'draft' ? t('workflow.abTest.statusDraft', 'Draft') : test.status})`
                  }))
                ]}
              />
              {abTests.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('workflow.abTest.noTests', 'No A/B tests found. Create a test in the Landing Pages section.')}
                </p>
              )}
            </div>

            {config.abTestId && (
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
                <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-2">
                  <span>{t('workflow.abTest.trafficSplit', 'Traffic Split')}</span>
                </div>
                <div className="flex h-3 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-blue-600 transition-all"
                    style={{ width: `${config.trafficSplit || 50}%` }}
                  />
                  <div
                    className="bg-gradient-to-r from-green-500 to-green-600 transition-all"
                    style={{ width: `${100 - (config.trafficSplit || 50)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">
                    {t('workflow.abTest.variantA', 'Variant A')}: {config.trafficSplit || 50}%
                  </span>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    {t('workflow.abTest.variantB', 'Variant B')}: {100 - (config.trafficSplit || 50)}%
                  </span>
                </div>
              </div>
            )}

            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-2">
                <FlaskConical className="w-4 h-4 text-purple-600 dark:text-purple-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-purple-700 dark:text-purple-300 mb-1">
                    {t('workflow.abTest.howItWorks', 'How it works')}
                  </p>
                  <ul className="text-xs text-purple-600 dark:text-purple-400 list-disc list-inside space-y-1">
                    <li>{t('workflow.abTest.info1', 'Traffic will be split according to the selected test')}</li>
                    <li>{t('workflow.abTest.info2', 'Each lead follows only one path')}</li>
                    <li>{t('workflow.abTest.info3', 'Connect different actions to each output')}</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        );

      case 'add_tag':
        return (
          <>
            {!isCrmConnected && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mb-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}

            {isCrmConnected && (
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setTagMode('existing')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    tagMode === 'existing'
                      ? 'bg-pink-50 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700 text-pink-700 dark:text-pink-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <Tag className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
                  {t('workflow.config.existingTag', 'Existing Tag')}
                </button>
                <button
                  type="button"
                  onClick={() => setTagMode('new')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                    tagMode === 'new'
                      ? 'bg-pink-50 dark:bg-pink-900/30 border-pink-300 dark:border-pink-700 text-pink-700 dark:text-pink-300'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                >
                  <Plus className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
                  {t('workflow.config.newTag', 'New Tag')}
                </button>
              </div>
            )}

            {tagMode === 'existing' && isCrmConnected ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.config.selectTag', 'Select Tag')}
                </label>
                {isLoadingTags ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('workflow.config.loadingTags', 'Loading tags...')}
                  </div>
                ) : crmTags.length > 0 ? (
                  <select
                    value={config.tagName || ''}
                    onChange={(e) => handleConfigChange('tagName', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="">{t('workflow.config.selectTagPlaceholder', 'Select a tag...')}</option>
                    {crmTags.map((tag) => (
                      <option key={tag.id} value={tag.name}>
                        {tag.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {t('workflow.config.noTagsFound', 'No tags found in CRM. Create a new tag.')}
                    </p>
                    <button
                      type="button"
                      onClick={() => setTagMode('new')}
                      className="mt-2 text-sm text-pink-600 dark:text-pink-400 hover:underline font-medium"
                    >
                      {t('workflow.config.createNewTag', 'Create new tag')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.config.tagName', 'Tag Name')}
                </label>
                <Input
                  value={config.tagName || ''}
                  onChange={(e) => handleConfigChange('tagName', e.target.value)}
                  placeholder={t('workflow.config.tagNamePlaceholder', 'E.g.: hot-lead, interested')}
                />
              </div>
            )}
          </>
        );

      case 'create_task':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.taskTitle', 'Task Title')}
              </label>
              <Input
                value={config.taskTitle || ''}
                onChange={(e) => handleConfigChange('taskTitle', e.target.value)}
                placeholder={t('workflow.config.taskTitlePlaceholder', 'Call {{lead.name}}')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.priority', 'Priority')}
              </label>
              <Select
                value={config.priority || 'medium'}
                onChange={(e) => handleConfigChange('priority', e.target.value)}
                options={[
                  { value: 'low', label: t('workflow.config.low', 'Low') },
                  { value: 'medium', label: t('workflow.config.medium', 'Medium') },
                  { value: 'high', label: t('workflow.config.high', 'High') },
                  { value: 'urgent', label: t('workflow.config.urgent', 'Urgent') },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.dueDays', 'Due (days)')}
              </label>
              <Input
                type="number"
                min="1"
                value={config.dueDays || 1}
                onChange={(e) => handleConfigChange('dueDays', parseInt(e.target.value))}
              />
            </div>
          </>
        );

      case 'schedule_call':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.scheduleHours', 'Schedule in (hours)')}
              </label>
              <Input
                type="number"
                min="1"
                value={config.scheduleHours || 2}
                onChange={(e) => handleConfigChange('scheduleHours', parseInt(e.target.value))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.callNotes', 'Call notes')}
              </label>
              <Textarea
                value={config.callNotes || ''}
                onChange={(e) => handleConfigChange('callNotes', e.target.value)}
                placeholder={t('workflow.config.callNotesPlaceholder', 'Important information...')}
                rows={3}
              />
            </div>
          </>
        );

      case 'redirect':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.redirectTo', 'Redirect to')}
              </label>
              <Select
                value={config.targetLpId || ''}
                onChange={(e) => handleConfigChange('targetLpId', e.target.value)}
                options={[
                  { value: '', label: t('workflow.config.selectLPPlaceholder', 'Select a LP...') },
                  { value: 'lp-obrigado', label: t('workflow.config.lpThankYouPage', 'LP - Thank You Page') },
                  { value: 'lp-oferta', label: t('workflow.config.lpSpecialOffer', 'LP - Special Offer') },
                  { value: 'lp-webinar', label: t('workflow.config.lpWebinarRegistration', 'LP - Webinar Registration') },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.externalUrlLabel', 'Or External URL')}
              </label>
              <Input
                value={config.externalUrl || ''}
                onChange={(e) => handleConfigChange('externalUrl', e.target.value)}
                placeholder={t('workflow.config.externalUrlPlaceholder', 'https://...')}
              />
            </div>
          </>
        );

      case 'api_call':
        const headers = config.headers || [{ key: '', value: '' }];
        const queryParams = config.queryParams || [{ key: '', value: '' }];

        const addHeader = () => {
          handleConfigChange('headers', [...headers, { key: '', value: '' }]);
        };

        const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
          const newHeaders = [...headers];
          newHeaders[index][field] = value;
          handleConfigChange('headers', newHeaders);
        };

        const removeHeader = (index: number) => {
          const newHeaders = headers.filter((_: any, i: number) => i !== index);
          handleConfigChange('headers', newHeaders.length > 0 ? newHeaders : [{ key: '', value: '' }]);
        };

        const addQueryParam = () => {
          handleConfigChange('queryParams', [...queryParams, { key: '', value: '' }]);
        };

        const updateQueryParam = (index: number, field: 'key' | 'value', value: string) => {
          const newParams = [...queryParams];
          newParams[index][field] = value;
          handleConfigChange('queryParams', newParams);
        };

        const removeQueryParam = (index: number) => {
          const newParams = queryParams.filter((_: any, i: number) => i !== index);
          handleConfigChange('queryParams', newParams.length > 0 ? newParams : [{ key: '', value: '' }]);
        };

        return (
          <>
            {/* URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.apiCall.url', 'API URL')}
              </label>
              <Input
                value={config.apiUrl || ''}
                onChange={(e) => handleConfigChange('apiUrl', e.target.value)}
                placeholder="https://api.exemplo.com/endpoint"
              />
            </div>

            {/* HTTP Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.apiCall.method', 'HTTP Method')}
              </label>
              <Select
                value={config.method || 'POST'}
                onChange={(e) => handleConfigChange('method', e.target.value)}
                options={[
                  { value: 'GET', label: 'GET' },
                  { value: 'POST', label: 'POST' },
                  { value: 'PUT', label: 'PUT' },
                  { value: 'PATCH', label: 'PATCH' },
                  { value: 'DELETE', label: 'DELETE' },
                ]}
              />
            </div>

            {/* Authentication */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.apiCall.authType', 'Authentication')}
              </label>
              <Select
                value={config.authType || 'none'}
                onChange={(e) => handleConfigChange('authType', e.target.value)}
                options={[
                  { value: 'none', label: t('workflow.apiCall.noAuth', 'None') },
                  { value: 'bearer', label: 'Bearer Token' },
                  { value: 'basic', label: 'Basic Auth' },
                  { value: 'api_key', label: 'API Key' },
                ]}
              />
            </div>

            {config.authType === 'bearer' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Bearer Token
                </label>
                <Input
                  type="password"
                  value={config.bearerToken || ''}
                  onChange={(e) => handleConfigChange('bearerToken', e.target.value)}
                  placeholder="seu-token-aqui"
                />
              </div>
            )}

            {config.authType === 'basic' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.apiCall.username', 'Username')}
                  </label>
                  <Input
                    value={config.basicUser || ''}
                    onChange={(e) => handleConfigChange('basicUser', e.target.value)}
                    placeholder="usuario"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.apiCall.password', 'Password')}
                  </label>
                  <Input
                    type="password"
                    value={config.basicPass || ''}
                    onChange={(e) => handleConfigChange('basicPass', e.target.value)}
                    placeholder="senha"
                  />
                </div>
              </div>
            )}

            {config.authType === 'api_key' && (
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.apiCall.headerName', 'Header Name')}
                  </label>
                  <Input
                    value={config.apiKeyHeader || 'X-API-Key'}
                    onChange={(e) => handleConfigChange('apiKeyHeader', e.target.value)}
                    placeholder="X-API-Key"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    API Key
                  </label>
                  <Input
                    type="password"
                    value={config.apiKeyValue || ''}
                    onChange={(e) => handleConfigChange('apiKeyValue', e.target.value)}
                    placeholder="sua-api-key"
                  />
                </div>
              </div>
            )}

            {/* Headers */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.apiCall.headers', 'Headers')}
                </label>
                <button
                  onClick={addHeader}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> {t('common.add', 'Add')}
                </button>
              </div>
              <div className="space-y-2">
                {headers.map((header: any, index: number) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      value={header.key}
                      onChange={(e) => updateHeader(index, 'key', e.target.value)}
                      placeholder="Content-Type"
                      className="flex-1"
                    />
                    <Input
                      value={header.value}
                      onChange={(e) => updateHeader(index, 'value', e.target.value)}
                      placeholder="application/json"
                      className="flex-1"
                    />
                    <button
                      onClick={() => removeHeader(index)}
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Query Parameters */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('workflow.apiCall.queryParams', 'Query Parameters')}
                </label>
                <button
                  onClick={addQueryParam}
                  className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> {t('common.add', 'Add')}
                </button>
              </div>
              <div className="space-y-2">
                {queryParams.map((param: any, index: number) => (
                  <div key={index} className="flex gap-2 items-center">
                    <Input
                      value={param.key}
                      onChange={(e) => updateQueryParam(index, 'key', e.target.value)}
                      placeholder="page"
                      className="flex-1"
                    />
                    <Input
                      value={param.value}
                      onChange={(e) => updateQueryParam(index, 'value', e.target.value)}
                      placeholder="1"
                      className="flex-1"
                    />
                    <button
                      onClick={() => removeQueryParam(index)}
                      className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Request Body */}
            {['POST', 'PUT', 'PATCH'].includes(config.method || 'POST') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.apiCall.body', 'Request Body (JSON)')}
                </label>
                <textarea
                  value={config.body || '{\n  \n}'}
                  onChange={(e) => handleConfigChange('body', e.target.value)}
                  placeholder={'{\n  "name": "{{lead.name}}",\n  "email": "{{lead.email}}"\n}'}
                  className="w-full h-32 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono text-sm resize-none focus:ring-2 focus:ring-primary-500"
                />

                {/* Variable Helper */}
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-400 mb-2">
                    {t('workflow.apiCall.variablesHelp', 'Available Variables:')}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      '{{lead.name}}',
                      '{{lead.email}}',
                      '{{lead.phone}}',
                      '{{lead.company}}',
                      '{{lead.score}}',
                      '{{lead.stage}}',
                      '{{lead.source}}',
                      '{{lead.createdAt}}',
                      '{{deal.value}}',
                      '{{deal.title}}',
                      '{{user.name}}',
                      '{{user.email}}',
                      '{{timestamp}}',
                    ].map((variable) => (
                      <button
                        key={variable}
                        onClick={() => {
                          const currentBody = config.body || '{\n  \n}';
                          handleConfigChange('body', currentBody.replace(/\n}$/, `\n  "${variable.replace(/[{}]/g, '')}": "${variable}"\n}`));
                        }}
                        className="px-2 py-0.5 text-xs bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 font-mono"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Timeout */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.apiCall.timeout', 'Timeout (seconds)')}
              </label>
              <Input
                type="number"
                value={config.timeout || 30}
                onChange={(e) => handleConfigChange('timeout', parseInt(e.target.value))}
                min={1}
                max={120}
              />
            </div>

            {/* Retry on Error */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="retryOnError"
                checked={config.retryOnError || false}
                onChange={(e) => handleConfigChange('retryOnError', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="retryOnError" className="text-sm text-gray-700 dark:text-gray-300">
                {t('workflow.apiCall.retryOnError', 'Retry on error')}
              </label>
            </div>

            {config.retryOnError && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.apiCall.maxRetries', 'Maximum retries')}
                </label>
                <Select
                  value={config.maxRetries?.toString() || '3'}
                  onChange={(e) => handleConfigChange('maxRetries', parseInt(e.target.value))}
                  options={[
                    { value: '1', label: '1' },
                    { value: '2', label: '2' },
                    { value: '3', label: '3' },
                    { value: '5', label: '5' },
                  ]}
                />
              </div>
            )}
          </>
        );

      case 'notification':
        return (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.notification.title', 'Notification Title')}
              </label>
              <Input
                value={config.title || ''}
                onChange={(e) => handleConfigChange('title', e.target.value)}
                placeholder={t('workflow.notification.titlePlaceholder', 'E.g.: New qualified lead')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.notification.message', 'Message')}
              </label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder={t('workflow.notification.messagePlaceholder', 'Lead {{lead.name}} ({{lead.email}}) needs attention')}
                className="w-full h-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.notification.channel', 'Channel')}
              </label>
              <Select
                value={config.channel || 'in_app'}
                onChange={(e) => handleConfigChange('channel', e.target.value)}
                options={[
                  { value: 'in_app', label: t('workflow.notification.inApp', 'In-app') },
                  { value: 'email', label: t('workflow.notification.email', 'E-mail') },
                  { value: 'both', label: t('workflow.notification.both', 'In-app + Email') },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.notification.priority', 'Priority')}
              </label>
              <Select
                value={config.priority || 'medium'}
                onChange={(e) => handleConfigChange('priority', e.target.value)}
                options={[
                  { value: 'low', label: t('workflow.notification.low', 'Low') },
                  { value: 'medium', label: t('workflow.notification.medium', 'Medium') },
                  { value: 'high', label: t('workflow.notification.high', 'High') },
                  { value: 'urgent', label: t('workflow.notification.urgent', 'Urgent') },
                ]}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.notification.recipients', 'Recipients')}
              </label>
              <Select
                value={config.recipients || 'owner'}
                onChange={(e) => handleConfigChange('recipients', e.target.value)}
                options={[
                  { value: 'owner', label: t('workflow.notification.owner', 'Lead owner') },
                  { value: 'team', label: t('workflow.notification.team', 'Entire team') },
                  { value: 'admins', label: t('workflow.notification.admins', 'Admins only') },
                ]}
              />
            </div>

            {/* Variable Helper */}
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <p className="text-xs font-medium text-yellow-700 dark:text-yellow-400 mb-2">
                {t('workflow.notification.variablesHelp', 'Available Variables:')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {['{{lead.name}}', '{{lead.email}}', '{{lead.phone}}', '{{lead.company}}', '{{lead.source}}', '{{timestamp}}'].map((variable) => (
                  <button
                    key={variable}
                    onClick={() => {
                      const current = config.message || '';
                      handleConfigChange('message', current + variable);
                    }}
                    className="px-2 py-0.5 text-xs bg-white dark:bg-gray-800 text-yellow-600 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-700 rounded hover:bg-yellow-100 dark:hover:bg-yellow-900/30 font-mono"
                  >
                    {variable}
                  </button>
                ))}
              </div>
            </div>
          </>
        );

      // Marketplace action configs (texto simples, pt-BR)
      case 'marketplace_notify_buyer':
      case 'marketplace_update_order':
      case 'marketplace_add_note':
      case 'marketplace_add_tag':
      case 'marketplace_publish_blog_post':
      case 'marketplace_create_ticket': {
        const nodeType = node.data.type;
        const storeBox = (
          isMarketplaceConnected ? (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 mb-4 text-sm text-green-800 dark:text-green-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              {marketplaceStoreName
                ? t('workflow.mkp.connectedTo', 'Loja conectada: {{name}}', { name: marketplaceStoreName })
                : t('workflow.mkp.connected', 'Loja conectada')}
            </div>
          ) : (
            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mb-4">
              <div className="flex items-start gap-2 text-yellow-800 dark:text-yellow-300 text-sm">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{t('workflow.mkp.notConnectedWarning', 'A loja ainda não está conectada. Sem ela este passo não funciona.')}</span>
              </div>
              {onOpenMarketplace && (
                <button type="button" onClick={onOpenMarketplace} className="mt-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:underline" data-testid="panel-connect-store">
                  {t('workflow.mkp.connectNow', 'Conectar a loja agora')}
                </button>
              )}
            </div>
          )
        );
        const vars = (
          <p className="text-xs text-gray-500 mt-1">
            {t('workflow.mkp.variablesAvailable', 'Você pode usar')}: {'{{customer.first_name}}, {{customer.name}}, {{order.number}}, {{order.total}}, {{order.tracking_code}}, {{store.name}}, {{cart.url}}'}
          </p>
        );
        const label = (text: string) => (
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{text}</label>
        );

        if (nodeType === 'marketplace_create_ticket') {
          return (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-300" data-testid="ticket-disabled">
              <div className="flex items-start gap-2">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{t('workflow.mkp.ticketDisabled', 'O marketplace não tem chamados (tickets), então este passo não faz nada. Apague este passo e use "Avisar comprador" ou "Anotar no pedido".')}</span>
              </div>
            </div>
          );
        }

        if (nodeType === 'marketplace_notify_buyer') {
          return (
            <>
              {storeBox}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('workflow.mkp.notifyBuyerDesc', 'Manda um e-mail para quem comprou, com o nome e o visual da sua loja.')}</p>
              <div className="mb-3">
                {label(t('workflow.mkp.cfg_subject', 'Assunto'))}
                <Input value={config.subject || ''} onChange={(e) => handleConfigChange('subject', e.target.value)} placeholder={t('workflow.mkp.subjectPlaceholder', 'Seu pedido {{order.number}} saiu para entrega')} />
              </div>
              <div className="mb-3">
                {label(t('workflow.mkp.cfg_message', 'Mensagem'))}
                <Textarea value={config.message || ''} onChange={(e) => handleConfigChange('message', e.target.value)} placeholder={t('workflow.mkp.messagePlaceholder', 'Oi, {{customer.first_name}}! Seu pedido já está a caminho.')} rows={5} />
                {vars}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  {label(t('workflow.mkp.buttonLabel', 'Texto do botão (opcional)'))}
                  <Input value={config.buttonLabel || ''} onChange={(e) => handleConfigChange('buttonLabel', e.target.value)} placeholder={t('workflow.mkp.buttonLabelPlaceholder', 'Ver meu pedido')} />
                </div>
                <div>
                  {label(t('workflow.mkp.buttonUrl', 'Link do botão (opcional)'))}
                  <Input value={config.buttonUrl || ''} onChange={(e) => handleConfigChange('buttonUrl', e.target.value)} placeholder="{{cart.url}}" />
                </div>
              </div>
            </>
          );
        }

        if (nodeType === 'marketplace_update_order') {
          return (
            <>
              {storeBox}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('workflow.mkp.updateOrderDesc', 'Muda o pedido que disparou o fluxo. O cliente recebe o e-mail da loja quando o pedido é enviado ou entregue.')}</p>
              <div className="mb-3">
                {label(t('workflow.mkp.newStatus', 'Novo status'))}
                <Select
                  value={config.orderStatus || ''}
                  onChange={(e) => handleConfigChange('orderStatus', e.target.value)}
                  options={[
                    { value: '', label: t('workflow.mkp.status.keep', 'Não mudar o status') },
                    { value: 'processing', label: t('workflow.mkp.status.processing', 'Em separação') },
                    { value: 'shipped', label: t('workflow.mkp.status.shipped', 'Enviado') },
                    { value: 'delivered', label: t('workflow.mkp.status.delivered', 'Entregue') },
                    { value: 'cancelled', label: t('workflow.mkp.status.cancelled', 'Cancelado') },
                  ]}
                />
              </div>
              <div className="mb-3">
                {label(t('workflow.mkp.trackingCode', 'Código de rastreio (opcional)'))}
                <Input value={config.trackingCode || ''} onChange={(e) => handleConfigChange('trackingCode', e.target.value)} placeholder="BR123456789BR" />
                <p className="text-xs text-gray-500 mt-1">{t('workflow.mkp.trackingHelp', 'Com código, o pedido vira "Enviado" e o cliente recebe o rastreio.')}</p>
              </div>
              <div>
                {label(t('workflow.mkp.note', 'Anotação no pedido (opcional)'))}
                <Textarea value={config.note || ''} onChange={(e) => handleConfigChange('note', e.target.value)} placeholder={t('workflow.mkp.notePlaceholder', 'Mudado pela automação')} rows={2} />
              </div>
            </>
          );
        }

        if (nodeType === 'marketplace_add_note') {
          return (
            <>
              {storeBox}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('workflow.mkp.addNoteDesc', 'Escreve uma anotação no pedido (só a sua equipe vê).')}</p>
              {label(t('workflow.mkp.noteText', 'Anotação'))}
              <Textarea value={config.note || ''} onChange={(e) => handleConfigChange('note', e.target.value)} placeholder={t('workflow.mkp.noteTextPlaceholder', 'Cliente recebeu o e-mail de boas-vindas')} rows={3} />
              {vars}
            </>
          );
        }

        if (nodeType === 'marketplace_add_tag') {
          return (
            <>
              {storeBox}
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('workflow.mkp.addTagDesc', 'Põe uma etiqueta no cliente lá na loja (ex.: "vip"). Precisa ter o e-mail do cliente.')}</p>
              {label(t('workflow.mkp.cfg_tagName', 'Etiqueta'))}
              <Input value={config.tagName || ''} onChange={(e) => handleConfigChange('tagName', e.target.value)} placeholder={t('workflow.mkp.tagNamePlaceholder', 'vip, carrinho-abandonado, recorrente...')} />
              <p className="text-xs text-gray-500 mt-1">{t('workflow.mkp.tagHelp', 'Mais de uma? Separe por vírgula.')}</p>
            </>
          );
        }

        // marketplace_publish_blog_post
        const mode = config.mode || 'text';
        return (
          <>
            {storeBox}
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{t('workflow.mkp.publishBlogDesc', 'Publica um post no blog do site da loja. O link aparece em Blog & SEO.')}</p>
            <div className="mb-3">
              {label(t('workflow.mkp.blogMode', 'Como escrever o post?'))}
              <div className="flex gap-2">
                {[
                  { v: 'text', l: t('workflow.mkp.blogModeText', 'Eu escrevo') },
                  { v: 'ai', l: t('workflow.mkp.blogModeAi', 'Escrever com IA') },
                ].map((o) => (
                  <button key={o.v} type="button" onClick={() => handleConfigChange('mode', o.v)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${mode === o.v ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600'}`}>
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            {mode === 'ai' ? (
              <div className="mb-3">
                {label(t('workflow.mkp.blogTopic', 'Tema do artigo'))}
                <Input value={config.topic || ''} onChange={(e) => handleConfigChange('topic', e.target.value)} placeholder={t('workflow.mkp.blogTopicPlaceholder', 'Como escolher o tamanho certo do tênis')} />
                <p className="text-xs text-gray-500 mt-1">{t('workflow.mkp.blogAiHelp', 'A IA escreve título, texto e SEO no estilo do seu negócio.')}</p>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  {label(t('workflow.mkp.blogTitle', 'Título'))}
                  <Input value={config.title || ''} onChange={(e) => handleConfigChange('title', e.target.value)} placeholder={t('workflow.mkp.blogTitlePlaceholder', 'Novidade na {{store.name}}')} />
                </div>
                <div className="mb-3">
                  {label(t('workflow.mkp.blogContent', 'Texto do post'))}
                  <Textarea value={config.content || ''} onChange={(e) => handleConfigChange('content', e.target.value)} rows={6} placeholder={t('workflow.mkp.blogContentPlaceholder', 'Escreva o texto. Pode usar HTML simples (<p>, <h2>).')} />
                </div>
                <div className="mb-3">
                  {label(t('workflow.mkp.blogExcerpt', 'Resumo (opcional)'))}
                  <Input value={config.excerpt || ''} onChange={(e) => handleConfigChange('excerpt', e.target.value)} />
                </div>
              </>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <div>
                {label(t('workflow.mkp.blogCategory', 'Categoria (opcional)'))}
                <Input value={config.category || ''} onChange={(e) => handleConfigChange('category', e.target.value)} placeholder={t('workflow.mkp.blogCategoryPlaceholder', 'Dicas')} />
              </div>
              <div>
                {label(t('workflow.mkp.blogCover', 'Imagem de capa (link, opcional)'))}
                <Input value={config.coverImageUrl || ''} onChange={(e) => handleConfigChange('coverImageUrl', e.target.value)} placeholder="https://..." />
              </div>
            </div>
            <div>
              {label(t('workflow.mkp.blogStatus', 'Quando aparecer no site?'))}
              <Select
                value={config.status || 'published'}
                onChange={(e) => handleConfigChange('status', e.target.value)}
                options={[
                  { value: 'published', label: t('workflow.mkp.blogPublishNow', 'Publicar na hora') },
                  { value: 'draft', label: t('workflow.mkp.blogDraft', 'Deixar como rascunho (eu reviso antes)') },
                ]}
              />
            </div>
          </>
        );
      }

      // Social Media trigger configs
      case 'instagram_comment':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <div className="flex items-center gap-2 text-pink-700 dark:text-pink-400 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                {t('workflow.social.instagramCommentDesc', 'Triggers when someone comments on an Instagram post')}
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-pink-50/50 dark:bg-pink-900/10 rounded-lg border border-pink-100 dark:border-pink-800/50 mb-3">
              <input
                type="checkbox"
                id="includeReels"
                checked={config.includeReels || false}
                onChange={(e) => handleConfigChange('includeReels', e.target.checked)}
                className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
              />
              <label htmlFor="includeReels" className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                {t('workflow.social.includeReels', 'Include Reels comments')}
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.postFilter', 'Filter by post (optional)')}
              </label>
              <SocialPostPicker
                platform="instagram"
                value={String(config.postId || '')}
                onChange={(postId) => handleConfigChange('postId', postId)}
              />
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.keywordFilter', 'Filter by keyword (optional)')}
              </label>
              <Input
                value={config.keyword || ''}
                onChange={(e) => handleConfigChange('keyword', e.target.value)}
                placeholder={t('workflow.social.keywordPlaceholder', 'E.g.: price, buy, interested...')}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.social.keywordHelp', 'Leave empty to trigger on any comment')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.instagramVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400">
                {'{{comment.text}}, {{comment.id}}, {{commenter.username}}, {{commenter.id}}, {{post.id}}, {{post.caption}}'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('workflow.social.instagramPermNote', 'Requires Instagram Business account connected in CRM with instagram_manage_comments and pages_read_engagement permissions')}
              </p>
            </div>
          </>
        );

      case 'facebook_comment':
        return (
          <>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-4">
              <div className="flex items-center gap-2 text-blue-700 dark:text-blue-400 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                {t('workflow.social.facebookCommentDesc', 'Triggers when someone comments on a Facebook post')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.postFilter', 'Filter by post (optional)')}
              </label>
              <SocialPostPicker
                platform="facebook"
                value={String(config.postId || '')}
                onChange={(postId) => handleConfigChange('postId', postId)}
              />
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.keywordFilter', 'Filter by keyword (optional)')}
              </label>
              <Input
                value={config.keyword || ''}
                onChange={(e) => handleConfigChange('keyword', e.target.value)}
                placeholder={t('workflow.social.keywordPlaceholder', 'E.g.: price, buy, interested...')}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.social.keywordHelp', 'Leave empty to trigger on any comment')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.facebookVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400">
                {'{{comment.text}}, {{comment.id}}, {{commenter.name}}, {{commenter.id}}, {{post.id}}'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('workflow.social.facebookPermNote', 'Requires Facebook Page connected in CRM with pages_read_engagement and pages_manage_metadata permissions')}
              </p>
            </div>
          </>
        );

      case 'threads_comment':
        return (
          <>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 mb-4">
              <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-5.602.04-8.196 3.2-8.68 8.398L3.5 12l.005 1.483c.484 5.2 3.078 8.36 8.68 8.398 3.248-.02 5.518-.903 7.15-2.779 1.108-1.274 1.697-2.77 1.697-4.317 0-.898-.235-1.63-.673-2.113-.476-.527-1.185-.81-2.05-.82-.635 0-1.18.186-1.548.49-.3.248-.562.695-.562 1.468v.15c0 .675-.258 1.295-.747 1.795-.546.557-1.317.863-2.171.863-1.773 0-3.149-1.363-3.149-3.105 0-1.7 1.325-3.085 2.953-3.085.65 0 1.22.197 1.664.57l.015-.468h2.081l-.054 3.213c0 .553.06 1.02.221 1.394.288.667.91 1.064 1.667 1.064.97 0 1.928-.45 2.57-1.238.735-.902 1.11-2.158 1.11-3.724 0-2.03-.677-3.769-1.956-5.028C17.254 5.19 14.93 4.422 12.206 4.4 9.058 4.427 6.68 5.493 5.134 7.565c-1.378 1.846-2.09 4.356-2.118 7.456.03 3.1.742 5.607 2.118 7.452 1.546 2.073 3.924 3.14 7.07 3.167 2.29-.018 4.256-.521 5.846-1.496l.978 1.74C17.28 27.142 14.951 27.75 12.186 24z"/></svg>
                {t('workflow.social.threadsCommentDesc', 'Triggers when someone comments on a Threads post')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.keywordFilter', 'Filter by keyword (optional)')}
              </label>
              <Input
                value={config.keyword || ''}
                onChange={(e) => handleConfigChange('keyword', e.target.value)}
                placeholder={t('workflow.social.keywordPlaceholder', 'E.g.: price, buy, interested...')}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.social.keywordHelp', 'Leave empty to trigger on any comment')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.threadsVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400">
                {'{{comment.text}}, {{comment.id}}, {{commenter.username}}, {{commenter.id}}, {{post.id}}'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('workflow.social.threadsPermNote', 'Requires Threads account connected with threads_manage_replies permission')}
              </p>
            </div>
          </>
        );

      case 'instagram_first_dm':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <div className="flex items-center gap-2 text-pink-700 dark:text-pink-400 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                {t('workflow.social.instagramFirstDmDesc', 'Triggers when someone sends their first DM to your Instagram account')}
              </div>
              <p className="text-xs text-pink-600 dark:text-pink-500 mt-1.5">
                {t('workflow.social.instagramFirstDmInfo', 'Fires only once per person — on their very first message. Use this to automatically welcome new contacts.')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.instagramVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400">
                {'{{sender.username}}, {{sender.name}}, {{sender.id}}, {{message.text}}, {{message.mid}}'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('workflow.social.instagramFirstDmPermNote', 'Requires the "messages" webhook field subscribed in your Meta App Dashboard and instagram_business_manage_messages permission.')}
              </p>
            </div>
          </>
        );

      case 'instagram_icebreaker':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-800 mb-4">
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-400 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                {t('workflow.social.instagramIcebreakerDesc', 'Triggers when someone taps an Ice Breaker button in your Instagram DM')}
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-500 mt-1.5">
                {t('workflow.social.instagramIcebreakerInfo', 'Ice Breakers are tappable buttons shown to users who open your DM for the first time (max 4 buttons).')}
              </p>
            </div>

            {/* Ice Breaker Buttons Configuration */}
            {(() => {
              const buttons: Array<{ question: string; payload: string }> = Array.isArray(config.iceBreakers) && config.iceBreakers.length > 0
                ? config.iceBreakers
                : [{ question: '', payload: '' }];

              const updateButtons = (newButtons: Array<{ question: string; payload: string }>) => {
                const newConfig = { ...config, iceBreakers: newButtons };
                setConfig(newConfig);
                // isConfigured if at least one button has both question and payload filled
                const hasValidButton = newButtons.some(b => b.question.trim() && b.payload.trim());
                onUpdate({ config: newConfig, isConfigured: hasValidButton });
              };

              return (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('workflow.social.icebreakerButtons', 'Ice Breaker Buttons')}
                  </label>
                  <p className="text-xs text-gray-500">
                    {t('workflow.social.icebreakerButtonsHelp', 'Configure the buttons that will appear when someone opens your DM for the first time. These will be saved to your Instagram account when you activate the workflow.')}
                  </p>

                  <div className="space-y-3">
                    {buttons.map((btn, idx) => (
                      <div key={`ib-${idx}`} className="p-3 rounded-lg border border-purple-200 dark:border-purple-700 bg-purple-50/30 dark:bg-purple-900/10">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                            {t('workflow.social.icebreakerButton', 'Button')} {idx + 1}
                          </span>
                          {buttons.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                const newBtns = buttons.filter((_, i) => i !== idx);
                                updateButtons(newBtns);
                              }}
                              className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Input
                            value={btn.question}
                            onChange={(e) => {
                              const newBtns = [...buttons];
                              newBtns[idx] = { ...newBtns[idx], question: e.target.value };
                              updateButtons(newBtns);
                            }}
                            placeholder={t('workflow.social.icebreakerQuestionPlaceholder', 'Button text (e.g.: "What services do you offer?")')}
                          />
                          <Input
                            value={btn.payload}
                            onChange={(e) => {
                              const newBtns = [...buttons];
                              newBtns[idx] = { ...newBtns[idx], payload: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_') };
                              updateButtons(newBtns);
                            }}
                            placeholder={t('workflow.social.icebreakerPayloadPlaceholder', 'Payload ID (e.g.: SERVICES)')}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {buttons.length < 4 && (
                    <button
                      type="button"
                      onClick={() => updateButtons([...buttons, { question: '', payload: '' }])}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      {t('workflow.social.icebreakerAddButton', 'Add button')} ({buttons.length}/4)
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Filter by specific payload */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.icebreakerPayloadFilter', 'Trigger filter (optional)')}
              </label>
              <Input
                value={config.payload || ''}
                onChange={(e) => {
                  const currentButtons = Array.isArray(config.iceBreakers) ? config.iceBreakers : [];
                  const hasValidButton = currentButtons.some((b: any) => b.question?.trim() && b.payload?.trim());
                  const newConfig = { ...config, payload: e.target.value };
                  setConfig(newConfig);
                  onUpdate({ config: newConfig, isConfigured: hasValidButton });
                }}
                placeholder={t('workflow.social.icebreakerPayloadFilterPlaceholder', 'Leave empty for all buttons, or enter a payload to filter')}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.social.icebreakerPayloadFilterHelp', 'If set, this workflow only triggers when the specific button with this payload is tapped')}
              </p>
            </div>

            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.instagramVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400">
                {'{{sender.username}}, {{sender.name}}, {{sender.id}}, {{postback.title}}, {{postback.payload}}'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('workflow.social.instagramIcebreakerPermNote', 'Requires "messaging_postbacks" webhook field in Meta App Dashboard. Ice Breaker buttons are synced to Instagram when the workflow is activated.')}
              </p>
            </div>
          </>
        );

      case 'instagram_story_reply':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-lg border border-orange-200 dark:border-orange-800 mb-4">
              <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 text-sm font-medium">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                {t('workflow.social.instagramStoryReplyDesc', 'Triggers when someone replies to your Instagram Story via DM')}
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-500 mt-1.5">
                {t('workflow.social.instagramStoryReplyInfo', 'Fires every time a follower sends a DM in response to one of your Stories. The bot can then automatically reply to that DM.')}
              </p>
            </div>

            {/* Keyword filter (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.storyReplyKeyword', 'Keyword filter (optional)')}
              </label>
              <Input
                value={config.keyword || ''}
                onChange={(e) => {
                  const newConfig = { ...config, keyword: e.target.value };
                  setConfig(newConfig);
                  onUpdate({ config: newConfig, isConfigured: true });
                }}
                placeholder={t('workflow.social.storyReplyKeywordPlaceholder', 'Leave empty for all replies, or enter a keyword to filter')}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.social.storyReplyKeywordHelp', 'If set, this workflow only triggers when the reply message contains this keyword')}
              </p>
            </div>

            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.instagramVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400">
                {'{{sender.username}}, {{sender.name}}, {{sender.id}}, {{message.text}}, {{message.mid}}, {{story.id}}, {{story.url}}'}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3">
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {t('workflow.social.instagramStoryReplyPermNote', 'Requires the "messages" webhook field subscribed in your Meta App Dashboard and instagram_manage_messages permission.')}
              </p>
            </div>
          </>
        );

      case 'live_keyword_detected':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 rounded-lg border border-red-200 dark:border-red-800 mb-4">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-400 text-sm font-medium">
                <Radio className="w-4 h-4" />
                {t('workflow.social.liveKeywordDetectedDesc', 'Triggers when a keyword is detected in live stream comments (Instagram, Facebook or YouTube)')}
              </div>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
              <p className="text-xs text-blue-700 dark:text-blue-400">
                {t('workflow.social.liveKeywordNote', 'Configure keywords and link this workflow in the Live Automation page. This trigger fires automatically when someone comments the keyword during a live.')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.liveVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400 font-mono">
                {'{{comment.text}}, {{comment.id}}, {{commenter.username}}, {{commenter.name}}, {{commenter.id}}, {{matchedKeyword}}, {{product.name}}, {{product.url}}, {{coupon}}, {{liveSession.title}}'}
              </p>
            </div>
          </>
        );

      case 'live_ended':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg border border-orange-200 dark:border-orange-800 mb-4">
              <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 text-sm font-medium">
                <Radio className="w-4 h-4" />
                {t('workflow.social.liveEndedDesc', 'Triggers when the live ends. Receives the entire list of captured contacts for bulk sending.')}
              </div>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mb-3">
              <p className="text-xs text-blue-700 dark:text-blue-400">
                {t('workflow.social.liveEndedNote', 'This trigger fires automatically when you end a live on the Live Automation page. All captured contacts are sent as a list to this workflow.')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.social.liveVars', 'Available data')}:
              </p>
              <p className="text-xs text-gray-400 font-mono">
                {'{{contact.username}}, {{contact.name}}, {{contact.keyword}}, {{contact.product_name}}, {{contact.product_url}}, {{liveSession.title}}, {{liveSession.total_captures}}'}
              </p>
            </div>
          </>
        );

      // Social Media action configs
      case 'reply_comment':
        return (
          <>
            <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <div className="flex items-center gap-2 text-pink-700 dark:text-pink-400 text-sm font-medium">
                <MessageCircle className="w-4 h-4" />
                {t('workflow.social.replyCommentDesc', 'Automatically replies to the comment on the post')}
              </div>
            </div>

            {/* Reply mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.replyMode', 'Reply mode')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input
                    type="radio"
                    name="replyMode"
                    value="fixed"
                    checked={config.replyMode !== 'ai'}
                    onChange={() => handleConfigChange('replyMode', 'fixed')}
                    className="text-primary-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeFixed', 'Same reply for everyone')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.replyModeFixedDesc', 'Sends the same message to all comments')}</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input
                    type="radio"
                    name="replyMode"
                    value="ai"
                    checked={config.replyMode === 'ai'}
                    onChange={() => handleConfigChange('replyMode', 'ai')}
                    className="text-primary-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeAi', 'Reply with AI')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.replyModeAiDesc', 'AI generates a personalized reply based on the post and comment context')}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Fixed messages - user creates multiple and selects one to use */}
            {config.replyMode !== 'ai' && (() => {
              const messages: string[] = Array.isArray(config.messages) && config.messages.length > 0
                ? config.messages
                : (config.message ? [config.message] : ['']);
              const selectedIndex: number = typeof config.selectedMessageIndex === 'number'
                ? Math.min(config.selectedMessageIndex, messages.length - 1)
                : 0;

              const updateAll = (newMessages: string[], newSelectedIndex: number) => {
                const safeIndex = Math.max(0, Math.min(newSelectedIndex, newMessages.length - 1));
                const newConfig = {
                  ...config,
                  messages: newMessages,
                  selectedMessageIndex: safeIndex,
                  message: newMessages[safeIndex] || '',
                };
                setConfig(newConfig);
                onUpdate({ config: newConfig, isConfigured: !!(newMessages[safeIndex]?.trim()) });
              };

              return (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('workflow.social.replyMessages', 'Reply messages')}
                  </label>

                  <div className={messages.length > 2 ? 'max-h-[340px] overflow-y-auto space-y-3 pr-1' : 'space-y-3'}>
                  {messages.map((msg, idx) => {
                    const isSelected = idx === selectedIndex;
                    return (
                      <div
                        key={`msg-${idx}-${messages.length}`}
                        className={`relative rounded-lg border-2 transition-colors ${isSelected ? 'border-pink-400 bg-pink-50/50 dark:bg-pink-900/10 dark:border-pink-600' : 'border-gray-200 dark:border-gray-600'}`}
                      >
                        <div className="flex items-start gap-2 p-3">
                          <input
                            type="radio"
                            name="selectedMessage"
                            checked={isSelected}
                            onChange={() => updateAll(messages, idx)}
                            className="mt-2.5 text-pink-500 flex-shrink-0"
                          />
                          <div className="flex-1">
                            <Textarea
                              value={msg}
                              onChange={(e) => {
                                const newMsgs = [...messages];
                                newMsgs[idx] = e.target.value;
                                updateAll(newMsgs, selectedIndex);
                              }}
                              placeholder={t('workflow.social.replyPlaceholder', 'Hi {{commenter.username}}! Thanks for your comment...')}
                              rows={3}
                            />
                            {isSelected && (
                              <span className="inline-block mt-1 text-xs font-medium text-pink-600 dark:text-pink-400">
                                {t('workflow.social.activeMessage', 'Active - this message will be sent')}
                              </span>
                            )}
                          </div>
                          {messages.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!confirm(t('workflow.social.confirmDeleteMessage', 'Remove this message?'))) return;
                                const newMsgs = messages.filter((_, i) => i !== idx);
                                const newIdx = idx < selectedIndex ? selectedIndex - 1 : (idx === selectedIndex ? 0 : selectedIndex);
                                updateAll(newMsgs, newIdx);
                              }}
                              className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                              title={t('common.remove', 'Remove')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>

                  <button
                    type="button"
                    onClick={() => updateAll([...messages, ''], selectedIndex)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-pink-400 hover:text-pink-600 dark:hover:text-pink-400 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('workflow.social.addMessage', 'Add another message')}
                  </button>

                  <p className="text-xs text-gray-500">
                    {t('workflow.social.replyVars', 'Use {{commenter.username}}, {{commenter.name}}, {{comment.text}} to personalize')}
                  </p>
                </div>
              );
            })()}

            {/* AI config */}
            {config.replyMode === 'ai' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.social.aiTone', 'Reply tone')}
                  </label>
                  <Select
                    value={config.aiTone || 'friendly'}
                    onChange={(e) => handleConfigChange('aiTone', e.target.value)}
                    options={[
                      { value: 'friendly', label: t('workflow.social.toneFriendly', 'Friendly') },
                      { value: 'professional', label: t('workflow.social.toneProfessional', 'Professional') },
                      { value: 'casual', label: t('workflow.social.toneCasual', 'Casual') },
                      { value: 'enthusiastic', label: t('workflow.social.toneEnthusiastic', 'Enthusiastic') },
                      { value: 'formal', label: t('workflow.social.toneFormal', 'Formal') },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.social.aiInstructions', 'Additional instructions (optional)')}
                  </label>
                  <Textarea
                    value={config.aiInstructions || ''}
                    onChange={(e) => handleConfigChange('aiInstructions', e.target.value)}
                    placeholder={t('workflow.social.aiInstructionsPlaceholder', 'E.g.: Always mention our product X, include an emoji at the end...')}
                    rows={3}
                  />
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {t('workflow.social.aiContextNote', 'AI will use the post context (caption, content type) and the comment text to generate a relevant and personalized reply.')}
                  </p>
                </div>
              </div>
            )}

            {/* Reply scope */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.replyScope', 'Reply to')}
              </label>
              <Select
                value={config.replyScope || 'each'}
                onChange={(e) => handleConfigChange('replyScope', e.target.value)}
                options={[
                  { value: 'each', label: t('workflow.social.replyScopeEach', 'Each comment individually') },
                  { value: 'first_per_user', label: t('workflow.social.replyScopeFirstPerUser', 'Only the first comment from each user') },
                ]}
              />
              <p className="text-xs text-gray-500 mt-1">
                {config.replyScope === 'first_per_user'
                  ? t('workflow.social.replyScopeFirstPerUserDesc', 'Replies only to the first comment from each user, ignoring repeated comments from the same user')
                  : t('workflow.social.replyScopeEachDesc', 'Replies to all comments that trigger the workflow')
                }
              </p>
            </div>
          </>
        );

      case 'send_social_dm':
        return (
          <>
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 mb-4">
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-400 text-sm font-medium">
                <Send className="w-4 h-4" />
                {t('workflow.social.sendDmDesc', 'Sends a direct message to the comment author')}
              </div>
            </div>

            {/* DM reply mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.replyMode', 'Reply mode')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input
                    type="radio"
                    name="dmReplyMode"
                    value="fixed"
                    checked={config.replyMode !== 'ai'}
                    onChange={() => handleConfigChange('replyMode', 'fixed')}
                    className="text-primary-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeFixed', 'Same reply for everyone')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.dmModeFixedDesc', 'Sends the same DM to everyone')}</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input
                    type="radio"
                    name="dmReplyMode"
                    value="ai"
                    checked={config.replyMode === 'ai'}
                    onChange={() => handleConfigChange('replyMode', 'ai')}
                    className="text-primary-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeAi', 'Reply with AI')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.dmModeAiDesc', 'AI generates a personalized welcome message based on context')}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Fixed messages - multiple with selection */}
            {config.replyMode !== 'ai' && (() => {
              const messages: string[] = Array.isArray(config.messages) && config.messages.length > 0
                ? config.messages
                : (config.message ? [config.message] : ['']);
              const selectedIndex: number = typeof config.selectedMessageIndex === 'number'
                ? Math.min(config.selectedMessageIndex, messages.length - 1)
                : 0;

              const updateAll = (newMessages: string[], newSelectedIndex: number) => {
                const safeIndex = Math.max(0, Math.min(newSelectedIndex, newMessages.length - 1));
                const newConfig = {
                  ...config,
                  messages: newMessages,
                  selectedMessageIndex: safeIndex,
                  message: newMessages[safeIndex] || '',
                };
                setConfig(newConfig);
                onUpdate({ config: newConfig, isConfigured: !!(newMessages[safeIndex]?.trim()) });
              };

              return (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('workflow.social.dmMessages', 'DM messages')}
                  </label>
                  <div className={messages.length > 2 ? 'max-h-[340px] overflow-y-auto space-y-3 pr-1' : 'space-y-3'}>
                  {messages.map((msg, idx) => {
                    const isSelected = idx === selectedIndex;
                    return (
                      <div
                        key={`dm-msg-${idx}-${messages.length}`}
                        className={`relative rounded-lg border-2 transition-colors ${isSelected ? 'border-purple-400 bg-purple-50/50 dark:bg-purple-900/10 dark:border-purple-600' : 'border-gray-200 dark:border-gray-600'}`}
                      >
                        <div className="flex items-start gap-2 p-3">
                          <input
                            type="radio"
                            name="selectedDmMessage"
                            checked={isSelected}
                            onChange={() => updateAll(messages, idx)}
                            className="mt-2.5 text-purple-500 flex-shrink-0"
                          />
                          <div className="flex-1">
                            <Textarea
                              value={msg}
                              onChange={(e) => {
                                const newMsgs = [...messages];
                                newMsgs[idx] = e.target.value;
                                updateAll(newMsgs, selectedIndex);
                              }}
                              placeholder={t('workflow.social.dmPlaceholder', 'Hi {{commenter.username}}! I noticed you\'re interested...')}
                              rows={3}
                            />
                            {isSelected && (
                              <span className="inline-block mt-1 text-xs font-medium text-purple-600 dark:text-purple-400">
                                {t('workflow.social.activeMessage', 'Active - this message will be sent')}
                              </span>
                            )}
                          </div>
                          {messages.length > 1 && (
                            <button
                              type="button"
                              onClick={() => {
                                if (!confirm(t('workflow.social.confirmDeleteMessage', 'Remove this message?'))) return;
                                const newMsgs = messages.filter((_, i) => i !== idx);
                                const newIdx = idx < selectedIndex ? selectedIndex - 1 : (idx === selectedIndex ? 0 : selectedIndex);
                                updateAll(newMsgs, newIdx);
                              }}
                              className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                              title={t('common.remove', 'Remove')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  </div>
                  <button
                    type="button"
                    onClick={() => updateAll([...messages, ''], selectedIndex)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {t('workflow.social.addMessage', 'Add another message')}
                  </button>
                  <p className="text-xs text-gray-500">
                    {t('workflow.social.dmVars', 'Use {{commenter.username}}, {{commenter.name}}, {{comment.text}}, {{post.id}}, {{sender.username}}, {{message.text}} to personalize')}
                  </p>
                </div>
              );
            })()}

            {/* AI config */}
            {config.replyMode === 'ai' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.social.aiTone', 'Reply tone')}
                  </label>
                  <Select
                    value={config.aiTone || 'friendly'}
                    onChange={(e) => handleConfigChange('aiTone', e.target.value)}
                    options={[
                      { value: 'friendly', label: t('workflow.social.toneFriendly', 'Friendly') },
                      { value: 'professional', label: t('workflow.social.toneProfessional', 'Professional') },
                      { value: 'casual', label: t('workflow.social.toneCasual', 'Casual') },
                      { value: 'enthusiastic', label: t('workflow.social.toneEnthusiastic', 'Enthusiastic') },
                      { value: 'formal', label: t('workflow.social.toneFormal', 'Formal') },
                    ]}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('workflow.social.aiInstructions', 'Additional instructions (optional)')}
                  </label>
                  <Textarea
                    value={config.aiInstructions || ''}
                    onChange={(e) => handleConfigChange('aiInstructions', e.target.value)}
                    placeholder={t('workflow.social.dmAiInstructionsPlaceholder', 'E.g.: Welcome the user, mention our services, be friendly...')}
                    rows={3}
                  />
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-blue-600 dark:text-blue-400">
                    {t('workflow.social.dmAiContextNote', 'AI will use the message context to generate a relevant and personalized DM reply.')}
                  </p>
                </div>
              </div>
            )}

            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 mt-3">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 text-sm">
                <AlertTriangle className="w-4 h-4" />
                {t('workflow.social.dmNote', 'DMs can only be sent to users who have already interacted with the page/account. The CRM handles permissions automatically.')}
              </div>
            </div>
          </>
        );

      case 'private_reply':
        return (
          <>
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800 mb-4">
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-400 text-sm font-medium mb-1">
                <Send className="w-4 h-4" />
                {t('workflow.social.privateReplyDesc', 'Private Reply via DM (Instagram)')}
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-400">
                {t('workflow.social.privateReplyInfo', 'Sends an automatic DM to whoever commented, without needing them to have started a conversation first.')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.privateReplyMessage', 'DM Message')}
              </label>
              <Textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                placeholder={t('workflow.social.privateReplyPlaceholder', 'Hi {{commenter.username}}! I saw your comment. {{product.name}}: {{product.url}}')}
                rows={4}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('workflow.social.privateReplyVars', 'Use {{commenter.username}}, {{commenter.name}}, {{comment.text}}, {{product.name}}, {{product.url}}, {{coupon}} to personalize')}
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 mt-3 space-y-1.5">
              <p className="text-xs text-blue-700 dark:text-blue-400 font-medium">
                {t('workflow.social.privateReplyRules', 'Private Reply Rules:')}
              </p>
              <ul className="text-xs text-blue-600 dark:text-blue-400 space-y-0.5 list-disc list-inside">
                <li>{t('workflow.social.privateReplyRule1', 'Only 1 message per comment')}</li>
                <li>{t('workflow.social.privateReplyRule2', 'Must be sent within 7 days of the comment')}</li>
                <li>{t('workflow.social.privateReplyRule3', 'If user doesn\'t follow the page, it goes to "Requests"')}</li>
                <li>{t('workflow.social.privateReplyRule4', 'Works only on Instagram')}</li>
              </ul>
            </div>
          </>
        );

      case 'reply_welcome_dm':
        return (() => {
          const handleDmConfigChange = (key: string, value: any) => {
            const newConfig = { ...config, [key]: value };
            setConfig(newConfig);
            const isAi = key === 'replyMode' ? value === 'ai' : config.replyMode === 'ai';
            const msg = key === 'message' ? value : config.message;
            onUpdate({ config: newConfig, isConfigured: isAi || !!(msg?.trim()) });
          };
          return (
          <>
            <div className="p-3 bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <div className="flex items-center gap-2 text-pink-700 dark:text-pink-400 text-sm font-medium mb-1">
                <MessageSquare className="w-4 h-4" />
                {t('workflow.social.replyWelcomeDmDesc', 'Sends a welcome DM in response to the first message received')}
              </div>
              <p className="text-xs text-pink-600 dark:text-pink-500">
                {t('workflow.social.replyWelcomeDmInfo', 'Use this action with the "First Instagram DM" trigger. The user already opened the conversation, so the message is delivered instantly.')}
              </p>
            </div>

            {/* Reply mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.replyMode', 'Reply mode')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input type="radio" name="welcomeDmMode" value="fixed" checked={config.replyMode !== 'ai'} onChange={() => handleDmConfigChange('replyMode', 'fixed')} className="text-primary-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeFixed', 'Same reply for everyone')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.welcomeDmFixedDesc', 'Sends the same welcome message to everyone')}</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input type="radio" name="welcomeDmMode" value="ai" checked={config.replyMode === 'ai'} onChange={() => handleDmConfigChange('replyMode', 'ai')} className="text-primary-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeAi', 'Reply with AI')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.welcomeDmAiDesc', 'AI generates a personalized welcome based on the message received')}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Fixed message */}
            {config.replyMode !== 'ai' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.social.welcomeDmMessage', 'Welcome message')}
                </label>
                <Textarea
                  value={config.message || ''}
                  onChange={(e) => handleDmConfigChange('message', e.target.value)}
                  placeholder={t('workflow.social.welcomeDmPlaceholder', 'Hi {{sender.username}}! 👋 Welcome! How can I help you?')}
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('workflow.social.welcomeDmVars', 'Use {{sender.username}}, {{sender.name}}, {{message.text}} to personalize')}
                </p>
              </div>
            )}

            {/* AI config */}
            {config.replyMode === 'ai' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('workflow.social.aiTone', 'Reply tone')}</label>
                  <Select value={config.aiTone || 'friendly'} onChange={(e) => handleDmConfigChange('aiTone', e.target.value)} options={[
                    { value: 'friendly', label: t('workflow.social.toneFriendly', 'Friendly') },
                    { value: 'professional', label: t('workflow.social.toneProfessional', 'Professional') },
                    { value: 'casual', label: t('workflow.social.toneCasual', 'Casual') },
                    { value: 'enthusiastic', label: t('workflow.social.toneEnthusiastic', 'Enthusiastic') },
                    { value: 'formal', label: t('workflow.social.toneFormal', 'Formal') },
                  ]} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('workflow.social.aiInstructions', 'Additional instructions (optional)')}</label>
                  <Textarea value={config.aiInstructions || ''} onChange={(e) => handleDmConfigChange('aiInstructions', e.target.value)} placeholder={t('workflow.social.welcomeDmAiPlaceholder', 'E.g.: Welcome the user, mention our services, ask how you can help...')} rows={3} />
                </div>
              </div>
            )}

            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('workflow.social.instagramVars', 'Available data')}:</p>
              <p className="text-xs text-gray-400">{'{{sender.username}}, {{sender.name}}, {{sender.id}}, {{message.text}}, {{message.mid}}'}</p>
            </div>
          </>
        );
        })();

      case 'reply_icebreaker':
        return (() => {
          // Find the icebreaker trigger node to read configured buttons
          const triggerNode = allNodes.find(n =>
            n.data?.type === 'instagram_icebreaker' || n.data?.type === 'trigger_instagram_icebreaker'
          );
          const triggerButtons: Array<{ question: string; payload: string }> = triggerNode?.data?.config?.iceBreakers?.filter(
            (b: any) => b.question?.trim() && b.payload?.trim()
          ) || [];

          // Per-button responses stored as config.responses = { [payload]: { replyMode, message, aiTone, aiInstructions } }
          const responses: Record<string, { replyMode?: string; message?: string; aiTone?: string; aiInstructions?: string }> = config.responses || {};

          const updateResponse = (payload: string, key: string, value: any) => {
            const current = responses[payload] || {};
            const updated = { ...current, [key]: value };
            const newResponses = { ...responses, [payload]: updated };
            const newConfig = { ...config, responses: newResponses };
            setConfig(newConfig);
            // isConfigured if at least one button has a response configured
            const hasConfig = Object.values(newResponses).some((r: any) =>
              r.replyMode === 'ai' || !!(r.message?.trim())
            );
            onUpdate({ config: newConfig, isConfigured: hasConfig });
          };

          return (
          <>
            <div className="p-3 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-800 mb-4">
              <div className="flex items-center gap-2 text-purple-700 dark:text-purple-400 text-sm font-medium mb-1">
                <MessageSquare className="w-4 h-4" />
                {t('workflow.social.replyIcebreakerDesc', 'Sends a DM in response to an Ice Breaker button tap')}
              </div>
              <p className="text-xs text-purple-600 dark:text-purple-500">
                {t('workflow.social.replyIcebreakerPerButton', 'Configure a response for each Ice Breaker button. The reply language follows the button language.')}
              </p>
            </div>

            {triggerButtons.length === 0 && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  {t('workflow.social.noIcebreakerButtons', 'No Ice Breaker buttons configured. Add buttons in the Ice Breaker trigger node first.')}
                </p>
              </div>
            )}

            {triggerButtons.map((btn, idx) => {
              const resp = responses[btn.payload] || {};
              const isAi = resp.replyMode === 'ai';
              return (
                <div key={`ib-resp-${btn.payload}-${idx}`} className="mt-4 p-4 rounded-lg border border-purple-200 dark:border-purple-700 bg-purple-50/30 dark:bg-purple-900/10">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                      {btn.payload}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white truncate">"{btn.question}"</span>
                  </div>

                  {/* Per-button reply mode */}
                  <div className="space-y-2 mb-3">
                    <div className="flex gap-2">
                      <label className={`flex-1 flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-colors ${!isAi ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
                        <input type="radio" name={`ibMode-${btn.payload}`} checked={!isAi} onChange={() => updateResponse(btn.payload, 'replyMode', 'fixed')} className="text-purple-500" />
                        {t('workflow.social.replyModeFixed', 'Same reply for everyone')}
                      </label>
                      <label className={`flex-1 flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-xs transition-colors ${isAi ? 'border-purple-400 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-200 dark:border-gray-600'}`}>
                        <input type="radio" name={`ibMode-${btn.payload}`} checked={isAi} onChange={() => updateResponse(btn.payload, 'replyMode', 'ai')} className="text-purple-500" />
                        {t('workflow.social.replyModeAi', 'Reply with AI')}
                      </label>
                    </div>
                  </div>

                  {/* Fixed message for this button */}
                  {!isAi && (
                    <Textarea
                      value={resp.message || ''}
                      onChange={(e) => updateResponse(btn.payload, 'message', e.target.value)}
                      placeholder={`${t('workflow.social.icebreakerReplyPlaceholder', 'Hi {{sender.username}}!')} — "${btn.question}"`}
                      rows={3}
                    />
                  )}

                  {/* AI config for this button */}
                  {isAi && (
                    <div className="space-y-2">
                      <Select value={resp.aiTone || 'friendly'} onChange={(e) => updateResponse(btn.payload, 'aiTone', e.target.value)} options={[
                        { value: 'friendly', label: t('workflow.social.toneFriendly', 'Friendly') },
                        { value: 'professional', label: t('workflow.social.toneProfessional', 'Professional') },
                        { value: 'casual', label: t('workflow.social.toneCasual', 'Casual') },
                        { value: 'enthusiastic', label: t('workflow.social.toneEnthusiastic', 'Enthusiastic') },
                        { value: 'formal', label: t('workflow.social.toneFormal', 'Formal') },
                      ]} />
                      <Textarea value={resp.aiInstructions || ''} onChange={(e) => updateResponse(btn.payload, 'aiInstructions', e.target.value)} placeholder={t('workflow.social.icebreakerAiPlaceholder', 'E.g.: Explain our services in detail, include pricing...')} rows={2} />
                    </div>
                  )}
                </div>
              );
            })}

            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('workflow.social.instagramVars', 'Available data')}:</p>
              <p className="text-xs text-gray-400">{'{{sender.username}}, {{sender.name}}, {{sender.id}}, {{postback.title}}, {{postback.payload}}'}</p>
            </div>
          </>
        );
        })();

      case 'reply_story_dm':
        return (() => {
          const handleStoryDmConfigChange = (key: string, value: any) => {
            const newConfig = { ...config, [key]: value };
            setConfig(newConfig);
            const isAi = key === 'replyMode' ? value === 'ai' : config.replyMode === 'ai';
            const msg = key === 'message' ? value : config.message;
            onUpdate({ config: newConfig, isConfigured: isAi || !!(msg?.trim()) });
          };
          return (
          <>
            <div className="p-3 bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-900/20 dark:to-pink-900/20 rounded-lg border border-orange-200 dark:border-orange-800 mb-4">
              <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400 text-sm font-medium mb-1">
                <MessageCircle className="w-4 h-4" />
                {t('workflow.social.replyStoryDmDesc', 'Sends a DM reply when someone responds to your Story')}
              </div>
              <p className="text-xs text-orange-600 dark:text-orange-500">
                {t('workflow.social.replyStoryDmInfo', 'Use this action with the "Story Reply" trigger. The follower already started the conversation by replying to your Story, so the message is delivered instantly.')}
              </p>
            </div>

            {/* Reply mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.social.replyMode', 'Reply mode')}
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input type="radio" name="storyDmMode" value="fixed" checked={config.replyMode !== 'ai'} onChange={() => handleStoryDmConfigChange('replyMode', 'fixed')} className="text-primary-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeFixed', 'Same reply for everyone')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.storyDmFixedDesc', 'Sends the same message to everyone who replies to your Story')}</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <input type="radio" name="storyDmMode" value="ai" checked={config.replyMode === 'ai'} onChange={() => handleStoryDmConfigChange('replyMode', 'ai')} className="text-primary-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{t('workflow.social.replyModeAi', 'Reply with AI')}</p>
                    <p className="text-xs text-gray-500">{t('workflow.social.storyDmAiDesc', 'AI generates a personalized reply based on the Story reply message')}</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Fixed message */}
            {config.replyMode !== 'ai' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.social.storyDmMessage', 'Reply message')}
                </label>
                <Textarea
                  value={config.message || ''}
                  onChange={(e) => handleStoryDmConfigChange('message', e.target.value)}
                  placeholder={t('workflow.social.storyDmPlaceholder', 'Hi {{sender.username}}! Thanks for replying to my Story! 🙌')}
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('workflow.social.storyDmVars', 'Use {{sender.username}}, {{sender.name}}, {{message.text}}, {{story.id}} to personalize')}
                </p>
              </div>
            )}

            {/* AI config */}
            {config.replyMode === 'ai' && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('workflow.social.aiTone', 'Reply tone')}</label>
                  <Select value={config.aiTone || 'friendly'} onChange={(e) => handleStoryDmConfigChange('aiTone', e.target.value)} options={[
                    { value: 'friendly', label: t('workflow.social.toneFriendly', 'Friendly') },
                    { value: 'professional', label: t('workflow.social.toneProfessional', 'Professional') },
                    { value: 'casual', label: t('workflow.social.toneCasual', 'Casual') },
                    { value: 'enthusiastic', label: t('workflow.social.toneEnthusiastic', 'Enthusiastic') },
                    { value: 'formal', label: t('workflow.social.toneFormal', 'Formal') },
                  ]} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('workflow.social.aiInstructions', 'Additional instructions (optional)')}</label>
                  <Textarea value={config.aiInstructions || ''} onChange={(e) => handleStoryDmConfigChange('aiInstructions', e.target.value)} placeholder={t('workflow.social.storyDmAiPlaceholder', 'E.g.: Thank them for engaging with the Story, ask if they want to know more...')} rows={3} />
                </div>
              </div>
            )}

            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('workflow.social.instagramVars', 'Available data')}:</p>
              <p className="text-xs text-gray-400">{'{{sender.username}}, {{sender.name}}, {{sender.id}}, {{message.text}}, {{message.mid}}, {{story.id}}, {{story.url}}'}</p>
            </div>
          </>
        );
        })();

      // Marketplace trigger configs
      case 'marketplace_order_created':
      case 'marketplace_payment_confirmed':
      case 'marketplace_cart_abandoned':
      case 'marketplace_store_registered':
      case 'marketplace_ticket_opened':
      case 'marketplace_order_processing':
      case 'marketplace_order_shipped':
      case 'marketplace_order_delivered':
      case 'marketplace_order_cancelled':
      case 'marketplace_order_refunded':
      case 'marketplace_payment_failed':
      case 'marketplace_payment_refunded':
        if (node.data.type === 'marketplace_store_registered' || node.data.type === 'marketplace_ticket_opened') {
          return (
            <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-300">
              {t('workflow.mkp.triggerNeverFires', 'A loja nunca manda este aviso, então este gatilho não dispara. Troque por outro gatilho do marketplace.')}
            </div>
          );
        }
        return (
          <>
            {isMarketplaceConnected ? (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 mb-4 text-sm text-green-800 dark:text-green-300 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 flex-shrink-0" />
                {marketplaceStoreName
                  ? t('workflow.mkp.triggerConnectedTo', 'Começa sozinho quando chegar o aviso da loja ({{name}}).', { name: marketplaceStoreName })
                  : t('workflow.mkp.triggerConnected', 'Começa sozinho quando a loja avisar.')}
              </div>
            ) : (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mb-4">
                <div className="flex items-start gap-2 text-yellow-800 dark:text-yellow-300 text-sm">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{t('workflow.mkp.triggerNeedsStore', 'Conecte a loja para este gatilho funcionar. É só colar a chave da loja.')}</span>
                </div>
                {onOpenMarketplace && (
                  <button type="button" onClick={onOpenMarketplace} className="mt-2 text-sm font-semibold text-indigo-700 dark:text-indigo-300 hover:underline">
                    {t('workflow.mkp.connectNow', 'Conectar a loja agora')}
                  </button>
                )}
              </div>
            )}
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('workflow.mkp.triggerData', 'Dados que chegam:')}</p>
              <p className="text-xs text-gray-400 font-mono leading-relaxed">
                {node.data.type === 'marketplace_cart_abandoned'
                  ? '{{customer.name}}, {{customer.email}}, {{cart.url}}, {{store.name}}'
                  : '{{customer.name}}, {{customer.email}}, {{customer.phone}}, {{order.number}}, {{order.total}}, {{order.status}}, {{order.tracking_code}}, {{store.name}}'}
              </p>
            </div>
          </>
        );

      case 'form_submit':
        return (
          <>
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 mb-4">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                <FileText className="w-4 h-4" />
                {t('workflow.triggers.formSubmitDesc', 'Triggers when a form is submitted')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.formMode', 'Which form?')}
              </label>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="formMode"
                    checked={!config.formMode || config.formMode === 'any'}
                    onChange={() => {
                      const newConfig = { ...config, formMode: 'any', formId: '' };
                      setConfig(newConfig);
                      onUpdate({ config: newConfig, isConfigured: true });
                    }}
                    className="text-emerald-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('workflow.config.anyForm', 'Any form')}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="formMode"
                    checked={config.formMode === 'specific'}
                    onChange={() => handleConfigChange('formMode', 'specific')}
                    className="text-emerald-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('workflow.config.specificForm', 'Specific form')}
                  </span>
                </label>
              </div>
            </div>
            {config.formMode === 'specific' && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.config.selectForm', 'Select form')}
                </label>
                <Select
                  value={config.formId || ''}
                  onChange={(e) => handleConfigChange('formId', e.target.value)}
                  options={[
                    { value: '', label: t('workflow.config.formPlaceholder', 'Select a form...') },
                    ...landingPages.map((lp) => ({
                      value: lp.id,
                      label: `${lp.name}${lp.status === 'published' ? '' : ` (${lp.status})`}`,
                    })),
                  ]}
                />
                {landingPages.length === 0 && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                    {t('workflow.config.noForms', 'No landing pages with forms found.')}
                  </p>
                )}
              </div>
            )}
            {config.formMode === 'any' || !config.formMode ? (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 mt-3">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  {t('workflow.config.anyFormNote', 'This trigger will fire when any form is submitted.')}
                </p>
              </div>
            ) : null}
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('workflow.triggers.formVars', 'Available data')}: {'{{lead.name}}, {{lead.email}}, {{lead.phone}}, {{form.id}}'}
              </p>
            </div>
          </>
        );

      case 'tag_added':
        return (
          <>
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 mb-4">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                <Tag className="w-4 h-4" />
                {t('workflow.triggers.tagAddedDesc', 'Triggers when a tag is added to the lead')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.watchTag', 'Tag to watch')}
              </label>
              <Input
                value={config.tagName || ''}
                onChange={(e) => handleConfigChange('tagName', e.target.value)}
                placeholder="Ex: interessado, qualificado, vip..."
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.config.watchTagHelp', 'Leave empty to trigger on any tag')}
              </p>
            </div>
          </>
        );

      case 'lead_created':
        return (
          <>
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 mb-4">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                <UserPlus className="w-4 h-4" />
                {t('workflow.triggers.leadCreatedDesc', 'Triggers when a new lead is created in the CRM')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Pipeline
              </label>
              <Select
                value={config.pipelineId || ''}
                onChange={(e) => handleConfigChange('pipelineId', e.target.value)}
                options={[
                  { value: '', label: t('workflow.config.anyPipeline', 'Any pipeline') },
                  ...crmPipelines.map(p => ({ value: p.id, label: p.name }))
                ]}
              />
              <p className="text-xs text-gray-400 mt-1.5">
                {t('workflow.config.pipelineFilterHelp', 'Filter by specific pipeline or leave empty for all')}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('workflow.triggers.leadVars', 'Available data')}: {'{{lead.name}}, {{lead.email}}, {{lead.phone}}, {{lead.company}}'}
              </p>
            </div>
          </>
        );

      case 'webhook':
        return (
          <>
            <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800 mb-4">
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                <Globe className="w-4 h-4" />
                {t('workflow.triggers.webhookDesc', 'Triggers when an external webhook is received')}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.webhookSecret', 'Secret (optional)')}
              </label>
              <Input
                value={config.webhookSecret || ''}
                onChange={(e) => handleConfigChange('webhookSecret', e.target.value)}
                placeholder="Chave secreta para validar o webhook"
              />
            </div>
            <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-3">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                {t('workflow.triggers.webhookInfo', 'Send a POST to the webhook URL with the lead data in the body.')}
              </p>
              <p className="text-xs text-gray-400">
                {t('workflow.triggers.webhookVars', 'Available data')}: {'{{lead.name}}, {{lead.email}}, {{lead.phone}}, + campos customizados do body'}
              </p>
            </div>
          </>
        );

      case 'update_lead':
        return (
          <>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <p className="text-sm text-pink-600 dark:text-pink-400">
                {t('workflow.config.updateLeadInfo', 'Updates lead fields in the CRM')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.fieldToUpdate', 'Field to update')}
              </label>
              <Select
                value={config.fieldName || ''}
                onChange={(e) => handleConfigChange('fieldName', e.target.value)}
                options={[
                  { value: '', label: t('common.select', 'Select') },
                  { value: 'status', label: 'Status' },
                  { value: 'score', label: 'Score' },
                  { value: 'company', label: t('workflow.config.company', 'Company') },
                  { value: 'position', label: t('workflow.config.position', 'Position') },
                  { value: 'notes', label: t('workflow.config.notes', 'Notes') },
                  { value: 'custom', label: t('workflow.config.customField', 'Custom field') },
                ]}
              />
            </div>
            {config.fieldName === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('workflow.config.customFieldName', 'Field name')}
                </label>
                <Input
                  value={config.customFieldName || ''}
                  onChange={(e) => handleConfigChange('customFieldName', e.target.value)}
                  placeholder="Ex: origem, segmento..."
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.fieldValue', 'New value')}
              </label>
              <Input
                value={config.fieldValue || ''}
                onChange={(e) => handleConfigChange('fieldValue', e.target.value)}
                placeholder="{{lead.name}} ou valor fixo..."
              />
            </div>
            {!isCrmConnected && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      case 'assign_owner':
        return (
          <>
            <div className="p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-200 dark:border-pink-800 mb-4">
              <p className="text-sm text-pink-600 dark:text-pink-400">
                {t('workflow.config.assignOwnerInfo', 'Assigns the lead to an owner in the CRM')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.ownerEmail', 'Owner email')}
              </label>
              <Input
                value={config.ownerEmail || ''}
                onChange={(e) => handleConfigChange('ownerEmail', e.target.value)}
                placeholder="vendedor@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('workflow.config.assignMode', 'Assignment mode')}
              </label>
              <Select
                value={config.assignMode || 'fixed'}
                onChange={(e) => handleConfigChange('assignMode', e.target.value)}
                options={[
                  { value: 'fixed', label: t('workflow.config.fixedOwner', 'Fixed owner') },
                  { value: 'round_robin', label: t('workflow.config.roundRobin', 'Round Robin (rotation)') },
                ]}
              />
            </div>
            {config.assignMode === 'round_robin' && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg mt-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('workflow.config.roundRobinHelp', 'The system will automatically distribute among team members')}
                </p>
              </div>
            )}
            {!isCrmConnected && (
              <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800 mt-3">
                <div className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  {t('workflow.config.crmRequired', 'Configure CRM integration to use this action')}
                </div>
              </div>
            )}
          </>
        );

      default:
        return (
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t('workflow.config.comingSoon', 'Settings for this node type coming soon')}
            </p>
          </div>
        );
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/50 dark:to-gray-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <Settings className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">{t('workflow.properties', 'Properties')}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('workflow.configureStep', 'Configure this step')}</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Node Info Card */}
        <div className={`p-4 rounded-2xl border-2 ${
          node.type === 'trigger' ? 'bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800' :
          node.type === 'action' ? 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800' :
          node.type === 'crmAction' ? 'bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 border-pink-200 dark:border-pink-800' :
          node.type === 'condition' ? 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-orange-200 dark:border-orange-800' :
          'bg-gradient-to-br from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 border-cyan-200 dark:border-cyan-800'
        }`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${
                node.type === 'trigger' ? 'bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400' :
                node.type === 'action' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' :
                node.type === 'crmAction' ? 'bg-pink-100 dark:bg-pink-900/50 text-pink-600 dark:text-pink-400' :
                node.type === 'marketplaceAction' ? 'bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400' :
                node.type === 'condition' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400' :
                'bg-cyan-100 dark:bg-cyan-900/50 text-cyan-600 dark:text-cyan-400'
              }`}>
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{node.data.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize mt-0.5">
                  {node.type === 'trigger' ? t('workflow.triggerType', 'Trigger') :
                   node.type === 'action' ? t('workflow.actionType', 'Action') :
                   node.type === 'crmAction' ? t('workflow.crmActionType', 'CRM Action') :
                   node.type === 'marketplaceAction' ? t('workflow.mkp.actionType', 'Ação na loja (marketplace)') :
                   node.type === 'condition' ? t('workflow.conditionType', 'Condition') :
                   t('workflow.delayType', 'Delay')}
                </p>
              </div>
            </div>
            {node.data.isConfigured ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                <CheckCircle className="w-3.5 h-3.5" />
                {t('workflow.configured', 'Configured')}
              </span>
            ) : (
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                <AlertTriangle className="w-3.5 h-3.5" />
                {t('workflow.pending', 'Pending')}
              </span>
            )}
          </div>
        </div>

        {/* Label */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            {t('workflow.stepName', 'Step Name')}
          </label>
          <Input
            value={node.data.label}
            onChange={(e) => onUpdate({ label: e.target.value })}
            placeholder={t('workflow.stepNamePlaceholder', 'Step name')}
            className="!rounded-xl"
          />
        </div>

        {/* Type-specific Configuration */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-primary-500 rounded-full" />
            <h4 className="text-sm font-bold text-gray-900 dark:text-white">
              {t('workflow.configuration', 'Configuration')}
            </h4>
          </div>
          <div className="space-y-4 pl-3 border-l-2 border-gray-100 dark:border-gray-700">
            {renderConfigFields()}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-3">
        <div className="flex gap-2">
          <button
            onClick={onDuplicate}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
          >
            <Copy className="w-4 h-4" />
            {t('common.duplicate', 'Duplicate')}
          </button>
          <button
            onClick={onDelete}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl font-medium text-sm hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
          >
            <Trash2 className="w-4 h-4" />
            {t('common.delete', 'Delete')}
          </button>
        </div>
      </div>
    </div>
  );
};

// Wrap with ReactFlowProvider
const FlowAutomationPageWrapper: React.FC = () => (
  <ReactFlowProvider>
    <FlowAutomationPage />
  </ReactFlowProvider>
);

export default FlowAutomationPageWrapper;
